import { useState, useEffect, useRef, createContext, useContext, Fragment } from 'react';
import { supabase } from './supabase';
import {
  getProjects, createProject, updateProject, deleteProject,
  getNotes, addNote, deleteNote, updateNote,
  getMessages, sendMessage, subscribeToMessages,
  getFiles, addFile, updateFile, deleteFile,
  getDocuments, createDocument, signDocument, updateDocument, deleteDocument,
  getInvoices, createInvoice, markInvoicePaid, markInvoiceUnpaid, updateInvoice, deleteInvoice,
  getResources, addResource, deleteResource,
  getSessions, createSession, addSessionItems,
  getChecklist, addChecklistItem, toggleChecklistItem,
  getTimeline, addTimelineEntry,
  getAssessment, upsertAssessmentScore,
  updateClient, incrementUsage, getUsageRecord, requestUpgrade,
  getThoughts, addThought, deleteThought,
  updateThoughtText, createProjectFromThought,
  getTeamMembersForClient, createTeamMember, updateTeamMember, deleteTeamMember,
  getClientItemCounts, getClientResourcesForPicker,
  getProjectChecklist, addProjectChecklistItem, toggleProjectChecklistItem, deleteProjectChecklistItem, updateProjectChecklistItem,
  uploadClientLogo, deleteClientLogo, reopenAdvisorReview, getClientCheckslistProgress,
  getKnowledgeForPrompt, getProjectCardData,
  adminSetClientPassword,
} from './supabase';
import { C, F, Card, Btn, Badge, PhaseBar, SectionHead, fmtDate, fmtTime, fmtMoney, todayLocal, callClaude, Wordmark, useIsMobile, AppShell } from './tokens';
import { BottomNav } from './BottomNav';
import { LayoutDashboard, FolderKanban, Folder, FileText, Receipt, Library, Clock, MessageSquare, Mic, CheckCircle2, UserPlus, Calendar, Eye, EyeOff, MoreHorizontal, Compass } from 'lucide-react';
import ChangePasswordCard from './ChangePasswordCard';
import ChangeEmailCard from './ChangeEmailCard';
import { getDisplayEmail } from './lib/userEmail';
import AdminDirectory from './AdminDirectory';
import CompassModule from './modules/compass/CompassModule';
import { COMPASS_ENABLED, isPilot0Context } from './modules/compass/config';
import { ActionButton, OverflowMenu } from './PortalControls';

// PREVIEW_TABS retired — formerly held opt-in tabs (Team Members) hidden by default.
// Now empty: all tabs use normal visibility (visible unless in a client's hidden_tabs).
const PREVIEW_TABS = [];

// Display-only title-casing for names interpolated into greeting messages.
// Stored names are often lowercase ("bob"); we don't mutate storage, just
// render "Bob" in the greeting line. Leaves already-capitalized letters
// alone so "McKay" / "O'Brien" survive.
function titleCaseName(s){
  if (typeof s !== "string") return s;
  return s.replace(/\b([a-z])/g, (_, c) => c.toUpperCase());
}

// Inbox row classifier: maps a timeline row to a colored badge + a route.
// Used by the Inbox to show event-type-specific badges and to make rows
// clickable so they land on the relevant page. Pure function — no React,
// no side effects. Rules are evaluated top-to-bottom; first match wins.
//
// route shape: { tab: <ALL_TABS string>, subView?: 'review'|'gap'|'assessment',
//                projectId?: <uuid> } — or null for non-clickable rows
//                (the catch-all 'milestone').
function classifyTimelineEvent(event){
  const title = String(event?.title || '');
  const eventType = String(event?.type || '');

  // 1. DOC — createDocument / signDocument timeline events (see supabase.js).
  if (/document (sent|signed)|signature|signed by/i.test(title)) {
    return { type:'doc', label:'DOC', color:'doc', route:{ tab:'Docs' } };
  }
  // 2. ASSESSMENT — completion drops the user into the advisor review subview.
  if (/assessment/i.test(title)) {
    return { type:'assessment', label:'ASSESSMENT', color:'assessment', route:{ tab:'Overview', subView:'review' } };
  }
  // 3. SESSION — by stored type='session' or session-keyword title.
  if (eventType === 'session' || /\bsession\b/i.test(title)) {
    return { type:'session', label:'SESSION', color:'session', route:{ tab:'Sessions' } };
  }
  // 4. INSIGHT — stored type='insight'. No dedicated insights view yet;
  //    land on Overview as the closest match.
  if (eventType === 'insight') {
    return { type:'insight', label:'INSIGHT', color:'insight', route:{ tab:'Overview' } };
  }
  // 5. PROJECT — project create events + extracted action items.
  //    No related_id column on timeline yet, so we always land on the
  //    Projects list rather than the specific project detail.
  if (/project (created|completed|updated)/i.test(title) || /^new project/i.test(title) || /^action:/i.test(title)) {
    return { type:'project', label:'PROJECT', color:'project', route:{ tab:'Projects' } };
  }
  // 6. MILESTONE — catch-all. Badge renders but the row is NOT clickable.
  return { type:'milestone', label:'MILESTONE', color:'milestone', route:null };
}

// ── Assessment data ───────────────────────────────────────────
const CATEGORIES = [
  {id:"operations",label:"Business Operations & Strategy",icon:"◈",color:C.navy,   desc:"Leadership, execution, workflow, organizational clarity"},
  {id:"benefits",  label:"Health Benefits",               icon:"◉",color:C.green,  desc:"Employee health coverage, costs, and plan options"},
  {id:"workers",   label:"Workers Compensation",          icon:"◎",color:C.teal,   desc:"Coverage, claims management, and cost reduction"},
  {id:"staffing",  label:"Staffing & Recruiting",         icon:"◐",color:C.purple, desc:"Hiring, retention, and workforce planning"},
  {id:"payroll",   label:"Payroll Services",              icon:"◑",color:C.amber,  desc:"Payroll processing, compliance, and automation"},
  {id:"it",        label:"IT & Cybersecurity",            icon:"◒",color:C.red,    desc:"Technology infrastructure and security posture"},
  {id:"marketing", label:"Marketing Support",             icon:"◓",color:C.gold,   desc:"Lead generation, brand awareness, digital presence"},
];

const SUNGLASS_QS = [
  {id:"b1_a1",bucket:"Bucket 1",lens:"Atmosphere",text:"Operational efficiency and workflow clarity",           prompt:"Where do things slow down? What systems feel unclear?"},
  {id:"b1_a2",bucket:"Bucket 1",lens:"Atmosphere",text:"Sales leadership and management rhythm",                prompt:"Is there a consistent sales process? How is performance tracked?"},
  {id:"b1_a3",bucket:"Bucket 1",lens:"Atmosphere",text:"Financial visibility, reporting, and confidence",       prompt:"Real-time visibility into numbers? What financial blind spots concern you?"},
  {id:"b1_a4",bucket:"Bucket 1",lens:"Atmosphere",text:"Branding and overall client engagement experience",     prompt:"How consistently is your brand experienced? Where does it fall short?"},
  {id:"b1_a5",bucket:"Bucket 1",lens:"Atmosphere",text:"Leadership mindset and team alignment",                 prompt:"Are leaders aligned? Where does misalignment show up most?"},
  {id:"b1_e1",bucket:"Bucket 1",lens:"Education", text:"Onboarding and training of new employees",             prompt:"How quickly do new people become effective? What's missing?"},
  {id:"b1_e2",bucket:"Bucket 1",lens:"Education", text:"Ongoing development and leadership enhancement",        prompt:"Is continuous learning happening intentionally or reactively?"},
  {id:"b1_e3",bucket:"Bucket 1",lens:"Education", text:"Clarity around roles, expectations, and metrics",      prompt:"Does every person know what success looks like in their role?"},
  {id:"b1_e4",bucket:"Bucket 1",lens:"Education", text:"How clients are educated through delivery",            prompt:"Do clients clearly understand your value? Where does confusion show up?"},
  {id:"b1_r1",bucket:"Bucket 1",lens:"Relationship",text:"Trust and alignment between leadership and employees",prompt:"Do people feel heard and supported? Where does trust break down?"},
  {id:"b1_r2",bucket:"Bucket 1",lens:"Relationship",text:"Cross-team collaboration and communication",         prompt:"Where do silos or communication gaps create the most friction?"},
  {id:"b1_r3",bucket:"Bucket 1",lens:"Relationship",text:"Client relationships and feedback loops",            prompt:"How well do you know what your clients actually think?"},
  {id:"b2_a1",bucket:"Bucket 2",lens:"Atmosphere",text:"Organizational readiness for growth or acquisition",   prompt:"If you doubled in size tomorrow, what would break first?"},
  {id:"b2_a2",bucket:"Bucket 2",lens:"Atmosphere",text:"Leadership capacity to support expansion",             prompt:"Do you have the leadership bench to scale?"},
  {id:"b2_a3",bucket:"Bucket 2",lens:"Atmosphere",text:"Cultural alignment between core and new entities",     prompt:"How would your culture hold up through major expansion?"},
  {id:"b2_a4",bucket:"Bucket 2",lens:"Atmosphere",text:"Systems and financial structures needed to scale",     prompt:"What infrastructure gaps would prevent clean scaling?"},
  {id:"b2_e1",bucket:"Bucket 2",lens:"Education", text:"Internal readiness for new services or offerings",    prompt:"Could your team confidently support new offerings today?"},
  {id:"b2_e2",bucket:"Bucket 2",lens:"Education", text:"How employees learn about expansion initiatives",     prompt:"How are you communicating growth plans internally?"},
  {id:"b2_e3",bucket:"Bucket 2",lens:"Education", text:"How clients are introduced to expanded offerings",    prompt:"Do clients see you as capable of more than what you deliver?"},
  {id:"b2_e4",bucket:"Bucket 2",lens:"Education", text:"Clarity around how new revenue streams complement existing", prompt:"Is the strategic logic of your expansion clear internally?"},
  {id:"b2_r1",bucket:"Bucket 2",lens:"Relationship",text:"Strategic partners, advisors, acquisition targets",  prompt:"What relationships need to be built to support growth?"},
  {id:"b2_r2",bucket:"Bucket 2",lens:"Relationship",text:"Integration of new teams, leadership, or cultures",  prompt:"How have you handled cultural integration before?"},
  {id:"b2_r3",bucket:"Bucket 2",lens:"Relationship",text:"Client trust through growth or acquisition",         prompt:"How will existing clients experience your expansion?"},
];

const LENS_COLORS = {Atmosphere:C.navy,Education:C.teal,Relationship:C.purple};
const FILE_CATEGORIES = ["Agreements","Session Details","Client Docs","Advisor Docs","Operations","Sales","Owner Updates","Other"];
const avg = arr => { const v=arr.filter(x=>x!=null&&!isNaN(x)); return v.length ? Math.round((v.reduce((a,b)=>a+b,0)/v.length)*10)/10 : null; };

const USAGE_LIMITS = {
  ai_messages:    {label:"AI Message Reviews",      free:5},
  ai_files:       {label:"File AI Interpretations", free:3},
  ai_sessions:    {label:"Session Transcripts",     free:2},
  ai_gap_summaries:{label:"Gap Analysis Summaries", free:3},
  ai_doc_analysis:{label:"AI Document Analyses",   free_client:10, free_advisor:5},
};

// ── Micro components ──────────────────────────────────────────
// Breadcrumb context — tabs push extra segments via setSegments(...). Cleared on unmount.
// Used by ProjectsTab to add the active project name as a third crumb when drilled in.
const BreadcrumbContext = createContext({setSegments:()=>{}});

// Sticky header context — a tab can publish a full-bleed sticky bar that
// AppShell renders directly inside <main>, outside the content wrapper.
const StickyHeaderContext = createContext({setHeader:()=>{}});

const Ring = ({value,max=5,size=48,color=C.navy}) => {
  if(value==null) return <div style={{width:size,height:size,borderRadius:"50%",border:`2px dashed ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center"}}><span style={{fontFamily:F.mono,fontSize:8,color:C.charLight}}>—</span></div>;
  const r=20,circ=2*Math.PI*r,pct=(value/max)*100;
  return (
    <svg width={size} height={size} viewBox="0 0 52 52">
      <circle cx="26" cy="26" r={r} fill="none" stroke={C.bgAlt} strokeWidth="3.5"/>
      <circle cx="26" cy="26" r={r} fill="none" stroke={color} strokeWidth="3.5" strokeDasharray={`${(pct/100)*circ} ${circ}`} strokeLinecap="round" transform="rotate(-90 26 26)"/>
      <text x="26" y="31" textAnchor="middle" fill={color} fontSize="14" fontFamily={F.display} fontWeight="700">{value}</text>
    </svg>
  );
};

const Spinner = () => (
  <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
    <div style={{width:32,height:32,border:`3px solid ${C.navyFaint}`,borderTop:`3px solid ${C.navy}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
  </div>
);

// ── ASSESSMENT ENGINE ─────────────────────────────────────────
function AssessmentView({client, isAdvisor, assessmentData, onDone, onBack}) {
  const [bucket,setBucket]=useState("Bucket 1");
  const [scores,setScores]=useState({});
  const [notes,setNotes]=useState({});
  const [open,setOpen]=useState(null);
  const [saving,setSaving]=useState(false);

  // Load existing scores from assessmentData
  useEffect(()=>{
    if(!assessmentData) return;
    const s={}, n={};
    assessmentData.forEach(row=>{
      if(isAdvisor){ s[row.question_id]=row.advisor_score; n[row.question_id]=row.advisor_note; }
      else { s[row.question_id]=row.client_score; n[row.question_id]=row.client_note; }
    });
    setScores(s); setNotes(n);
  },[assessmentData,isAdvisor]);

  const bQs=SUNGLASS_QS.filter(q=>q.bucket===bucket);
  const scored=SUNGLASS_QS.filter(q=>scores[q.id]!=null).length;
  const allDone=SUNGLASS_QS.every(q=>scores[q.id]!=null);

  async function handleScore(q,v) {
    setScores(p=>({...p,[q.id]:v}));
    await upsertAssessmentScore(client.id, q.id, q.bucket, q.lens, isAdvisor?'advisor':'client', v, notes[q.id]||null);
  }

  async function handleNote(q,text) {
    setNotes(p=>({...p,[q.id]:text}));
  }

  async function handleNoteBlur(q) {
    if(scores[q.id]!=null){
      await upsertAssessmentScore(client.id, q.id, q.bucket, q.lens, isAdvisor?'advisor':'client', scores[q.id], notes[q.id]||null);
    }
  }

  async function handleSubmit() {
    setSaving(true);
    // Save any remaining notes
    for(const q of SUNGLASS_QS){
      if(scores[q.id]!=null){
        await upsertAssessmentScore(client.id, q.id, q.bucket, q.lens, isAdvisor?'advisor':'client', scores[q.id], notes[q.id]||null);
      }
    }
    const field = isAdvisor ? 'advisor_review_complete' : 'assessment_complete';
    await updateClient(client.id, {[field]: true});
    if(!isAdvisor){
      await addTimelineEntry(client.id, {type:'milestone', title:'Business Assessment Completed', body:'Client completed the BB Integrated Business Assessment.', date:todayLocal()});
    }
    setSaving(false);
    onDone();
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <div style={{fontFamily:F.display,fontSize:21,color:C.charcoal,fontWeight:600}}>{isAdvisor?"Assessment Review":"BB Integrated Business Assessment"}</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginTop:4}}>{isAdvisor?`Reviewing ${client.user?.name||'client'}'s answers — add your scores and observations`:"Rate each area 1–5 and share your thinking"}</div>
        </div>
        <Btn variant="ghost" small onClick={onBack}>← Back</Btn>
      </div>
      <Card style={{padding:"12px 18px",marginBottom:18}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}><span style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>{scored} of {SUNGLASS_QS.length} scored</span><span style={{fontFamily:F.mono,fontSize:11,color:C.navy,fontWeight:600}}>{Math.round((scored/SUNGLASS_QS.length)*100)}%</span></div>
        <div style={{background:C.bgAlt,borderRadius:3,height:4}}><div style={{width:`${(scored/SUNGLASS_QS.length)*100}%`,height:"100%",background:C.navy,borderRadius:3,transition:"width .4s"}}/></div>
      </Card>
      <div style={{display:"flex",gap:8,marginBottom:18}}>
        {["Bucket 1","Bucket 2"].map(b=>(
          <button key={b} onClick={()=>setBucket(b)} style={{flex:1,background:bucket===b?C.navyFaint:"transparent",border:`1px solid ${bucket===b?C.navyBorder:C.border}`,borderRadius:8,padding:"11px 14px",cursor:"pointer",textAlign:"left"}}>
            <div style={{fontFamily:F.body,fontSize:12,color:bucket===b?C.navy:C.charMid,fontWeight:600}}>{b==="Bucket 1"?"Current State — People, Process & Performance":"Future State — Expansion & New Revenue"}</div>
            <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,marginTop:2}}>{SUNGLASS_QS.filter(q=>q.bucket===b&&scores[q.id]!=null).length}/{SUNGLASS_QS.filter(q=>q.bucket===b).length} scored</div>
          </button>
        ))}
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:7,marginBottom:18}}>
        {bQs.map(q=>{
          const lc=LENS_COLORS[q.lens]||C.navy;
          const isOpen=open===q.id;
          const my=scores[q.id];
          const clientRow = assessmentData?.find(r=>r.question_id===q.id);
          const cli=isAdvisor?clientRow?.client_score:null;
          const cliNote=isAdvisor?clientRow?.client_note:null;
          const gap=isAdvisor&&my!=null&&cli!=null?Math.abs(my-cli):null;
          return (
            <Card key={q.id} style={{overflow:"hidden",border:`1px solid ${isOpen?lc+"55":C.border}`}}>
              <div onClick={()=>setOpen(isOpen?null:q.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 16px",cursor:"pointer"}}>
                <Ring value={my} color={lc} size={40}/>
                <div style={{flex:1}}>
                  <div style={{display:"flex",gap:6,marginBottom:3,flexWrap:"wrap"}}>
                    <span style={{background:lc+"15",color:lc,border:`1px solid ${lc}30`,borderRadius:3,padding:"1px 6px",fontSize:8,fontWeight:700,fontFamily:F.body,textTransform:"uppercase"}}>{q.lens}</span>
                    {isAdvisor&&cli!=null&&<span style={{fontFamily:F.mono,fontSize:9,color:C.charLight}}>Client: {cli}/5</span>}
                    {gap!=null&&gap>=2&&<span style={{background:C.redBg,color:C.red,border:`1px solid ${C.redBd}`,borderRadius:3,padding:"1px 5px",fontSize:8,fontWeight:700,fontFamily:F.body}}>GAP {gap}</span>}
                  </div>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500}}>{q.text}</div>
                </div>
                <span style={{color:C.charLight,fontSize:11}}>{isOpen?"▲":"▼"}</span>
              </div>
              {isOpen&&(
                <div style={{padding:"0 16px 14px",borderTop:`1px solid ${C.border}`,background:C.bgAlt}}>
                  {isAdvisor&&(
                    <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"11px 13px",margin:"12px 0"}}>
                      <div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,marginBottom:5,textTransform:"uppercase",letterSpacing:".06em"}}>Client's Answer</div>
                      <div style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                        <Ring value={cli} color={C.navyMid} size={36}/>
                        <div style={{fontFamily:F.body,fontSize:12,color:cliNote?C.charcoal:C.charLight,fontStyle:cliNote?"normal":"italic",lineHeight:1.6}}>{cliNote||"No notes provided."}</div>
                      </div>
                    </div>
                  )}
                  {isAdvisor&&<div style={{fontFamily:F.body,fontSize:11,color:C.navyMid,fontStyle:"italic",marginBottom:10}}>💡 {q.prompt}</div>}
                  <div style={{marginBottom:11}}>
                    <div style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"6px 10px",marginBottom:7,display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontFamily:F.body,fontSize:12,color:C.navy,fontWeight:700}}>Rate this area:</span>
                      <span style={{fontFamily:F.body,fontSize:11,color:C.navyMid}}>1 = Needs Work</span>
                      <span style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>·</span>
                      <span style={{fontFamily:F.body,fontSize:11,color:C.navyMid}}>5 = Excellent</span>
                    </div>
                    <div style={{display:"flex",gap:7}}>
                      {[1,2,3,4,5].map(v=>(
                        <button key={v} onClick={()=>handleScore(q,v)} style={{width:44,height:44,borderRadius:8,border:`2px solid ${my===v?lc:C.border}`,background:my===v?lc+"15":C.surface,color:my===v?lc:C.charMid,fontFamily:F.display,fontSize:17,fontWeight:700,cursor:"pointer",transition:"all .15s"}}>{v}</button>
                      ))}
                    </div>
                  </div>
                  {!isAdvisor&&<div style={{fontFamily:F.body,fontSize:11,color:C.charMid,fontStyle:"italic",marginBottom:7}}>{q.prompt}</div>}
                  <textarea value={notes[q.id]||""} onChange={e=>handleNote(q,e.target.value)} onBlur={()=>handleNoteBlur(q)} placeholder={isAdvisor?"Your observations…":"What's driving this score?"} rows={3} style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 11px",color:C.charcoal,fontFamily:F.body,fontSize:13,resize:"vertical",boxSizing:"border-box"}}/>
                </div>
              )}
            </Card>
          );
        })}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingTop:14,borderTop:`1px solid ${C.border}`}}>
        <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>{allDone?"✓ All questions scored — ready to submit":"Score all questions to submit"}</div>
        <Btn variant="primary" onClick={handleSubmit} disabled={!allDone} loading={saving}>{isAdvisor?"Complete Review":"Submit Assessment"}</Btn>
      </div>
    </div>
  );
}

// ── GAP ANALYSIS ──────────────────────────────────────────────
function GapAnalysisView({client, assessmentData, onAddProject, onBack, advisorId, onUpgradeRequest}) {
  const [loading,setLoading]=useState(false);
  const [summary,setSummary]=useState(null);
  const [sel,setSel]=useState([]);
  const [suggestions,setSuggestions]=useState({});
  const [genning,setGenning]=useState({});
  const [added,setAdded]=useState([]);
  const [customGaps,setCustomGaps]=useState([]); // recommendations added to gaps

  const gaps=SUNGLASS_QS.map(q=>{
    const row=assessmentData?.find(r=>r.question_id===q.id);
    const cS=row?.client_score, aS=row?.advisor_score;
    return {...q,cS,aS,gap:(cS!=null&&aS!=null)?Math.abs(cS-aS):null};
  }).filter(g=>g.gap!=null&&g.gap>=0).sort((a,b)=>b.gap-a.gap);

  const lenses=["Atmosphere","Education","Relationship"];
  const lAvg=(field,lens)=>avg(SUNGLASS_QS.filter(q=>q.lens===lens).map(q=>{const r=assessmentData?.find(d=>d.question_id===q.id);return r?.[field];}).filter(x=>x!=null));

  async function genSummary(){
    setLoading(true);
    setSummary(null);
    if(advisorId){
      try{
        const u=await getUsageRecord(advisorId);
        const used=u?.ai_gap_summaries||0;
        if(used>=USAGE_LIMITS.ai_gap_summaries.free){
          // Don't block — just increment and continue
        }
        await incrementUsage(advisorId,"ai_gap_summaries");
      }catch(e){console.log("Usage tracking error:",e);}
    }
    const allScores=gaps.map(g=>`"${g.text}" (${g.lens}): Client scored ${g.cS}/5, Advisor scored ${g.aS}/5, Gap = ${g.gap}`).join("\n");
    const alignedItems=gaps.filter(g=>g.gap===0).map(g=>g.text).join(", ");
    const bigGaps=gaps.filter(g=>g.gap>=2).map(g=>`${g.text} (gap: ${g.gap})`).join(", ");
    const sys=`You are a BB Integrated business consultant with deep expertise in organizational assessment and gap analysis. You are reviewing a completed dual-perspective business assessment where both the business owner (client) and their advisor independently scored the same questions.

Your role is to provide a thorough, insightful, and actionable analysis of the results. Write like a seasoned consultant delivering a formal assessment debrief — specific, direct, and forward-looking.

Return ONLY valid JSON with these exact keys:
- "executiveSummary": 3-4 sentences giving a high-level overview of the assessment results and what they signal about the business
- "strengths": 2-3 sentences identifying genuine areas of alignment and strength between client and advisor perspectives
- "criticalGaps": 3-4 sentences describing the most significant gaps, what they reveal about blind spots or misalignment, and why they matter
- "patterns": 2-3 sentences identifying themes across the lenses (Atmosphere, Education, Relationship) and what patterns emerge
- "recommendations": array of 4-6 specific actionable recommendation strings, each one a clear next step the advisor recommends
- "priorityAreas": array of up to 5 strings, each being a specific area to focus on first

No markdown, no backticks, only valid JSON.`;
    const prompt=`Client: ${client.user?.name||'the client'} at ${client.company||'their business'}

All Assessment Scores:
${allScores}

${alignedItems?`Areas of alignment (Gap=0): ${alignedItems}`:""}
${bigGaps?`Critical gaps (2+ points): ${bigGaps}`:""}

Lens averages:
${["Atmosphere","Education","Relationship"].map(l=>{
  const cs=avg(SUNGLASS_QS.filter(q=>q.lens===l).map(q=>{const r=assessmentData?.find(d=>d.question_id===q.id);return r?.client_score;}).filter(x=>x!=null));
  const as=avg(SUNGLASS_QS.filter(q=>q.lens===l).map(q=>{const r=assessmentData?.find(d=>d.question_id===q.id);return r?.advisor_score;}).filter(x=>x!=null));
  return `${l}: Client avg ${cs?.toFixed(1)||'N/A'}, Advisor avg ${as?.toFixed(1)||'N/A'}`;
}).join("\n")}`;

    const raw=await callClaude(sys,prompt,2000);
    try{
      const parsed=JSON.parse(raw.replace(/```json|```/g,"").trim());
      setSummary(parsed);
    }catch{
      setSummary({
        executiveSummary:`The assessment for ${client.user?.name||'this client'} reveals a mixed picture with several areas of strong alignment alongside meaningful gaps that warrant focused attention.`,
        strengths:"Both client and advisor are aligned on foundational operational elements, suggesting a solid shared understanding of current business realities.",
        criticalGaps:bigGaps?`The most significant gaps appear in: ${bigGaps}. These represent areas where the client's self-perception differs meaningfully from the advisor's external view — classic blind spots.`:"Some gaps exist that should be reviewed in your next session.",
        patterns:"The gap pattern suggests the client may be more optimistic about relationship and future-state areas than the advisor's external assessment supports.",
        recommendations:"Schedule a debrief session to walk through the largest gaps together. Use the identified gaps to build a focused 90-day action plan prioritizing the highest-gap areas.",
        priorityAreas:gaps.filter(g=>g.gap>=1).slice(0,5).map(g=>g.text)
      });
    }
    setLoading(false);
  }

  async function genSuggestion(g){
    if(!g||!g.id) return;
    setGenning(p=>({...p,[g.id]:true}));
    try{
      const sys=`BB Integrated consultant. Return only JSON with keys "name","description","phase","priority". No markdown.`;
      const prompt=`Gap:"${g.text||''}"\nClient score:${g.cS||'N/A'}\nAdvisor score:${g.aS||'N/A'}\nGap size:${g.gap||1}`;
      const raw=await callClaude(sys,prompt,800);
      let parsed;
      try{parsed=JSON.parse(raw.replace(/```json|```/g,"").trim());}
      catch{parsed={name:`Address: ${g.text?.slice(0,50)||'this gap'}`,description:"A structured initiative to resolve this identified gap and align perspectives.",phase:"Clarity",priority:"high"};}
      setSuggestions(p=>({...p,[g.id]:parsed}));
    }catch(e){
      console.error("genSuggestion error:",e);
      setSuggestions(p=>({...p,[g.id]:{name:`Address: ${g.text?.slice(0,50)||'this gap'}`,description:"A structured initiative to resolve this identified gap.",phase:"Clarity",priority:"medium"}}));
    }
    setGenning(p=>({...p,[g.id]:false}));
  }

  function toggle(id){
    try {
      setSel(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
      const gap=gaps.find(g=>g.id===id)||customGaps.find(g=>g.id===id);
      if(gap&&!suggestions[id]) genSuggestion(gap);
    } catch(e) {
      console.error("Toggle error:", e);
    }
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontFamily:F.display,fontSize:21,color:C.charcoal,fontWeight:600}}>Gap Analysis — {client.user?.name||client.company}</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginTop:4}}>Select gaps to convert into action projects</div>
        </div>
        <Btn variant="ghost" small onClick={onBack}>← Back</Btn>
      </div>
      {/* Lens comparison */}
      <Card style={{padding:"16px 20px",marginBottom:16}}>
        <div style={{fontFamily:F.display,fontSize:15,color:C.charcoal,marginBottom:14}}>Score Comparison by Lens</div>
        <div style={{display:"flex",gap:0}}>
          {lenses.map((l,i)=>{
            const cs=lAvg("client_score",l)||0;
            const as=lAvg("advisor_score",l)||0;
            const gap=Math.abs(cs-as);
            const lc=LENS_COLORS[l];
            return (
              <div key={l} style={{flex:1,textAlign:"center",padding:"0 10px",borderRight:i<2?`1px solid ${C.border}`:"none"}}>
                <div style={{fontFamily:F.body,fontSize:11,color:lc,fontWeight:700,marginBottom:10}}>{l}</div>
                <div style={{display:"flex",justifyContent:"center",gap:10,marginBottom:8}}>
                  <div style={{textAlign:"center"}}><div style={{fontFamily:F.mono,fontSize:9,color:C.navyMid,marginBottom:3}}>Client</div><Ring value={cs} color={C.navyMid} size={44}/></div>
                  <div style={{textAlign:"center"}}><div style={{fontFamily:F.mono,fontSize:9,color:C.amber,marginBottom:3}}>Advisor</div><Ring value={as} color={C.amber} size={44}/></div>
                </div>
                <div style={{fontFamily:F.mono,fontSize:10,color:gap>=2?C.red:gap>=1?C.amber:C.green,fontWeight:700}}>Gap: {gap.toFixed(1)}</div>
              </div>
            );
          })}
        </div>
      </Card>
      {/* AI Summary */}
      <Card style={{padding:"16px 20px",marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:summary?14:0}}>
          <div>
            <div style={{fontFamily:F.display,fontSize:15,color:C.charcoal}}>Assessment Summary</div>
            <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginTop:2}}>AI-generated analysis based on both perspectives</div>
          </div>
          <Btn variant="outline" small onClick={genSummary} disabled={loading}>{loading?"Generating…":summary?"Regenerate":"Generate with AI"}</Btn>
        </div>
        {loading&&(
          <div style={{padding:"20px 0",textAlign:"center"}}>
            <div style={{fontFamily:F.body,fontSize:13,color:C.navyMid,animation:"pulse 1.5s infinite"}}>◈ Analyzing assessment data…</div>
            <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginTop:6}}>This may take a moment</div>
          </div>
        )}
        {summary&&!loading&&(
          <div>
            {/* Executive Summary */}
            <div style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"12px 14px",marginBottom:14}}>
              <div style={{fontFamily:F.mono,fontSize:8,color:C.navy,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",marginBottom:5}}>Executive Summary</div>
              <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.8}}>{summary.executiveSummary}</div>
            </div>
            {/* Three columns */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
              {[
                {k:"strengths",l:"Strengths",c:C.green,bg:C.greenBg,bd:C.greenBd},
                {k:"criticalGaps",l:"Critical Gaps",c:C.red,bg:C.redBg,bd:C.redBd},
                {k:"patterns",l:"Patterns & Themes",c:C.amber,bg:C.amberBg,bd:C.amberBd},
              ].map(s=>(
                <div key={s.k} style={{background:s.bg,border:`1px solid ${s.bd}`,borderRadius:8,padding:"11px 13px"}}>
                  <div style={{fontFamily:F.mono,fontSize:8,color:s.c,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",marginBottom:5}}>{s.l}</div>
                  <div style={{fontFamily:F.body,fontSize:12,color:C.charcoal,lineHeight:1.7}}>{summary[s.k]}</div>
                </div>
              ))}
            </div>
            {/* Recommendations */}
            {summary.recommendations&&(
              <div style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px",marginBottom:14}}>
                <div style={{fontFamily:F.mono,fontSize:8,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>Recommendations — Add to Gaps to Create Projects</div>
                {(Array.isArray(summary.recommendations)?summary.recommendations:[summary.recommendations]).map((rec,i)=>{
                  const recText=typeof rec==="string"?rec:rec.text||rec;
                  const alreadyAdded=customGaps.some(g=>g.text===recText);
                  return (
                    <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 0",borderBottom:i<(Array.isArray(summary.recommendations)?summary.recommendations:[summary.recommendations]).length-1?`1px solid ${C.border}`:"none"}}>
                      <div style={{flex:1,fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.7}}>{recText}</div>
                      {alreadyAdded?(
                        <span style={{fontFamily:F.body,fontSize:10,color:C.green,fontWeight:600,flexShrink:0}}>✓ In Gaps</span>
                      ):(
                        <button onClick={()=>{
                          const newGap={id:`custom_${Date.now()}_${i}`,text:recText,lens:"Recommendation",gap:1,cS:null,aS:null,isCustom:true};
                          setCustomGaps(p=>[...p,newGap]);
                        }} style={{background:C.navy,color:"#fff",border:"none",borderRadius:6,padding:"4px 12px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer",flexShrink:0,whiteSpace:"nowrap"}}>
                          + Add to Gaps ↓
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Priority areas */}
            {summary.priorityAreas?.length>0&&(
              <div>
                <div style={{fontFamily:F.mono,fontSize:8,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>Priority Focus Areas</div>
                <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
                  {summary.priorityAreas.map((a,i)=>(
                    <div key={i} style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:11,color:C.navy,fontWeight:500}}>
                      {i+1}. {a}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
      {/* Gap list */}
      <Card style={{padding:"16px 20px",marginBottom:18}}>
        <div style={{fontFamily:F.display,fontSize:15,color:C.charcoal,marginBottom:4}}>Identified Gaps — Select to Create Projects</div>
        <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:14}}>
          Select gaps to convert into projects. Recommendations you added appear here too.
          {customGaps.length>0&&<span style={{marginLeft:6,background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:4,padding:"1px 7px",fontSize:9,fontWeight:700}}>{customGaps.length} from recommendations</span>}
        </div>
        {gaps.filter(g=>g.gap>0).length===0&&customGaps.length===0&&<div style={{fontFamily:F.body,fontSize:13,color:C.charLight,fontStyle:"italic"}}>No significant gaps found. Use recommendations above to add items here.</div>}
        
        {/* Custom gaps from recommendations */}
        {customGaps.filter(g=>!added.includes(g.id)).map((g,i)=>{
          const on=sel.includes(g.id);
          const sug=suggestions[g.id];
          return (
            <div key={g.id} style={{padding:"11px 0",borderBottom:`1px solid ${C.border}`}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                <div onClick={()=>toggle(g.id)} style={{width:20,height:20,borderRadius:5,border:`2px solid ${on?C.navy:C.border}`,background:on?C.navyFaint:"transparent",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,marginTop:2}}>
                  {on&&<span style={{color:C.navy,fontSize:11}}>✓</span>}
                </div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:5}}>
                    <div>
                      <span style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500}}>{g.text}</span>
                      <span style={{marginLeft:7,background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:3,padding:"1px 5px",fontSize:8,fontWeight:700,fontFamily:F.body,textTransform:"uppercase"}}>Recommendation</span>
                    </div>
                    <button onClick={()=>setCustomGaps(p=>p.filter(x=>x.id!==g.id))} style={{background:"none",border:"none",color:C.charLight,cursor:"pointer",fontSize:11,flexShrink:0}}>✕</button>
                  </div>
                  {on&&(
                    <div style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"11px 13px",marginTop:7}}>
                      {genning[g.id]?<div style={{fontFamily:F.body,fontSize:12,color:C.navyMid,animation:"pulse 1.5s infinite"}}>◈ Generating project suggestion…</div>:sug?(
                        <>
                          <div style={{fontFamily:F.body,fontSize:9,color:C.navyMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:5}}>AI Project Suggestion</div>
                          <div style={{fontFamily:F.body,fontSize:13,color:C.navy,fontWeight:600,marginBottom:3}}>{sug.name}</div>
                          <div style={{fontFamily:F.body,fontSize:12,color:C.navyMid,marginBottom:8,lineHeight:1.6}}>{sug.description}</div>
                          <div style={{display:"flex",gap:7,alignItems:"center"}}>
                            <Badge label={sug.phase} type={sug.phase}/>
                            <Badge label={sug.priority+" priority"} type={sug.priority}/>
                            <button onClick={async()=>{
                              try{
                                await onAddProject({name:sug.name,description:sug.description,phase:sug.phase,risk:sug.priority==="high"?"high":sug.priority==="low"?"low":"medium",deadline:"TBD"});
                                setAdded(p=>[...p,g.id]);
                                setSel(p=>p.filter(x=>x!==g.id));
                              }catch(e){console.error(e);alert("Failed to create project.");}
                            }} style={{marginLeft:"auto",background:C.navy,color:"#fff",border:"none",borderRadius:6,padding:"5px 13px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Add to Projects →</button>
                          </div>
                        </>
                      ):null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Active gaps from assessment */}
        {gaps.filter(g=>g.gap>0&&!added.includes(g.id)).map((g,i,arr)=>{
          const on=sel.includes(g.id);
          const sug=suggestions[g.id];
          const gc=g.gap>=2?C.red:g.gap>=1?C.amber:C.green;
          return (
            <div key={g.id} style={{padding:"11px 0",borderBottom:i<arr.length-1?`1px solid ${C.border}`:"none"}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                <div onClick={()=>toggle(g.id)} style={{width:20,height:20,borderRadius:5,border:`2px solid ${on?gc:C.border}`,background:on?gc+"15":"transparent",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,marginTop:2}}>
                  {on&&<span style={{color:gc,fontSize:11}}>✓</span>}
                </div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:5}}>
                    <div>
                      <span style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500}}>{g.text}</span>
                      <span style={{marginLeft:7,background:LENS_COLORS[g.lens]+"15",color:LENS_COLORS[g.lens],border:`1px solid ${LENS_COLORS[g.lens]}30`,borderRadius:3,padding:"1px 5px",fontSize:8,fontWeight:700,fontFamily:F.body,textTransform:"uppercase"}}>{g.lens}</span>
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                      <span style={{fontFamily:F.mono,fontSize:10,color:C.navyMid}}>C:{g.cS}</span>
                      <span style={{fontFamily:F.mono,fontSize:10,color:C.amber}}>A:{g.aS}</span>
                      <span style={{background:gc+"15",color:gc,border:`1px solid ${gc}33`,borderRadius:4,padding:"2px 7px",fontSize:9,fontWeight:700,fontFamily:F.body}}>Gap:{g.gap}</span>
                    </div>
                  </div>
                  {on&&(
                    <div style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"11px 13px",marginTop:7}}>
                      {genning[g.id]?<div style={{fontFamily:F.body,fontSize:12,color:C.navyMid,animation:"pulse 1.5s infinite"}}>◈ Generating project suggestion…</div>:sug?(
                        <>
                          <div style={{fontFamily:F.body,fontSize:9,color:C.navyMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:5}}>AI Project Suggestion</div>
                          <div style={{fontFamily:F.body,fontSize:13,color:C.navy,fontWeight:600,marginBottom:3}}>{sug.name}</div>
                          <div style={{fontFamily:F.body,fontSize:12,color:C.navyMid,marginBottom:8,lineHeight:1.6}}>{sug.description}</div>
                          <div style={{display:"flex",gap:7,alignItems:"center"}}>
                            <Badge label={sug.phase} type={sug.phase}/>
                            <Badge label={sug.priority+" priority"} type={sug.priority}/>
                            <button onClick={async()=>{
                              try{
                                await onAddProject({name:sug.name,description:sug.description,phase:sug.phase,risk:sug.priority==="high"?"high":sug.priority==="low"?"low":"medium",deadline:"TBD"});
                                setAdded(p=>[...p,g.id]);
                                setSel(p=>p.filter(x=>x!==g.id));
                              }catch(e){console.error(e);alert("Failed to create project.");}
                            }} style={{marginLeft:"auto",background:C.navy,color:"#fff",border:"none",borderRadius:6,padding:"5px 13px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Add to Projects →</button>
                          </div>
                        </>
                      ):null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {/* Added gaps — collapsed section */}
        {added.length>0&&(
          <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
            <div style={{fontFamily:F.body,fontSize:11,color:C.green,fontWeight:600,marginBottom:6}}>✓ {added.length} gap{added.length>1?"s":""} added to Projects</div>
            {gaps.filter(g=>added.includes(g.id)).map(g=>(
              <div key={g.id} style={{fontFamily:F.body,fontSize:12,color:C.charLight,padding:"3px 0",textDecoration:"line-through"}}>{g.text}</div>
            ))}
          </div>
        )}
        {/* Aligned items */}
        {gaps.filter(g=>g.gap===0).length>0&&(
          <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
            <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginBottom:6}}>✓ Already aligned (Gap:0)</div>
            {gaps.filter(g=>g.gap===0).map(g=>(
              <div key={g.id} style={{fontFamily:F.body,fontSize:12,color:C.charLight,padding:"3px 0",display:"flex",justifyContent:"space-between"}}>
                <span>{g.text}</span>
                <span style={{color:C.green,fontSize:10,fontWeight:700}}>Aligned</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── LOGO UPLOADER ─────────────────────────────────────────────
function LogoUploader({client}) {
  const [uploading,setUploading]=useState(false);
  const [logoUrl,setLogoUrl]=useState(client.logo_url||null);
  const [showDelete,setShowDelete]=useState(false);
  const inputRef=useRef(null);

  async function handleUpload(file){
    if(!file) return;
    setUploading(true);
    try{
      const url=await uploadClientLogo(client.id,file);
      setLogoUrl(url);
      client.logo_url=url;
    }catch(e){console.error("Logo upload failed:",e);}
    setUploading(false);
  }

  async function handleDelete(){
    setUploading(true);
    await deleteClientLogo(client.id);
    setLogoUrl(null);
    client.logo_url=null;
    setShowDelete(false);
    setUploading(false);
  }

  return (
    <div style={{display:"flex",gap:6,alignItems:"center"}}>
      <input ref={inputRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>handleUpload(e.target.files[0])}/>
      {logoUrl?(
        <>
          <button onClick={()=>inputRef.current?.click()} disabled={uploading} style={{background:"rgba(255,255,255,.12)",color:"rgba(255,255,255,.7)",border:"1px solid rgba(255,255,255,.2)",borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,cursor:"pointer"}}>{uploading?"Uploading…":"Change Logo"}</button>
          {showDelete?(
            <>
              <button onClick={handleDelete} style={{background:"rgba(180,50,50,.6)",color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,cursor:"pointer"}}>Confirm Delete</button>
              <button onClick={()=>setShowDelete(false)} style={{background:"transparent",color:"rgba(255,255,255,.5)",border:"1px solid rgba(255,255,255,.15)",borderRadius:6,padding:"4px 8px",fontFamily:F.body,fontSize:10,cursor:"pointer"}}>Cancel</button>
            </>
          ):(
            <button onClick={()=>setShowDelete(true)} style={{background:"transparent",color:"rgba(255,255,255,.4)",border:"1px solid rgba(255,255,255,.15)",borderRadius:6,padding:"4px 8px",fontFamily:F.body,fontSize:10,cursor:"pointer"}}>✕</button>
          )}
        </>
      ):(
        <button onClick={()=>inputRef.current?.click()} disabled={uploading}
          onDragOver={e=>{e.preventDefault();e.stopPropagation();}}
          onDrop={e=>{e.preventDefault();handleUpload(e.dataTransfer.files[0]);}}
          style={{background:"rgba(255,255,255,.1)",color:"rgba(255,255,255,.65)",border:"1px dashed rgba(255,255,255,.3)",borderRadius:7,padding:"5px 12px",fontFamily:F.body,fontSize:10,cursor:"pointer"}}>
          {uploading?"Uploading…":"+ Upload Logo"}
        </button>
      )}
    </div>
  );
}

// Draft-side textarea panel — shared between the client and advisor inputs.
// Owns no state itself; receives all draft/AI/speech handles from OverviewTab.
function DraftPanel({
  placeholder, postLabel, speakTitle,
  newText, setNewText, saving, saveThought,
  toggleSpeech, listening,
  handleAiReviewDraft, aiReviewing,
  aiSuggestion, dismissAiSuggestion, postAiSuggestion,
  cancelDraft,
}){
  const hasText = !!(newText||"").trim();
  return (
    <div style={{marginBottom:10}}>
      <textarea value={newText} onChange={e=>setNewText(e.target.value)} placeholder={placeholder} rows={3}
        style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",color:C.charcoal,fontFamily:F.body,fontSize:13,resize:"none",boxSizing:"border-box",marginBottom:6}}/>
      {listening && (
        <div style={{display:"flex",alignItems:"center",gap:6,fontFamily:F.body,fontSize:11,color:"#C0392B",marginBottom:6}}>
          <span style={{width:7,height:7,borderRadius:"50%",background:"#C0392B",animation:"thoughtPulse 1s infinite"}}/>
          Listening… <span style={{color:C.charLight}}>Tap mic again to stop.</span>
        </div>
      )}
      <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
        <Btn variant="primary" small onClick={()=>saveThought()} loading={saving} disabled={!hasText}>{postLabel}</Btn>
        <button onClick={toggleSpeech} title={speakTitle}
          style={{background:listening?"#C0392B":C.bgAlt,color:listening?"#fff":C.charMid,border:`1px solid ${listening?"#C0392B":C.border}`,borderRadius:6,padding:"5px 11px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6,transition:"all .2s",animation:listening?"thoughtPulse 1s infinite":undefined}}>
          <Mic size={13}/> Use Mic
        </button>
        <button onClick={handleAiReviewDraft} disabled={!hasText || aiReviewing}
          style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"5px 11px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:(!hasText||aiReviewing)?"default":"pointer",opacity:(!hasText||aiReviewing)?.55:1}}>
          {aiReviewing?"AI Reviewing…":"AI Review"}
        </button>
        <button onClick={cancelDraft} disabled={!hasText && !aiSuggestion}
          style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 11px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:(!hasText&&!aiSuggestion)?"default":"pointer",opacity:(!hasText&&!aiSuggestion)?.55:1}}>
          Cancel
        </button>
      </div>

      {aiSuggestion && (
        <div style={{marginTop:12,paddingLeft:12,borderLeft:`2px solid ${C.navyBorder}`}}>
          <div style={{fontFamily:F.mono,fontSize:9,color:C.navy,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6}}>AI Suggestion</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.7,whiteSpace:"pre-wrap",marginBottom:10}}>{aiSuggestion}</div>
          <div style={{display:"flex",gap:7}}>
            <Btn variant="primary" small onClick={postAiSuggestion} loading={saving}>Post AI Note</Btn>
            <button onClick={dismissAiSuggestion}
              style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 11px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OverviewTab({client, isAdvisor, user, checklist, setChecklist, timeline, projects, setProjects, invoices, documents, onStartAssessment, onReviewAssessment, onViewGap, onReopenReview, assessmentData, onUpdateClient, onNavigate, isMobile}) {
  const [thoughts, setThoughts] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [newText, setNewText] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  // Inline edit state — same hook, used by Posted Notes rows.
  const [editingThought, setEditingThought] = useState(null);   // {id, draft}
  // Inline hard-delete confirm — { id } when armed.
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  // Draft-text AI Review (refines whatever's in the active textarea before
  // posting). Only one suggestion at a time since only one textarea is rendered.
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiReviewing, setAiReviewing] = useState(false);
  const quickWins=(projects||[]).reduce((total,p)=>total+(p.checklist_progress===100?1:0),0);
  const [assessmentHidden, setAssessmentHidden] = useState(client.assessment_hidden||false);
  const [togglingAssessment, setTogglingAssessment] = useState(false);

  // Voice-to-text state for "Share Your Thoughts" textarea.
  const [listening, setListening] = useState(false);
  const speechSupported = typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const recRef = useRef(null);
  function toggleSpeech(){
    if(!speechSupported){
      alert("Voice input isn't supported in this browser. Please use Chrome or Safari latest.");
      return;
    }
    if(listening){
      recRef.current?.stop();
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = 'en-US';
    rec.onresult = e => {
      const transcript = Array.from(e.results).map(r=>r[0].transcript).join(' ');
      setNewText(p => p ? p.trimEnd() + " " + transcript : transcript);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.start();
    recRef.current = rec;
    setListening(true);
  }

  const unpaidInvoices=(invoices||[]).filter(i=>i.status!=="paid").length;
  const pendingDocs=(documents||[]).filter(d=>d.status!=="signed").length;
  const hasClientScores = assessmentData?.some(r=>r.client_score!=null);
  const hasAdvisorScores = assessmentData?.some(r=>r.advisor_score!=null);

  // Parse thoughts from DB
  const parseThought = t => {
    try { return {...t,...JSON.parse(t.text)}; }
    catch { return {...t,title:"Note",text:t.text,authorRole:"client",priority:"later",submitted:false}; }
  };

  useEffect(()=>{
    getThoughts(client.id).then(data => {
      setThoughts((data||[]).map(parseThought));
    });
  },[client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggleAssessmentVisibility(){
    setTogglingAssessment(true);
    const newVal = !assessmentHidden;
    await updateClient(client.id, {assessment_hidden: newVal});
    setAssessmentHidden(newVal);
    onUpdateClient&&onUpdateClient({assessment_hidden: newVal});
    setTogglingAssessment(false);
  }

  // Post a note. `overrideText` lets "Post AI Note" submit the refined version
  // instead of whatever's in the textarea.
  async function saveThought(overrideText){
    const text = (typeof overrideText === "string" ? overrideText : newText).trim();
    if(!text) return;
    setSaving(true);
    try {
      const authorRole = isAdvisor?"advisor":"client";
      const payload = {title:newTitle||"Note", text, authorRole};
      const raw = await addThought(client.id, payload.title, text, authorRole);
      const parsed = parseThought({...raw, text: JSON.stringify(payload)});
      setThoughts(p=>[parsed,...p]);
      setNewTitle(""); setNewText(""); setShowForm(false);
      setAiSuggestion(null);
    } catch(e) {
      console.error("Failed to save thought:", e);
      alert("Failed to save. Please try again.");
    }
    setSaving(false);
  }

  // Inline edit of an existing note's text.
  function startEditThought(thought){ setEditingThought({id:thought.id, draft:thought.text}); }
  function cancelEditThought(){ setEditingThought(null); }
  async function saveEditThought(){
    if(!editingThought) return;
    const { id, draft } = editingThought;
    const trimmed = (draft||"").trim();
    if(!trimmed) return;
    const original = thoughts.find(t=>t.id===id);
    if(!original){ setEditingThought(null); return; }
    try {
      const payload = {title: original.title, text: trimmed, authorRole: original.authorRole};
      await updateThoughtText(id, payload);
      setThoughts(p=>p.map(t=>t.id===id?{...t,text:trimmed}:t));
      setEditingThought(null);
    } catch(e) { alert("Edit failed: "+e.message); }
  }

  // Hard delete — confirm inline, then deleteThought.
  function armDelete(thoughtId){ setDeleteConfirmId(thoughtId); }
  function cancelDelete(){ setDeleteConfirmId(null); }
  async function confirmDelete(thoughtId){
    try {
      await deleteThought(thoughtId);
      setThoughts(p=>p.filter(t=>t.id!==thoughtId));
      setDeleteConfirmId(null);
      if(editingThought?.id===thoughtId) setEditingThought(null);
    } catch(e) { alert("Delete failed: "+e.message); }
  }

  // "+ Project" on a posted note — fast project creation, no AI.
  async function handleQuickProject(thought){
    const name = thought.title && thought.title!=="Note" ? thought.title : thought.text.slice(0,60);
    try {
      const proj = await createProjectFromThought(client.id, name, thought.text);
      setProjects(p=>[proj,...(p||[])]);
    } catch(e) { alert("Failed to create project: "+e.message); }
  }

  // Cancel button on the active textarea — clears draft + any AI suggestion.
  function cancelDraft(){
    setNewText("");
    setAiSuggestion(null);
  }

  // AI Review on the DRAFT (pre-post). Returns a refined version of the note.
  async function handleAiReviewDraft(){
    const draft = (newText||"").trim();
    if(!draft) return;
    setAiReviewing(true);
    try {
      const sys = `Improve this note for clarity and actionability. Return ONLY the improved note text, no preamble, no markdown, no commentary.`;
      const raw = await callClaude(sys, draft, 800);
      setAiSuggestion((raw||"").trim());
    } catch(e) { alert("AI Review failed: "+e.message); }
    setAiReviewing(false);
  }
  function dismissAiSuggestion(){ setAiSuggestion(null); }
  function postAiSuggestion(){
    if(!aiSuggestion) return;
    saveThought(aiSuggestion);
  }

  // Show all non-archived notes in a single feed, newest first. (getThoughts
  // already orders by created_at desc.) Archived rows from the prior build stay
  // hidden — the new UI no longer surfaces or writes to that column.
  const postedNotes = thoughts.filter(t => !t.archived);

  // Group notes by local-date label: "Today", "Yesterday", or "Month D, YYYY".
  function dateGroupLabel(iso){
    if(!iso) return "Unknown";
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    const sameDay = (a, b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
    if(sameDay(d, today)) return "Today";
    if(sameDay(d, yesterday)) return "Yesterday";
    const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }
  // Group into ordered [{label, items}] preserving the source order (newest first).
  const groupedNotes = (() => {
    const out = [];
    let current = null;
    for(const t of postedNotes){
      const label = dateGroupLabel(t.created_at);
      if(!current || current.label !== label){
        current = { label, items: [] };
        out.push(current);
      }
      current.items.push(t);
    }
    return out;
  })();

  // Author label + dot color for a posted note row.
  const clientFirstName = (client.user?.name||"").split(" ")[0].toUpperCase() || "CLIENT";
  function authorLabel(t){ return t.authorRole === "advisor" ? "ADVISOR" : clientFirstName; }
  function authorDot(t){ return t.authorRole === "advisor" ? C.navy : C.teal; }

  // Single posted-note row — lines-not-boxes, hover-tinted, swaps into inline
  // edit OR delete-confirm modes when armed.
  const PostedNoteRow = ({t, isFirst, isLast}) => {
    const isEditingThis = editingThought && editingThought.id === t.id;
    const isConfirmingDelete = deleteConfirmId === t.id;
    return (
      <div
        onMouseEnter={e=>{ if(!isEditingThis && !isConfirmingDelete) e.currentTarget.style.background = C.bgAlt; }}
        onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}
        style={{
          padding:"14px 16px",
          borderTop: isFirst ? `1px solid ${C.border}` : "none",
          borderBottom: isLast ? "none" : `1px solid ${C.border}`,
          transition:"background .15s",
        }}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <span style={{width:8,height:8,borderRadius:"50%",background:authorDot(t),flexShrink:0}}/>
          <span style={{fontFamily:F.mono,fontSize:9,color:C.charMid,fontWeight:700,letterSpacing:".08em"}}>{authorLabel(t)}</span>
          <span style={{fontFamily:F.body,fontSize:10,color:C.charLight,marginLeft:"auto"}}>{fmtDate(t.created_at)}</span>
        </div>

        {isConfirmingDelete ? (
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{fontFamily:F.body,fontSize:13,color:C.red,fontWeight:600}}>Delete this note? This can't be undone.</span>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>confirmDelete(t.id)}
                style={{background:C.red,color:"#fff",border:"none",borderRadius:5,padding:"4px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Delete</button>
              <button onClick={cancelDelete}
                style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        ) : isEditingThis ? (
          <div>
            <textarea value={editingThought.draft} onChange={e=>setEditingThought({...editingThought,draft:e.target.value})} rows={3} autoFocus
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"7px 10px",color:C.charcoal,fontFamily:F.body,fontSize:13,resize:"vertical",boxSizing:"border-box"}}/>
            <div style={{display:"flex",gap:6,marginTop:6}}>
              <button onClick={saveEditThought} disabled={!editingThought.draft.trim()}
                style={{background:editingThought.draft.trim()?C.navy:C.bgAlt,color:editingThought.draft.trim()?"#fff":C.charLight,border:"none",borderRadius:5,padding:"4px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:editingThought.draft.trim()?"pointer":"default"}}>Save</button>
              <button onClick={cancelEditThought}
                style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14}}>
            <div style={{flex:1,fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{t.text}</div>
            <div style={{display:"flex",gap:5,flexShrink:0}}>
              <button onClick={()=>handleQuickProject(t)}
                style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>+ Project</button>
              <button onClick={()=>startEditThought(t)}
                style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Edit</button>
              <button onClick={()=>armDelete(t.id)}
                style={{background:"transparent",color:C.charLight,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Delete</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Hero */}
      <div style={{background:`linear-gradient(135deg,${C.navy} 0%,${C.navyMid} 100%)`,borderRadius:12,padding:"22px 26px",marginBottom:18,position:"relative"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div style={{flex:1}}>
            <div style={{fontFamily:F.display,fontSize:22,color:"#fff",fontWeight:600,marginBottom:3}}>{client.company||client.user?.name}</div>
            <div style={{fontFamily:F.body,fontSize:12,color:"rgba(255,255,255,.6)"}}>{client.user?.name} · {isAdvisor?"Advisor View":"Client Portal"}</div>
          </div>
          <div style={{display:"flex",gap:8,flexShrink:0,marginLeft:20,flexDirection:"column",alignItems:"flex-end"}}>
            {!hasClientScores&&!isAdvisor&&user.role!=="team_member"&&<button onClick={onStartAssessment} style={{background:"rgba(255,255,255,.15)",color:"#fff",border:"1px solid rgba(255,255,255,.3)",borderRadius:8,padding:"9px 16px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>Start Assessment →</button>}
            {hasClientScores&&!hasAdvisorScores&&isAdvisor&&<button onClick={onReviewAssessment} style={{background:"rgba(255,255,255,.15)",color:"#fff",border:"1px solid rgba(255,255,255,.3)",borderRadius:8,padding:"9px 16px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>Review Assessment →</button>}
            {hasClientScores&&hasAdvisorScores&&user.role!=="team_member"&&<button onClick={onViewGap} style={{background:"rgba(255,255,255,.15)",color:"#fff",border:"1px solid rgba(255,255,255,.3)",borderRadius:8,padding:"9px 16px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>View Gap Analysis →</button>}
            {hasAdvisorScores&&isAdvisor&&<button onClick={onReopenReview} style={{background:"rgba(255,255,255,.08)",color:"rgba(255,255,255,.6)",border:"1px solid rgba(255,255,255,.15)",borderRadius:8,padding:"6px 14px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Re-open Review</button>}
            {isAdvisor&&<LogoUploader client={client}/>}
          </div>
        </div>
      </div>

      {/* Alert banners */}
      {!hasClientScores&&!assessmentHidden&&user.role!=="team_member"&&(
        <div style={{background:C.amberBg,border:`1px solid ${C.amberBd}`,borderRadius:10,padding:"12px 18px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{flex:1,cursor:!isAdvisor?"pointer":"default"}} onClick={!isAdvisor?onStartAssessment:undefined}>
            <div style={{fontFamily:F.body,fontSize:13,color:C.amber,fontWeight:700,marginBottom:1}}>📋 Outstanding: BB Integrated Business Assessment</div>
            <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>Complete your assessment to unlock gap analysis and project recommendations.{!isAdvisor&&" Click here to begin →"}</div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0,marginLeft:14}}>
            {!isAdvisor&&<button onClick={onStartAssessment} style={{background:C.amber,color:"#fff",border:"none",borderRadius:7,padding:"7px 14px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Start Now</button>}
            {isAdvisor&&(
              <button onClick={toggleAssessmentVisibility} disabled={togglingAssessment} style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:7,padding:"5px 11px",fontFamily:F.body,fontSize:10,cursor:"pointer"}} title="Hide assessment notice from client">
                {togglingAssessment?"…":"Hide Notice"}
              </button>
            )}
          </div>
        </div>
      )}
      {isAdvisor&&assessmentHidden&&(
        <div style={{background:C.bgAlt,border:`1px dashed ${C.border}`,borderRadius:10,padding:"10px 16px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontFamily:F.body,fontSize:12,color:C.charLight}}>🔒 Assessment notice is hidden from client</div>
          <button onClick={toggleAssessmentVisibility} disabled={togglingAssessment} style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>
            {togglingAssessment?"…":"Show Notice"}
          </button>
        </div>
      )}
      {unpaidInvoices>0&&(
        <div style={{background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:10,padding:"12px 18px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div><div style={{fontFamily:F.body,fontSize:13,color:C.red,fontWeight:700,marginBottom:1}}>💳 Invoice outstanding</div><div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>View your open invoice in the Billing tab.</div></div>
          <Badge label={`${unpaidInvoices} unpaid`} type="unpaid"/>
        </div>
      )}

      {/* Stats — 3 bordered boxes, centered content */}
      <div style={{display:"flex",gap:12,marginTop:36}}>
        <div onClick={()=>onNavigate&&onNavigate("Projects")}
          onMouseEnter={e=>{const n=e.currentTarget.querySelector('[data-stat-value]');if(n)n.style.color=C.navy;}}
          onMouseLeave={e=>{const n=e.currentTarget.querySelector('[data-stat-value]');if(n)n.style.color=C.charcoal;}}
          style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",cursor:"pointer",border:`1px solid ${C.border}`,borderRadius:8,padding:"16px 18px"}}>
          <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>Active Projects</div>
          <div data-stat-value style={{fontFamily:F.display,fontSize:22,color:C.charcoal,fontWeight:600,transition:"color .15s"}}>{(projects||[]).length}</div>
        </div>
        <div onClick={()=>{
            if(user.role==="team_member") return;
            if(!hasClientScores&&!isAdvisor) onStartAssessment&&onStartAssessment();
            else if(hasClientScores&&hasAdvisorScores) onViewGap&&onViewGap();
          }}
          style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",cursor:user.role!=="team_member"&&((!hasClientScores&&!isAdvisor)||(hasClientScores&&hasAdvisorScores))?"pointer":"default",border:`1px solid ${C.border}`,borderRadius:8,padding:"16px 18px"}}>
          <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>Assessment</div>
          <div style={{fontFamily:F.display,fontSize:22,color:hasClientScores?C.green:C.amber,fontWeight:600}}>{hasClientScores?"Complete":"Pending"}</div>
        </div>
        <div onClick={()=>user.role!=="team_member"&&onNavigate&&onNavigate("settings")}
          onMouseEnter={e=>{const n=e.currentTarget.querySelector('[data-stat-value]');if(n)n.style.color=C.navy;}}
          onMouseLeave={e=>{const n=e.currentTarget.querySelector('[data-stat-value]');if(n)n.style.color=C.charcoal;}}
          style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center",cursor:user.role==="team_member"?"default":"pointer",border:`1px solid ${C.border}`,borderRadius:8,padding:"16px 18px"}}>
          <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>Support Areas</div>
          <div data-stat-value style={{fontFamily:F.display,fontSize:22,color:C.charcoal,fontWeight:600,transition:"color .15s"}}>{(client.selected_categories||[]).length} selected</div>
        </div>
      </div>

      {/* Project Progress — unboxed, diamond markers, single-row layout */}
      {(projects||[]).length>0&&(
        <div style={{marginTop:36}}>
          <h3 style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600,margin:"0 0 12px 0"}}>Project Progress</h3>
          {(()=>{
            if(!isMobile){
              return (projects||[]).map((p,idx)=>{
                const pct=p.checklist_progress!=null?p.checklist_progress:(p.progress||0);
                const color=pct>=70?C.green:pct>=40?C.navy:C.amber;
                return (
                  <div key={p.id} onClick={()=>onNavigate&&onNavigate("Projects",p.id)}
                    onMouseEnter={e=>e.currentTarget.style.background=C.bgAlt}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                    style={{padding:"22px 18px",borderTop:idx===0?"none":`1px solid ${C.border}`,cursor:"pointer",display:"flex",alignItems:"center",gap:14,transition:"background .15s"}}>
                    <span style={{fontFamily:F.body,fontSize:13,color:C.navyLight,flexShrink:0}}>◆</span>
                    <div style={{flex:1,fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500,minWidth:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name}</div>
                    <div style={{width:120,background:C.bgAlt,borderRadius:3,height:5,overflow:"hidden",flexShrink:0}}>
                      <div style={{width:`${pct}%`,height:"100%",background:color,borderRadius:3,transition:"width .5s"}}/>
                    </div>
                    <Badge label={p.status||"active"} type={p.status||"active"}/>
                    <span style={{fontFamily:F.mono,fontSize:10,color,fontWeight:600,minWidth:36,textAlign:"right",flexShrink:0}}>{pct}%</span>
                  </div>
                );
              });
            }
            // Mobile: sort by updated_at desc, cap at 5
            const sorted=[...(projects||[])].sort((a,b)=>{
              const aT=a.updated_at?new Date(a.updated_at).getTime():0;
              const bT=b.updated_at?new Date(b.updated_at).getTime():0;
              return bT-aT;
            });
            const shown=sorted.slice(0,5);
            const remaining=sorted.length-shown.length;
            return (
              <>
                {shown.map(p=>{
                  const pct=p.checklist_progress!=null?p.checklist_progress:(p.progress||0);
                  const color=pct>=70?C.green:pct>=40?C.navy:C.amber;
                  return (
                    <div key={p.id}
                      onClick={()=>onNavigate&&onNavigate("Projects",p.id)}
                      style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:14,marginBottom:12,cursor:"pointer",WebkitTapHighlightColor:"transparent"}}>
                      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,marginBottom:10}}>
                        <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600,lineHeight:1.25,flex:1,minWidth:0,overflowWrap:"anywhere"}}>{p.name}</div>
                        <Badge label={p.status||"active"} type={p.status||"active"}/>
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{flex:1,background:C.bgAlt,borderRadius:3,height:5,overflow:"hidden"}}>
                          <div style={{width:`${pct}%`,height:"100%",background:color,borderRadius:3}}/>
                        </div>
                        <span style={{fontFamily:F.mono,fontSize:11,color,fontWeight:600,minWidth:36,textAlign:"right"}}>{pct}%</span>
                      </div>
                    </div>
                  );
                })}
                {remaining>0&&(
                  <div onClick={()=>onNavigate&&onNavigate("Projects")}
                    style={{padding:"10px 14px",textAlign:"center",fontFamily:F.body,fontSize:13,color:C.navy,fontWeight:600,cursor:"pointer",WebkitTapHighlightColor:"transparent"}}>
                    View all projects ({remaining} more) →
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Recent Activity — unboxed */}
      {(timeline||[]).length>0&&(
        <div style={{marginTop:36}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <h3 style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600,margin:0}}>Recent Activity</h3>
            <button onClick={()=>onNavigate&&onNavigate("Timeline")}
              style={{background:"none",border:"none",color:C.navyMid,fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer",padding:0}}>
              View Timeline →
            </button>
          </div>
          {(timeline||[]).slice(0,3).map((t,i)=>{
            const tc={session:[C.navy,C.navyFaint,C.navyBorder],milestone:[C.teal,C.tealBg,C.tealBd],insight:[C.purple,C.purpleBg,C.purpleBd]};
            const [clr,bg,bd]=(tc[t.type]||tc.session);
            // Determine which tab to navigate to based on timeline entry title
            const getTab = title => {
              if(!title) return null;
              if(title.toLowerCase().includes("project")) return "Projects";
              if(title.toLowerCase().includes("session")) return "Sessions";
              if(title.toLowerCase().includes("assessment")) return null; // triggers assessment flow
              if(title.toLowerCase().includes("file")) return "Files";
              if(title.toLowerCase().includes("invoice")||title.toLowerCase().includes("paid")) return "Billing";
              if(title.toLowerCase().includes("message")) return "Messages";
              if(title.toLowerCase().includes("action")) return "Timeline";
              return "Timeline";
            };
            const targetTab = getTab(t.title);
            return (
              <div key={t.id||i} onClick={targetTab?()=>onNavigate&&onNavigate(targetTab):undefined}
                onMouseEnter={targetTab?e=>{e.currentTarget.style.background=C.bgAlt;}:undefined}
                onMouseLeave={targetTab?e=>{e.currentTarget.style.background="transparent";}:undefined}
                style={{display:"flex",gap:12,padding:"22px 18px",borderTop:i===0?"none":`1px solid ${C.border}`,alignItems:"flex-start",cursor:targetTab?"pointer":"default",transition:"background .15s"}}>
                <span style={{background:bg,color:clr,border:`1px solid ${bd}`,borderRadius:4,padding:"2px 7px",fontSize:9,fontWeight:700,fontFamily:F.body,textTransform:"uppercase",flexShrink:0}}>{t.type}</span>
                <div style={{flex:1}}>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500}}>{t.title}</div>
                  <div style={{fontFamily:F.body,fontSize:9,color:C.charLight,marginTop:2}}>{fmtDate(t.date)}</div>
                </div>
                {targetTab&&<span style={{fontFamily:F.mono,fontSize:9,color:C.navyMid,flexShrink:0}}>→</span>}
              </div>
            );
          })}
        </div>
      )}
      {/* Share Your Thoughts — at bottom (hidden from team members) */}
      {user.role!=="team_member" && (
      <Card style={{padding:"16px 18px",marginTop:36,marginBottom:14}}>
        <div style={{fontFamily:F.display,fontSize:16,color:C.charcoal,marginBottom:4}}>Share Your Thoughts</div>
        <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginBottom:18}}>Braindump ideas anytime — we'll review them together in our next session.</div>

        {/* ── Posted Notes (full width, top of section) ───────── */}
        <div style={{marginBottom:24}}>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600,marginBottom:10}}>Posted Notes</div>
          {postedNotes.length === 0 ? (
            <div style={{fontFamily:F.body,fontSize:12,color:C.charLight,fontStyle:"italic",padding:"10px 0"}}>No notes posted yet. Share your thoughts below.</div>
          ) : (
            groupedNotes.map((group, gi) => (
              <div key={group.label} style={{marginTop: gi === 0 ? 0 : 16}}>
                <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700,padding:"6px 0",borderTop: gi === 0 ? "none" : `1px solid ${C.border}`,marginBottom:4}}>
                  {group.label}
                </div>
                <div>
                  {group.items.map((t, i) => (
                    <PostedNoteRow key={t.id} t={t} isFirst={i===0} isLast={i===group.items.length-1}/>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── Two input columns ────────────────────────────────── */}
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
          <div>
            <div style={{fontFamily:F.body,fontSize:11,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".07em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:C.teal}}/>{client.user?.name?.split(" ")[0]||"Client"}'s Thoughts
            </div>
            {!isAdvisor && (
              <DraftPanel
                placeholder="What's on your mind? Type your thought here…"
                postLabel="Post Thought"
                speakTitle={listening?"Stop listening":"Speak your thought"}
                newText={newText} setNewText={setNewText}
                saving={saving} saveThought={saveThought}
                toggleSpeech={toggleSpeech} listening={listening}
                handleAiReviewDraft={handleAiReviewDraft} aiReviewing={aiReviewing}
                aiSuggestion={aiSuggestion} dismissAiSuggestion={dismissAiSuggestion} postAiSuggestion={postAiSuggestion}
                cancelDraft={cancelDraft}/>
            )}
          </div>
          <div>
            <div style={{fontFamily:F.body,fontSize:11,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".07em",marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:C.navy}}/>
              Advisor Notes
            </div>
            {isAdvisor && (
              <DraftPanel
                placeholder="Add your advisor notes here…"
                postLabel="Post Note"
                speakTitle={listening?"Stop listening":"Speak your note"}
                newText={newText} setNewText={setNewText}
                saving={saving} saveThought={saveThought}
                toggleSpeech={toggleSpeech} listening={listening}
                handleAiReviewDraft={handleAiReviewDraft} aiReviewing={aiReviewing}
                aiSuggestion={aiSuggestion} dismissAiSuggestion={dismissAiSuggestion} postAiSuggestion={postAiSuggestion}
                cancelDraft={cancelDraft}/>
            )}
            <style>{`@keyframes thoughtPulse{0%,100%{opacity:1}50%{opacity:.55}}`}</style>
          </div>
        </div>
      </Card>
      )}

    </div>
  );
}

// ── Custom dropdown button (matches Mark Complete style) ──────
function DropBtn({value, options, onChange, bg, color, border, disabled}) {
  const [open,setOpen]=useState(false);
  const ref=useRef(null);
  useEffect(()=>{
    function handler(e){if(ref.current&&!ref.current.contains(e.target))setOpen(false);}
    document.addEventListener("mousedown",handler);
    return()=>document.removeEventListener("mousedown",handler);
  },[]);
  const label=options.find(o=>o.value===value)?.label||value;
  return (
    <div ref={ref} style={{position:"relative",display:"inline-block"}}>
      <button onClick={()=>!disabled&&setOpen(p=>!p)}
        style={{background:bg||C.navyFaint,color:color||C.navy,border:border||`1px solid ${C.navyBorder}`,borderRadius:7,padding:"6px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:disabled?"default":"pointer",display:"flex",alignItems:"center",gap:5,whiteSpace:"nowrap"}}>
        {label}{!disabled&&<span style={{fontSize:8,opacity:.6}}>▼</span>}
      </button>
      {open&&(
        <div style={{position:"absolute",top:"100%",left:0,marginTop:4,background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,boxShadow:"0 4px 16px rgba(30,58,95,.12)",zIndex:30,minWidth:"100%",overflow:"hidden"}}>
          {options.map(o=>(
            <button key={o.value} onClick={()=>{onChange(o.value);setOpen(false);}}
              style={{display:"block",width:"100%",background:o.value===value?C.navyFaint:"transparent",color:C.charcoal,border:"none",padding:"8px 14px",fontFamily:F.body,fontSize:11,fontWeight:o.value===value?600:400,cursor:"pointer",textAlign:"left",whiteSpace:"nowrap"}}
              onMouseEnter={e=>e.currentTarget.style.background=C.bgAlt}
              onMouseLeave={e=>e.currentTarget.style.background=o.value===value?C.navyFaint:"transparent"}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PROJECTS TAB ──────────────────────────────────────────────
function ProjectsTab({client, isAdvisor, user, projects, setProjects, directProjectId, onDirectProjectHandled, isMobile, advisorId}) {
  const [sel,setSel]=useState(null);
  const [creating,setCreating]=useState(false);
  const [deleteConfirm,setDeleteConfirm]=useState(null);
  const [editing,setEditing]=useState(null);
  const [showCompleted,setShowCompleted]=useState(false);
  const [cardData,setCardData]=useState({});
  const [sortDir,setSortDir]=useState(()=>{
    try{const raw=localStorage.getItem('bbportal:projectSort:v1');if(raw){const p=JSON.parse(raw);if(p?.sortDir==='asc'||p?.sortDir==='desc')return p.sortDir;}}catch{}
    return "desc";
  });
  useEffect(()=>{
    try{localStorage.setItem('bbportal:projectSort:v1',JSON.stringify({sortDir}));}catch{}
  },[sortDir]);
  const {setSegments}=useContext(BreadcrumbContext);

  // Publish the active project as a third breadcrumb segment when drilled in.
  useEffect(()=>{
    const active=sel?projects.find(p=>p.id===sel):null;
    if(active) setSegments([{label:active.name,onClick:()=>setSel(null)}]);
    else setSegments([]);
    return ()=>setSegments([]);
  },[sel,projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // Client-side sort by last activity (matches the "Updated X days ago" label
  // on each row). Applied AFTER the active/completed partition so completed
  // still falls below active. Projects with no activity sink to the bottom
  // regardless of direction.
  const sortByActivity=(arr)=>[...arr].sort((a,b)=>{
    const aT=cardData[a.id]?.lastActivityAt;
    const bT=cardData[b.id]?.lastActivityAt;
    if(!aT&&!bT) return 0;
    if(!aT) return 1;
    if(!bT) return -1;
    return sortDir==='desc'?new Date(bT)-new Date(aT):new Date(aT)-new Date(bT);
  });
  const activeProjects=sortByActivity(projects.filter(p=>p.status!=="complete"&&p.status!=="archived"));
  const completedProjects=sortByActivity(projects.filter(p=>p.status==="complete"||p.status==="archived"));

  async function refreshCardData(){
    try { setCardData(await getProjectCardData(client.id)); }
    catch(e){ console.error('Failed to load project card data:',e); }
  }

  useEffect(()=>{ refreshCardData(); },[client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function humanizeRelative(iso){
    if(!iso) return null;
    const days=Math.floor((Date.now()-new Date(iso).getTime())/86400000);
    if(days<=0) return "Updated today";
    if(days===1) return "Updated yesterday";
    if(days<7) return `Updated ${days} days ago`;
    if(days<14) return "Updated 1 week ago";
    if(days<30) return `No activity in ${Math.floor(days/7)} weeks`;
    if(days<60) return "No activity in 1 month";
    return `No activity in ${Math.floor(days/30)} months`;
  }
  function activityColor(iso){
    if(!iso) return C.charLight;
    const days=Math.floor((Date.now()-new Date(iso).getTime())/86400000);
    if(days<=6) return C.green;
    if(days<=13) return C.amber;
    return C.red;
  }

  // Open project directly if navigated from Overview
  useEffect(()=>{
    if(directProjectId&&projects.length>0){
      const found=projects.find(p=>p.id===directProjectId);
      if(found){ setSel(found.id); onDirectProjectHandled&&onDirectProjectHandled(); }
    }
  },[directProjectId,projects]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCreate(){
    const name=prompt("Project name:");
    if(!name) return;
    setCreating(true);
    const proj=await createProject(client.id,{name,phase:"Clarity",status:"active",progress:0,risk:"medium",deadline:"TBD",description:""});
    setProjects(p=>[proj,...p]);
    await addTimelineEntry(client.id,{type:"milestone",title:`New project created: ${name}`,body:"",date:todayLocal()});
    setCreating(false);
  }

  async function handleDelete(proj){
    await deleteProject(proj.id);
    setProjects(p=>p.filter(pr=>pr.id!==proj.id));
    setDeleteConfirm(null);
  }

  async function handleFieldChange(proj, field, value){
    const updated=await updateProject(proj.id,{[field]:value});
    setProjects(p=>p.map(pr=>pr.id===proj.id?{...pr,...updated}:pr));
  }

  const STATUS_OPTIONS=["active","complete","paused","cancelled"];
  const PHASE_OPTIONS=["Clarity","Insight","Alignment","Execution","Momentum"];
  const RISK_OPTIONS=["low","medium","high"];

  // Row component for the unified Projects list. Captures state and handlers
  // from ProjectsTab via closure. variant: "active" | "completed". Three render
  // paths: delete-confirm, mobile stacked, desktop two-sub-row.
  const ProjectRow = ({p, isFirst, isLast, variant}) => {
    const isCompleted = variant === "completed";
    const isDeleting = deleteConfirm === p.id;
    const cd = cardData[p.id] || {};
    const topTotal = cd.topTotal || 0;
    const topDone = cd.topDone || 0;
    const pct = topTotal > 0 ? Math.round((topDone/topTotal)*100) : (isCompleted ? 100 : 0);
    const nextItem = cd.nextItem;
    const lastAt = cd.lastActivityAt;
    const lastSource = cd.lastActivitySource;
    const firstName = cd.lastActivityAuthorName ? cd.lastActivityAuthorName.trim().split(/\s+/)[0] : null;
    const allDone = topTotal > 0 && topDone === topTotal;

    const rowBaseStyle = {
      padding: "22px 18px",
      borderTop: isFirst ? `1px solid ${C.border}` : "none",
      borderBottom: isLast ? "none" : `1px solid ${C.border}`,
      transition: "background .15s",
      opacity: isCompleted ? 0.7 : 1,
    };

    // Delete-confirm — stacks vertically on mobile.
    if (isDeleting) {
      return (
        <div style={{
          ...rowBaseStyle,
          display:"flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent:"space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap:12,
        }}>
          <div>
            <div style={{fontFamily:F.body,fontSize:13,color:C.red,fontWeight:600,marginBottom:2}}>Delete "{p.name}"?</div>
            <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>This cannot be undone. All notes will be lost.</div>
          </div>
          <div style={{display:"flex",gap:8,flexShrink:0,justifyContent: isMobile ? "flex-end" : "flex-start"}}>
            <button onClick={()=>handleDelete(p)} style={{background:C.red,color:"#fff",border:"none",borderRadius:7,padding:"6px 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>Delete</button>
            <button onClick={()=>setDeleteConfirm(null)} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:7,padding:"6px 12px",fontFamily:F.body,fontSize:12,cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      );
    }

    // Badges fragment — reused by both mobile and desktop paths.
    const badgesFragment = (
      isAdvisor && !isCompleted ? (
        <>
          <DropBtn value={p.status||"active"} onChange={v=>handleFieldChange(p,"status",v)}
            options={STATUS_OPTIONS.map(o=>({value:o,label:o}))}
            bg={C.navyFaint} color={C.navy} border={`1px solid ${C.navyBorder}`}/>
          <DropBtn value={p.phase||"Clarity"} onChange={v=>handleFieldChange(p,"phase",v)}
            options={PHASE_OPTIONS.map(o=>({value:o,label:o.toLowerCase()}))}
            bg={C.amberBg} color={C.amber} border={`1px solid ${C.amberBd}`}/>
          <DropBtn value={p.risk||"medium"} onChange={v=>handleFieldChange(p,"risk",v)}
            options={RISK_OPTIONS.map(o=>({value:o,label:o+" risk"}))}
            bg={p.risk==="high"?C.redBg:p.risk==="low"?C.greenBg:C.amberBg}
            color={p.risk==="high"?C.red:p.risk==="low"?C.green:C.amber}
            border={`1px solid ${p.risk==="high"?C.redBd:p.risk==="low"?C.greenBd:C.amberBd}`}/>
        </>
      ) : (
        <>
          <Badge label={p.status||(isCompleted?"complete":"active")} type={p.status||(isCompleted?"complete":"active")}/>
          <Badge label={(p.phase||"clarity").toLowerCase()} type={p.phase||"Clarity"}/>
          <Badge label={(p.risk||"medium")+" risk"} type={p.risk||"medium"}/>
        </>
      )
    );

    // Actions fragment — reused by both mobile and desktop paths.
    const actionsFragment = (
      <>
        {isAdvisor && !isCompleted && (
          <button onClick={async()=>{const updated=await updateProject(p.id,{status:"complete"});setProjects(prev=>prev.map(pr=>pr.id===p.id?{...pr,...updated}:pr));}} title="Mark complete"
            style={{background:C.greenBg,color:C.green,border:`1px solid ${C.greenBd}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>
            ✓ Complete
          </button>
        )}
        {isAdvisor && isCompleted && (
          <button onClick={async e=>{e.stopPropagation();const updated=await updateProject(p.id,{status:"active"});setProjects(prev=>prev.map(pr=>pr.id===p.id?{...pr,...updated}:pr));}}
            style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>
            Reopen
          </button>
        )}
        {isAdvisor && !isCompleted && (
          <button onClick={e=>{e.stopPropagation();setDeleteConfirm(p.id);}} title="Delete project"
            style={{background:C.redBg,color:C.red,border:`1px solid ${C.redBd}`,borderRadius:5,width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:12}}>
            🗑
          </button>
        )}
        <span style={{fontFamily:F.mono,fontSize:14,color:C.charLight,marginLeft:2}}>→</span>
      </>
    );

    // Mobile — self-contained card. The whole card is tappable to open the
    // project (matches existing behavior). Row actions move into a top-right
    // kebab → bottom sheet (advisor-only — clients and team members have no
    // actions on rows today). Lines-not-boxes is dropped here so the kebab
    // has a definite surface to anchor in.
    if (isMobile) {
      return (
        <div onClick={()=>setSel(p.id)}
          onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
          onMouseLeave={e=>{e.currentTarget.style.background=C.surface;}}
          style={{
            background:C.surface,
            border:`1px solid ${C.border}`,
            borderRadius:12,
            padding:16,
            marginBottom:12,
            cursor:"pointer",
            display:"flex",
            flexDirection:"column",
            position:"relative",
            opacity:isCompleted?0.7:1,
            transition:"background .15s",
          }}>
          {/* 1. Name — full width, line-clamp 2. paddingRight reserves space
              under the absolute-positioned kebab so long titles don't run
              behind it (kebab is 44px + 10px right offset = ~54px). */}
          <div style={{
            fontFamily:F.display, fontSize:16,
            color:C.charcoal, fontWeight:600, lineHeight:1.3,
            display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden",
            paddingRight:isAdvisor?54:0,
            ...(isCompleted ? {textDecoration:"line-through"} : {}),
          }}>{p.name}</div>

          {/* 2. Badges */}
          <div onClick={e=>e.stopPropagation()} style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginTop:12, ...(isCompleted ? {filter:"grayscale(1)"} : {})}}>
            {badgesFragment}
          </div>

          {/* 3. Next-item line */}
          {!isCompleted && topTotal > 0 && (
            <div style={{
              fontFamily:F.body, fontSize:12,
              color:allDone?C.green:C.charMid, fontWeight:allDone?600:400,
              whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              marginTop:12,
            }}>
              {allDone ? "All tasks complete" : (nextItem ? `Next: ${nextItem.text}${nextItem.due_date?` — due ${fmtDate(nextItem.due_date)}`:""}` : "")}
            </div>
          )}

          {/* 4. Progress */}
          <div style={{marginTop:12}}>
            <div style={{background:C.bgAlt,borderRadius:3,height:5,overflow:"hidden",marginBottom:4}}>
              <div style={{width:`${pct}%`,height:"100%",background: isCompleted ? C.charLight : (pct>=70?C.green:pct>=40?C.navy:C.amber),borderRadius:3,transition:"width .2s"}}/>
            </div>
            <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight}}>
              {topTotal>0 ? `${pct}% · ${topDone}/${topTotal}` : (isCompleted ? "No tasks recorded" : "No tasks yet")}
            </div>
          </div>

          {/* 5. Activity */}
          <div style={{fontFamily:F.body,fontSize:12,color:activityColor(lastAt),fontWeight:500,marginTop:12}}>
            {lastAt ? humanizeRelative(lastAt) : "No recent activity"}
            {firstName && lastSource==='note' && (
              <span style={{color:C.charLight,fontWeight:400,marginLeft:4}}>· by {firstName}</span>
            )}
          </div>

          {/* Top-right kebab → bottom sheet. Advisor-only. Item handlers
              mirror the desktop actionsFragment exactly: Complete/Reopen do
              the in-place setProjects update so the row visibly flips
              between active and completed partitions; Delete defers to the
              existing two-step setDeleteConfirm flow. */}
          {isAdvisor && (
            <div style={{position:"absolute", top:10, right:10}} onClick={e=>e.stopPropagation()}>
              <OverflowMenu
                title={p.name}
                subtitle="Project actions"
                items={isCompleted ? [
                  {
                    label:"Reopen",
                    onClick: async () => {
                      const updated = await updateProject(p.id, {status:"active"});
                      setProjects(prev => prev.map(pr => pr.id===p.id ? {...pr, ...updated} : pr));
                    },
                  },
                ] : [
                  {
                    label:"Mark complete",
                    onClick: async () => {
                      const updated = await updateProject(p.id, {status:"complete"});
                      setProjects(prev => prev.map(pr => pr.id===p.id ? {...pr, ...updated} : pr));
                    },
                  },
                  {label:"Delete", onClick: () => setDeleteConfirm(p.id), danger: true},
                ]}
              />
            </div>
          )}
        </div>
      );
    }

    // Desktop — two-sub-row layout.
    return (
      <div
        onClick={()=>setSel(p.id)}
        onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
        onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
        style={{...rowBaseStyle, cursor:"pointer", display:"flex", flexDirection:"column", gap:10}}>
        {/* Top sub-row: name | badges | actions */}
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{
              fontFamily:F.display,fontSize:16,
              color:C.charcoal,fontWeight:600,lineHeight:1.25,
              whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
              ...(isCompleted ? {textDecoration:"line-through"} : {}),
            }}>{p.name}</div>
          </div>
          <div onClick={e=>e.stopPropagation()} style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end",maxWidth:"55%",...(isCompleted ? {filter:"grayscale(1)"} : {})}}>
            {badgesFragment}
          </div>
          <div onClick={e=>e.stopPropagation()} style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
            {actionsFragment}
          </div>
        </div>

        {/* Bottom sub-row: next-item | progress | activity */}
        <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          {!isCompleted && topTotal > 0 ? (
            <div style={{flex:1,minWidth:140,fontFamily:F.body,fontSize:11,color:allDone?C.green:C.charMid,fontWeight:allDone?600:400,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
              {allDone
                ? "All tasks complete"
                : (nextItem ? `Next: ${nextItem.text}${nextItem.due_date?` — due ${fmtDate(nextItem.due_date)}`:""}` : "")}
            </div>
          ) : (
            <div style={{flex:1,minWidth:140}}/>
          )}

          <div style={{flexShrink:0,width:140}}>
            <div style={{background:C.bgAlt,borderRadius:3,height:4,overflow:"hidden",marginBottom:3}}>
              <div style={{width:`${pct}%`,height:"100%",background: isCompleted ? C.charLight : (pct>=70?C.green:pct>=40?C.navy:C.amber),borderRadius:3,transition:"width .2s"}}/>
            </div>
            <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight}}>
              {topTotal>0 ? `${pct}% · ${topDone}/${topTotal}` : (isCompleted ? "No tasks recorded" : "No tasks yet")}
            </div>
          </div>

          <div style={{flexShrink:0,minWidth:140,textAlign:"right",fontFamily:F.body,fontSize:11,color:activityColor(lastAt),fontWeight:500}}>
            {lastAt ? humanizeRelative(lastAt) : "No recent activity"}
            {firstName && lastSource==='note' && (
              <span style={{color:C.charLight,fontWeight:400,marginLeft:4}}>· by {firstName}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if(sel!==null){
    const project=projects.find(p=>p.id===sel);
    if(!project){setSel(null);return null;}
   return <ProjectDrill client={client} project={project} isAdvisor={isAdvisor} user={user} onBack={()=>{setSel(null);refreshCardData();}} isMobile={isMobile} advisorId={advisorId} onUpdate={updated=>setProjects(p=>p.map(pr=>pr.id===project.id?{...pr,...updated}:pr))}/>;
  }

  return (
    <div>
      {isMobile?(
        <div style={{marginBottom:20}}>
          <div style={{fontFamily:F.display,fontSize:34,color:C.charcoal,fontWeight:600,marginBottom:6}}>Projects</div>
          <p style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginTop:0,marginBottom:14}}>Active engagements — tap any project to view detail and add notes.</p>
          {isAdvisor&&(
            <ActionButton variant="primary" size="lg" block label="+ New Project" onClick={handleCreate} loading={creating}/>
          )}
        </div>
      ):(
        <SectionHead title="Projects" sub="Active engagements — click any project to view detail and add notes."
          action={isAdvisor&&<Btn variant="primary" small onClick={handleCreate} loading={creating}>+ New Project</Btn>}/>
      )}
      {(activeProjects.length>0||completedProjects.length>0)&&(
        <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:6,marginBottom:10}}>
          <span style={{fontFamily:F.mono,fontSize:isMobile?10:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em"}}>Sort:</span>
          <button onClick={()=>setSortDir(d=>d==='desc'?'asc':'desc')} style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:isMobile?6:5,padding:isMobile?"8px 12px":"3px 9px",fontFamily:F.body,fontSize:isMobile?12:10,fontWeight:isMobile?600:700,cursor:"pointer"}}>Date {sortDir==='desc'?'▼':'▲'}</button>
        </div>
      )}
      {activeProjects.length===0&&completedProjects.length===0?(
        <Card style={{padding:40,textAlign:"center"}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charMid,marginBottom:6}}>No projects yet</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>Projects are created from assessment gaps or added manually by your advisor.</div>
        </Card>
      ):(
        <>
          {activeProjects.length>0&&(
            <div>
              {activeProjects.map((p,i)=>(
                <ProjectRow key={p.id||i} p={p} isFirst={i===0} isLast={i===activeProjects.length-1} variant="active"/>
              ))}
            </div>
          )}
          {completedProjects.length>0&&(
            <div style={{marginTop:14}}>
              <button onClick={()=>setShowCompleted(p=>!p)} style={{background:"none",border:"none",color:C.charMid,fontFamily:F.body,fontSize:12,cursor:"pointer",fontWeight:600,display:"flex",alignItems:"center",gap:6,padding:"8px 0"}}>
                {showCompleted?"▲":"▼"} Completed Projects ({completedProjects.length})
              </button>
              {showCompleted&&(
                <div style={{marginTop:8}}>
                  {completedProjects.map((p,i)=>(
                    <ProjectRow key={p.id} p={p} isFirst={i===0} isLast={i===completedProjects.length-1} variant="completed"/>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── AI TASK SUGGESTER ─────────────────────────────────────────
function AITaskSuggester({project, client, existingItems, advisorId, onAddTasks, onClose}) {
  const [context, setContext] = useState("");
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [adding, setAdding] = useState(false);
  const [prevSuggestions, setPrevSuggestions] = useState([]);
  const [error, setError] = useState(null);

  async function generate(isMore=false) {
    setGenerating(true);
    setError(null);
    if(isMore) setPrevSuggestions(suggestions.map(s=>s.text));
    else setPrevSuggestions([]);
    setSuggestions([]);
    setSelected([]);

    try {
      const knowledge = await getKnowledgeForPrompt(advisorId, {
        mode: 'advisor',
        categories: [
          'voice_guidelines','philosophy','coaching_method',
          'character_and_consistency','leadership_and_manhood',
          'mindset_and_drift','sales_and_business',
          'relationships_and_marriage','stories','one_liners_and_truths',
          'faith_in_practice',
        ],
      });

      const { data: recentNotes } = await supabase
        .from('notes')
        .select('text, author_role, created_at')
        .eq('client_id', client.id)
        .eq('project_id', project.id)
        .eq('author_role', 'advisor')
        .order('created_at', { ascending: false })
        .limit(6);

      const voiceGuidelines = knowledge
        .filter(k=>k.category==='voice_guidelines')
        .map(k=>k.content)
        .join('\n\n');

      const wisdomEntries = knowledge
        .filter(k=>k.category!=='voice_guidelines')
        .map(k=>`[${k.category}] ${k.title}:\n${k.content}`)
        .join('\n\n---\n\n');

      const existingTasks = existingItems
        .filter(i=>!i.parent_id&&!i.done)
        .map(i=>`- ${i.text}`)
        .join('\n');

      const completedTasks = existingItems
        .filter(i=>!i.parent_id&&i.done)
        .map(i=>`- ${i.text}`)
        .join('\n');

      const notesContext = (recentNotes||[]).length>0
        ? (recentNotes||[]).map(n=>n.text).join('\n')
        : 'No recent advisor notes available.';

      const prevContext = prevSuggestions.length>0
        ? `\nPreviously suggested (do NOT repeat these):\n${prevSuggestions.map(s=>`- ${s}`).join('\n')}`
        : '';

      const system = `${voiceGuidelines}

---

You are an AI assistant helping an advisor at BB Integrated generate specific, actionable project checklist tasks.

Here is the advisor's coaching wisdom and frameworks to draw from:

${wisdomEntries}

Your job: suggest exactly 3 checklist tasks for this specific project. Each task should:
- Be concrete and actionable (something that can actually be checked off)
- Sound like the advisor thinking, not generic consultant language
- Draw naturally from the coaching wisdom above when relevant
- Be appropriately scoped — not too broad, not too tiny
- Be different from any tasks already in the checklist
- Directly advance THIS project's stated goal (project name + description) — do not suggest tasks related to other projects, even if context about them appears; if unsure whether something belongs in this project, leave it out

Return ONLY valid JSON. No markdown, no backticks, no explanation. Format:
{
  "suggestions": [
    {
      "text": "The task description",
      "rationale": "One sentence explaining why this task matters for this project",
      "priority": "high" | "medium" | "low"
    }
  ]
}`;

      const userPrompt = `Project: "${project.name}"
Description: ${project.description||'No description provided.'}
Phase: ${project.phase||'Clarity'}

Current open tasks in this project:
${existingTasks||'None yet.'}

Completed tasks:
${completedTasks||'None yet.'}

Recent advisor notes for this client:
${notesContext}

${context?`Advisor context / focus for this round: "${context}"`:''}
${prevContext}

Generate 3 specific, actionable checklist tasks for this project.`;

      const raw = await callClaude(system, userPrompt, 1500);
      const parsed = JSON.parse(raw.replace(/```json|```/g,'').trim());
      setSuggestions(parsed.suggestions||[]);
      setSelected((parsed.suggestions||[]).map((_,i)=>i));
    } catch(e) {
      console.error('AI Task Suggester error:', e);
      setError('Something went wrong generating suggestions. Please try again.');
    }
    setGenerating(false);
  }

  async function handleAddSelected() {
    if(selected.length===0) return;
    setAdding(true);
    const toAdd = suggestions.filter((_,i)=>selected.includes(i));
    await onAddTasks(toAdd);
    setAdding(false);
    onClose();
  }

  function toggleSelect(i) {
    setSelected(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i]);
  }

  return (
    <div style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:10,padding:'14px 16px',marginBottom:14}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{display:'flex',alignItems:'center',gap:7}}>
          <span style={{fontFamily:F.mono,fontSize:13,color:C.navy}}>◈</span>
          <span style={{fontFamily:F.body,fontSize:13,color:C.navy,fontWeight:600}}>AI Task Suggestions</span>
          <span style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>— powered by your knowledge base</span>
        </div>
        <button onClick={onClose} style={{background:'none',border:'none',color:C.charLight,cursor:'pointer',fontSize:16,lineHeight:1}}>✕</button>
      </div>

      <div style={{marginBottom:10}}>
        <textarea
          value={context}
          onChange={e=>setContext(e.target.value)}
          placeholder="Optional: give the AI a focus for this round (e.g. 'focus on the financial side' or 'we just finished onboarding, what's next?')"
          rows={2}
          style={{width:'100%',background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,padding:'8px 11px',color:C.charcoal,fontFamily:F.body,fontSize:12,resize:'none',boxSizing:'border-box'}}
        />
      </div>

      {suggestions.length===0&&!generating&&(
        <button onClick={()=>generate(false)}
          style={{background:C.navy,color:'#fff',border:'none',borderRadius:7,padding:'8px 18px',fontFamily:F.body,fontSize:12,fontWeight:600,cursor:'pointer',width:'100%'}}>
          Generate 3 Suggestions
        </button>
      )}

      {generating&&(
        <div style={{textAlign:'center',padding:'16px 0'}}>
          <div style={{fontFamily:F.body,fontSize:13,color:C.navyMid,animation:'pulse 1.5s infinite'}}>◈ Thinking about this project…</div>
          <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginTop:4}}>Drawing from your knowledge base</div>
        </div>
      )}

      {error&&(
        <div style={{background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:7,padding:'8px 12px',fontFamily:F.body,fontSize:12,color:C.red,marginBottom:10}}>
          {error}
        </div>
      )}

      {suggestions.length>0&&!generating&&(
        <div>
          {suggestions.map((s,i)=>{
            const on=selected.includes(i);
            const pc={high:C.red,medium:C.amber,low:C.green}[s.priority]||C.amber;
            return (
              <div key={i} onClick={()=>toggleSelect(i)} style={{
                background:on?C.surface:'rgba(255,255,255,.5)',
                border:`1px solid ${on?C.navyBorder:C.border}`,
                borderRadius:8,padding:'10px 12px',marginBottom:8,
                cursor:'pointer',transition:'all .15s',
              }}>
                <div style={{display:'flex',alignItems:'flex-start',gap:9}}>
                  <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${on?C.navy:C.border}`,background:on?C.navy:'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:2}}>
                    {on&&<span style={{color:'#fff',fontSize:10}}>✓</span>}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'flex-start',gap:5,marginBottom:4}}>
                      <span style={{fontFamily:F.mono,fontSize:9,color:C.navyLight,marginTop:2,flexShrink:0}} title="AI-suggested task">◈</span>
                      <span style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500,lineHeight:1.4}}>{s.text}</span>
                    </div>
                    <div style={{fontFamily:F.body,fontSize:11,color:C.charMid,lineHeight:1.5,marginLeft:14,marginBottom:5,fontStyle:'italic'}}>{s.rationale}</div>
                    <div style={{marginLeft:14}}>
                      <span style={{background:pc+'15',color:pc,border:`1px solid ${pc}33`,borderRadius:4,padding:'1px 7px',fontSize:9,fontWeight:700,fontFamily:F.body,textTransform:'uppercase'}}>{s.priority} priority</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          <div style={{display:'flex',gap:8,marginTop:4}}>
            <button onClick={handleAddSelected} disabled={adding||selected.length===0}
              style={{flex:1,background:selected.length>0?C.navy:'transparent',color:selected.length>0?'#fff':C.charLight,border:`1px solid ${selected.length>0?C.navy:C.border}`,borderRadius:7,padding:'8px 0',fontFamily:F.body,fontSize:12,fontWeight:600,cursor:selected.length>0?'pointer':'default',opacity:adding?.7:1}}>
              {adding?'Adding…':`Add ${selected.length} Selected →`}
            </button>
            <button onClick={()=>generate(true)} disabled={generating}
              style={{background:'transparent',color:C.navyMid,border:`1px solid ${C.navyBorder}`,borderRadius:7,padding:'8px 14px',fontFamily:F.body,fontSize:11,fontWeight:600,cursor:'pointer'}}>
              Generate 3 More
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
// ── PROJECT CHECKLIST ─────────────────────────────────────────
// Module-level: numeric ordering of priority labels. Lower index = more
// important (high=0). Use with `??` (not `||`) so `0` isn't treated as missing.
const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };
const PRIORITY_COLORS = {
  high: {color:C.red, bg:C.redBg, bd:C.redBd, label:"High"},
  medium: {color:C.amber, bg:C.amberBg, bd:C.amberBd, label:"Medium"},
  low: {color:C.green, bg:C.greenBg, bd:C.greenBd, label:"Low"},
};

function ProjectChecklist({project, client, isAdvisor, user, advisorId, onProgressUpdate}) {
  const [items,setItems]=useState([]);
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [showCompleted,setShowCompleted]=useState(false);
  const [filterPriority,setFilterPriority]=useState("all");
  const [sortBy,setSortBy]=useState(()=>{
    try{const raw=localStorage.getItem('bbportal:taskSort:v1');if(raw){const p=JSON.parse(raw);if(p?.sortBy==='date'||p?.sortBy==='priority')return p.sortBy;}}catch{}
    return "date";
  });
  const [sortDir,setSortDir]=useState(()=>{
    try{const raw=localStorage.getItem('bbportal:taskSort:v1');if(raw){const p=JSON.parse(raw);if(p?.sortDir==='asc'||p?.sortDir==='desc')return p.sortDir;}}catch{}
    return "desc";
  });
  useEffect(()=>{
    try{localStorage.setItem('bbportal:taskSort:v1',JSON.stringify({sortBy,sortDir}));}catch{}
  },[sortBy,sortDir]);
  const [priorityPopupId,setPriorityPopupId]=useState(null);       // item.id of row with open priority popup
  const [expandedSubtasks,setExpandedSubtasks]=useState({});       // { [parentId]: true } → show ALL active subs
  const [expandedCompletedSubtasks,setExpandedCompletedSubtasks]=useState({}); // { [parentId]: true } → show completed subs
  const [editingId,setEditingId]=useState(null);
  const [editText,setEditText]=useState("");
  const [editDueDate,setEditDueDate]=useState("");
  const [deleteConfirm,setDeleteConfirm]=useState(null);
  const [celebrating,setCelebrating]=useState(false);
  const prevPct=useRef(0);

  const [newText,setNewText]=useState("");
  const [newPriority,setNewPriority]=useState("medium");
  const [newOwner,setNewOwner]=useState(user.role==="team_member"?"team_member":(isAdvisor?"advisor":"client"));
  const [newDueDate,setNewDueDate]=useState("");
  const [showAddForm,setShowAddForm]=useState(false);
  const [showAISuggester,setShowAISuggester]=useState(false);

  const [subText,setSubText]=useState({});
  const [subDueDate,setSubDueDate]=useState({});
  const [showSubForm,setShowSubForm]=useState({});

  const subTextareaRefs=useRef({});
  useEffect(()=>{
    Object.entries(showSubForm).forEach(([parentId,isOpen])=>{
      if(isOpen&&subTextareaRefs.current[parentId]){
        const el=subTextareaRefs.current[parentId];
        el.style.height='auto';
        el.style.height=el.scrollHeight+'px';
      }
    });
  },[subText,showSubForm]);

  // Mobile branch is opt-in per-block (heading row, progress placement, button
  // cluster, filter/sort layout). Desktop JSX is untouched — every mobile path
  // is gated by `isMobile`.
  const isMobile=useIsMobile();

  useEffect(()=>{
    getProjectChecklist(project.id).then(data=>{
      setItems(data||[]);
      setLoading(false);
      calcProgress(data||[]);
    });
  },[project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the priority popup when clicking anywhere outside the popup wrapper.
  useEffect(()=>{
    if(!priorityPopupId) return;
    function handle(e){
      if(!e.target.closest('[data-priority-popup]')) setPriorityPopupId(null);
    }
    document.addEventListener('mousedown',handle);
    return ()=>document.removeEventListener('mousedown',handle);
  },[priorityPopupId]);

  function calcProgress(allItems){
    const topLevel=allItems.filter(i=>!i.parent_id);
    const done=topLevel.filter(i=>i.done).length;
    const pct=topLevel.length>0?Math.round((done/topLevel.length)*100):0;
    onProgressUpdate(pct);
    // Trigger celebration when hitting 100% for first time
    if(pct===100&&prevPct.current<100&&topLevel.length>0){
      setCelebrating(true);
      setTimeout(()=>setCelebrating(false),4000);
    }
    prevPct.current=pct;
  }

  async function addItem(){
    if(!newText.trim()) return;
    setSaving(true);
    // Owner derives from creator role, not from form state. Form's owner dropdown is currently a no-op.
    const ownerRole=user.role==="team_member"?"team_member":(isAdvisor?"advisor":"client");
    const item=await addProjectChecklistItem(project.id,client.id,newText,ownerRole,newPriority,newDueDate||null,null,user.id);
    const newItems=[...items,item];
    setItems(newItems);
    calcProgress(newItems);
    setNewText("");setNewPriority("medium");setNewOwner(user.role==="team_member"?"team_member":(isAdvisor?"advisor":"client"));setNewDueDate("");setShowAddForm(false);
    setSaving(false);
  }

  async function addSubItem(parentId){
    const text=subText[parentId];
    if(!text?.trim()) return;
    // Sub-tasks inherit parent priority; owner derives from creator role.
    const parent=items.find(i=>i.id===parentId);
    const priority=parent?.priority||"medium";
    const item=await addProjectChecklistItem(project.id,client.id,text,user.role==="team_member"?"team_member":(isAdvisor?"advisor":"client"),priority,subDueDate[parentId]||null,parentId,user.id);
    setItems(p=>[...p,item]);
    setSubText(p=>({...p,[parentId]:""}));
    setSubDueDate(p=>({...p,[parentId]:""}));
    setShowSubForm(p=>({...p,[parentId]:false}));
  }

  async function toggleItem(item){
    const updated=await toggleProjectChecklistItem(item.id,!item.done);
    const newItems=items.map(i=>i.id===item.id?{...i,done:updated.done}:i);
    setItems(newItems);
    calcProgress(newItems);
  }

  async function saveEdit(itemId){
    if(!editText.trim()) return;
    const due_date=editDueDate||null;
    await updateProjectChecklistItem(itemId,{text:editText,due_date});
    setItems(p=>p.map(i=>i.id===itemId?{...i,text:editText,due_date}:i));
    setEditingId(null);setEditText("");setEditDueDate("");
  }

  async function updatePriority(itemId,priority){
    await updateProjectChecklistItem(itemId,{priority});
    setItems(p=>p.map(i=>i.id===itemId?{...i,priority}:i));
  }

  async function updateOwner(itemId,owner_role){
    await updateProjectChecklistItem(itemId,{owner_role});
    setItems(p=>p.map(i=>i.id===itemId?{...i,owner_role}:i));
  }

  async function updateDueDate(itemId,due_date){
    await updateProjectChecklistItem(itemId,{due_date:due_date||null});
    setItems(p=>p.map(i=>i.id===itemId?{...i,due_date:due_date||null}:i));
  }

  async function deleteItem(itemId){
    await deleteProjectChecklistItem(itemId);
    const newItems=items.filter(i=>i.id!==itemId&&i.parent_id!==itemId);
    setItems(newItems);
    calcProgress(newItems);
    setDeleteConfirm(null);
  }

  // Organize items
  const topLevel=items.filter(i=>!i.parent_id);
  const getSubItems=(parentId)=>items.filter(i=>i.parent_id===parentId);
  const activeItems=topLevel.filter(i=>!i.done);
  const completedItems=topLevel.filter(i=>i.done);
  const done=completedItems.length;
  const pct=topLevel.length>0?Math.round((done/topLevel.length)*100):0;
  const color=pct>=70?C.green:pct>=40?C.navy:C.amber;

  // Filter and sort active items
  const filteredActive=activeItems
    .filter(i=>filterPriority==="all"||i.priority===filterPriority)
    .sort((a,b)=>{
      // "desc" is the user-facing convention: most important first / newest first.
      // For priority, that's ascending by PRIORITY_ORDER index (high=0 → top).
      // `??` (not `||`) so high's 0 isn't treated as missing.
      if(sortBy==="priority"){
        const diff=(PRIORITY_ORDER[a.priority]??1)-(PRIORITY_ORDER[b.priority]??1);
        return sortDir==='desc'?diff:-diff;
      }
      if(sortBy==="date"){
        const d=new Date(a.created_at)-new Date(b.created_at);
        return sortDir==='desc'?-d:d;
      }
      return 0;
    });

  const isOverdue=(item)=>item.due_date&&new Date(item.due_date)<new Date()&&!item.done;

  const ChecklistItem=({item,isSubItem=false,isFirst=false,subNumber=null})=>{
    const overdue=isOverdue(item);
    const isEditing=editingId===item.id;
    const dotColor=item.priority==="high"?C.red:item.priority==="low"?C.green:"#f5c518";
    const roleLabel=item.owner_role==="advisor"?"Advisor":item.owner_role==="team_member"?"Team Member":"Client";

    // Sub-task organization — only meaningful for top-level rows.
    const subs=isSubItem?[]:getSubItems(item.id);
    const activeSubs=subs.filter(s=>!s.done).sort((a,b)=>new Date(a.created_at)-new Date(b.created_at));
    const completedSubs=subs.filter(s=>s.done).sort((a,b)=>new Date(a.created_at)-new Date(b.created_at));
    const subsExpanded=expandedSubtasks[item.id];
    const completedSubsExpanded=expandedCompletedSubtasks[item.id];
    const visibleActive=subsExpanded?activeSubs:activeSubs.slice(0,3);

    // Inline edit form auto-grow: the mobile <textarea> resizes to fit its
    // content as the user types. Effect runs on edit-open and whenever editText
    // changes. When isEditing is false the ref is null and the effect no-ops.
    const editTextareaRef=useRef(null);
    useEffect(()=>{
      if(editTextareaRef.current){
        editTextareaRef.current.style.height='auto';
        editTextareaRef.current.style.height=editTextareaRef.current.scrollHeight+'px';
      }
    },[editText,isEditing]);

    return (
      <div style={{marginLeft:isSubItem?(isMobile?12:20):0}}>
        {deleteConfirm===item.id?(
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:7,marginBottom:6}}>
            <span style={{fontFamily:F.body,fontSize:12,color:C.red}}>Delete "{item.text.slice(0,40)}"?</span>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>deleteItem(item.id)} style={{background:C.red,color:"#fff",border:"none",borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Delete</button>
              <button onClick={()=>setDeleteConfirm(null)} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 8px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        ):(
          <div
            onMouseEnter={e=>{ if(deleteConfirm!==item.id) e.currentTarget.style.background = C.bgAlt; }}
            onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}
            style={{
              padding:isSubItem?(isMobile?"12px 0":"8px 0"):"22px 18px",
              borderTop:(isSubItem||isFirst)?"none":`1px solid ${C.border}`,
              transition:"background .15s",
              ...(isMobile?{position:"relative"}:{}),
            }}>
            {/* Main row */}
            <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
              {/* Priority dot (top-level) or sub-task number */}
              {isSubItem?(
                <div style={{width:20,fontFamily:F.body,fontSize:13,color:C.charMid,fontWeight:600,textAlign:"right",flexShrink:0,marginTop:1}}>{subNumber}.</div>
              ):(
                <div data-priority-popup style={{position:"relative",flexShrink:0}}>
                  <div onClick={()=>setPriorityPopupId(item.id===priorityPopupId?null:item.id)}
                    title={`Priority: ${item.priority||"medium"} — click to change`}
                    style={{width:14,height:14,borderRadius:"50%",background:dotColor,cursor:"pointer",marginTop:4}}/>
                  {priorityPopupId===item.id&&(
                    <div style={{position:"absolute",top:24,left:0,zIndex:50,background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,boxShadow:"0 6px 18px rgba(0,0,0,.12)",padding:"6px 0",minWidth:140}}>
                      <div style={{padding:"4px 12px",fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em"}}>Priority</div>
                      {[["high","🔴 High"],["medium","🟡 Medium"],["low","🟢 Low"]].map(([v,l])=>(
                        <div key={v} onClick={()=>{updatePriority(item.id,v);setPriorityPopupId(null);}}
                          onMouseEnter={e=>e.currentTarget.style.background=C.bgAlt}
                          onMouseLeave={e=>e.currentTarget.style.background="transparent"}
                          style={{padding:"6px 12px",fontFamily:F.body,fontSize:12,color:C.charcoal,cursor:"pointer",transition:"background .15s"}}>{l}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Content */}
              <div style={{
                flex:1,minWidth:0,
                ...(isMobile&&(user.role!=="team_member"||item.owner_id===user.id)?{paddingRight:44}:{}),
              }}>
                {isEditing?(
                  <div style={{marginBottom:4}}>
                    {isMobile?(
                      <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:6}}>
                        <textarea ref={editTextareaRef} value={editText} onChange={e=>setEditText(e.target.value)}
                          onKeyDown={e=>{if(e.key==='Enter'&&(e.metaKey||e.ctrlKey))saveEdit(item.id);if(e.key==='Escape'){setEditingId(null);setEditText("");setEditDueDate("");}}}
                          autoFocus rows={1}
                          style={{width:'100%',boxSizing:'border-box',background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:'8px 10px',fontFamily:F.body,fontSize:14,color:C.charcoal,lineHeight:1.4,resize:'none',minHeight:60,outline:'none'}}/>
                        <div style={{display:'flex',gap:6,alignItems:'center'}}>
                          <button onClick={()=>saveEdit(item.id)} style={{background:C.navy,color:"#fff",border:"none",borderRadius:5,padding:"6px 14px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Save</button>
                        </div>
                      </div>
                    ):(
                      <div style={{display:"flex",gap:6,marginBottom:6}}>
                        <input value={editText} onChange={e=>setEditText(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveEdit(item.id);if(e.key==="Escape"){setEditingId(null);setEditText("");setEditDueDate("");}}}
                          autoFocus style={{flex:1,background:C.bgAlt,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"4px 8px",color:C.charcoal,fontFamily:F.body,fontSize:11,boxSizing:"border-box"}}/>
                        <button onClick={()=>saveEdit(item.id)} style={{background:C.navy,color:"#fff",border:"none",borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Save</button>
                        <button onClick={()=>{setEditingId(null);setEditText("");setEditDueDate("");}} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 8px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>✕</button>
                      </div>
                    )}
                    {/* Due date: collapsed by default ("+ Set due date") so an
                        empty <input type="date"> doesn't sit in the form looking
                        ambiguous (browser pickers default to today on focus and
                        can accidentally commit it). User opts in to expose the
                        actual date input. */}
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      {editDueDate?(
                        <>
                          <span style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>Due:</span>
                          <input type="date" value={editDueDate} onChange={e=>setEditDueDate(e.target.value)}
                            style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 8px",fontFamily:F.body,fontSize:11,color:C.charcoal}}/>
                          <button onClick={()=>setEditDueDate("")} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Clear</button>
                        </>
                      ):(
                        <button onClick={()=>setEditDueDate(new Date().toISOString().slice(0,10))} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,color:C.charLight,cursor:"pointer"}}>+ Set due date</button>
                      )}
                    </div>
                  </div>
                ):isMobile?(
                  <div style={{display:'flex',alignItems:'flex-start',gap:4,marginBottom:8}}>
                    {item.source==='ai'&&<span style={{fontFamily:F.mono,fontSize:9,color:C.navyLight,marginTop:3,flexShrink:0}} title="AI-suggested task">◈</span>}
                    <div style={{fontFamily:F.body,fontSize:14,color:item.done?C.charLight:overdue?C.red:C.charcoal,textDecoration:item.done?"line-through":"none",lineHeight:1.4}}>{item.text}</div>
                  </div>
                ):(
                  <div style={{display:'flex',alignItems:'flex-start',gap:4,marginBottom:6}}>
                    {item.source==='ai'&&<span style={{fontFamily:F.mono,fontSize:9,color:C.navyLight,marginTop:3,flexShrink:0}} title="AI-suggested task">◈</span>}
                    <div style={{fontFamily:F.body,fontSize:13,color:item.done?C.charLight:overdue?C.red:C.charcoal,textDecoration:item.done?"line-through":"none",lineHeight:1.4}}>{item.text}</div>
                  </div>
                )}

                {/* Action area: mobile stacks meta + Mark Complete (the kebab
                    above handles +/✎/🗑); desktop keeps the existing meta-left,
                    Mark Complete + (+/✎/🗑) cluster-right pattern. */}
                {isMobile?(
                  <>
                    <div style={{fontFamily:F.body,fontSize:12,color:C.charLight,marginBottom:10,display:'flex',flexWrap:'wrap',gap:10}}>
                      <span>{roleLabel} · {fmtDate(item.created_at)}</span>
                      {item.due_date&&<span style={{color:overdue?C.red:C.charLight,fontWeight:overdue?600:400}}>Due {fmtDate(item.due_date)}</span>}
                    </div>
                    <div style={{display:'flex'}}>
                      <button onClick={()=>toggleItem(item)}
                        style={{background:item.done?C.greenBg:"transparent",color:item.done?C.green:C.charMid,border:`1px solid ${item.done?C.greenBd:C.border}`,borderRadius:5,padding:"3px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                        {item.done?"✓ Completed":"Mark Complete"}
                      </button>
                    </div>
                  </>
                ):(
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,marginTop:6,flexWrap:"wrap"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                      <span style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>{roleLabel} · {fmtDate(item.created_at)}</span>
                      {item.due_date&&(
                        <span style={{fontFamily:F.body,fontSize:11,color:overdue?C.red:C.charLight,fontWeight:overdue?600:400}}>Due {fmtDate(item.due_date)}</span>
                      )}
                    </div>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      <button onClick={()=>toggleItem(item)}
                        style={{background:item.done?C.greenBg:"transparent",color:item.done?C.green:C.charMid,border:`1px solid ${item.done?C.greenBd:C.border}`,borderRadius:5,padding:"3px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                        {item.done?"✓ Completed":"Mark Complete"}
                      </button>
                      {(user.role!=="team_member"||item.owner_id===user.id)&&(
                        <div style={{display:"flex",gap:3,flexShrink:0}}>
                          {!isSubItem&&(
                            <button onClick={()=>setShowSubForm(p=>({...p,[item.id]:!p[item.id]}))} title="Add sub-task"
                              style={{background:"none",border:`1px solid ${C.border}`,borderRadius:4,width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:11,color:C.charLight}}>+</button>
                          )}
                          <button onClick={()=>{setEditingId(item.id);setEditText(item.text);setEditDueDate(item.due_date||"");}} title="Edit"
                            style={{background:"none",border:`1px solid ${C.border}`,borderRadius:4,width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:11,color:C.charLight}}>✎</button>
                          <button onClick={()=>setDeleteConfirm(item.id)} title="Delete"
                            style={{background:"none",border:`1px solid ${C.border}`,borderRadius:4,width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:11,color:C.charLight}}>🗑</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile-only kebab → bottom sheet. Anchors absolute to the row
                wrapper (which gains position:relative on mobile). Role-gated
                identically to the desktop (+/✎/🗑) cluster. Active top-level
                tasks include "Add sub-task"; sub-tasks and completed tasks
                only get Edit + Delete. */}
            {isMobile&&!isEditing&&(user.role!=="team_member"||item.owner_id===user.id)&&(
              <div style={{position:"absolute",top:10,right:10}} onClick={e=>e.stopPropagation()}>
                <OverflowMenu
                  title={item.text.length>40?item.text.slice(0,40)+"…":item.text}
                  subtitle={isSubItem?"Sub-task actions":"Task actions"}
                  items={[
                    ...((!item.done&&!isSubItem)?[{label:"Add sub-task",onClick:()=>setShowSubForm(p=>({...p,[item.id]:!p[item.id]}))}]:[]),
                    {label:"Edit",onClick:()=>{setEditingId(item.id);setEditText(item.text);setEditDueDate(item.due_date||"");}},
                    {label:"Delete",onClick:()=>setDeleteConfirm(item.id),danger:true},
                  ]}
                />
              </div>
            )}

            {/* Mobile-only: while editing, the kebab is hidden and this ✕
                takes its exact screen position so the tap target doesn't
                move between view → edit modes. Cancel handler is the same
                state-reset triple used everywhere else. */}
            {isMobile&&isEditing&&(
              <button
                onClick={()=>{setEditingId(null);setEditText("");setEditDueDate("");}}
                aria-label="Cancel edit"
                style={{
                  position:"absolute",
                  top:10,
                  right:10,
                  background:C.bgAlt,
                  border:`1px solid ${C.border}`,
                  borderRadius:6,
                  width:36,
                  height:36,
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center",
                  cursor:"pointer",
                  fontSize:14,
                  color:C.charMid,
                  padding:0,
                }}
              >✕</button>
            )}

            {/* Sub-item add form — priority select removed; sub-tasks inherit parent priority */}
            {showSubForm[item.id]&&(isMobile?(
              <div style={{marginLeft:12,marginTop:8,display:"flex",flexDirection:"column",gap:8}}>
                <textarea
                  ref={el=>{if(el)subTextareaRefs.current[item.id]=el;else delete subTextareaRefs.current[item.id];}}
                  value={subText[item.id]||""}
                  onChange={e=>setSubText(p=>({...p,[item.id]:e.target.value}))}
                  onKeyDown={e=>{
                    if(e.key==="Enter"&&(e.metaKey||e.ctrlKey)) addSubItem(item.id);
                    if(e.key==="Escape") setShowSubForm(p=>({...p,[item.id]:false}));
                  }}
                  placeholder="Add sub-task…"
                  autoFocus
                  rows={1}
                  style={{width:"100%",boxSizing:"border-box",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",fontFamily:F.body,fontSize:14,color:C.charcoal,lineHeight:1.4,resize:"none",minHeight:60,outline:"none"}}
                />
                <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                  {subDueDate[item.id]?(
                    <>
                      <span style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>Due:</span>
                      <input type="date" value={subDueDate[item.id]||""} onChange={e=>setSubDueDate(p=>({...p,[item.id]:e.target.value}))}
                        style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 9px",fontFamily:F.mono,fontSize:11,color:C.charMid}}/>
                      <button onClick={()=>setSubDueDate(p=>({...p,[item.id]:""}))} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Clear</button>
                    </>
                  ):(
                    <button onClick={()=>setSubDueDate(p=>({...p,[item.id]:new Date().toISOString().slice(0,10)}))} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,color:C.charLight,cursor:"pointer"}}>+ Set due date</button>
                  )}
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <button onClick={()=>addSubItem(item.id)} style={{background:C.navy,color:"#fff",border:"none",borderRadius:6,padding:"6px 14px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Add</button>
                  <button onClick={()=>setShowSubForm(p=>({...p,[item.id]:false}))} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"6px 14px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>✕</button>
                </div>
              </div>
            ):(
              <div style={{marginLeft:26,marginTop:8,display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                <input value={subText[item.id]||""} onChange={e=>setSubText(p=>({...p,[item.id]:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addSubItem(item.id)}
                  placeholder="Add sub-task…" autoFocus style={{flex:1,minWidth:140,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 9px",color:C.charcoal,fontFamily:F.body,fontSize:12,boxSizing:"border-box"}}/>
                {subDueDate[item.id]?(
                  <>
                    <span style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>Due:</span>
                    <input type="date" value={subDueDate[item.id]||""} onChange={e=>setSubDueDate(p=>({...p,[item.id]:e.target.value}))}
                      style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 9px",fontFamily:F.mono,fontSize:11,color:C.charMid}}/>
                    <button onClick={()=>setSubDueDate(p=>({...p,[item.id]:""}))} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Clear</button>
                  </>
                ):(
                  <button onClick={()=>setSubDueDate(p=>({...p,[item.id]:new Date().toISOString().slice(0,10)}))} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,color:C.charLight,cursor:"pointer"}}>+ Set due date</button>
                )}
                <button onClick={()=>addSubItem(item.id)} style={{background:C.navy,color:"#fff",border:"none",borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Add</button>
                <button onClick={()=>setShowSubForm(p=>({...p,[item.id]:false}))} style={{background:"none",border:"none",color:C.charLight,cursor:"pointer",fontSize:12}}>✕</button>
              </div>
            ))}

            {/* Sub-tasks: active first (capped at 3 with show-more), then completed group (collapsed by default) */}
            {!isSubItem&&(activeSubs.length>0||completedSubs.length>0)&&(
              <div style={{marginTop:6}}>
                {visibleActive.map((sub,idx)=>(
                  <ChecklistItem key={sub.id} item={sub} isSubItem={true} subNumber={idx+1}/>
                ))}
                {activeSubs.length>3&&(
                  <div onClick={()=>setExpandedSubtasks(p=>({...p,[item.id]:!p[item.id]}))}
                    style={{paddingLeft:38,paddingTop:6,paddingBottom:6,cursor:"pointer",fontFamily:F.body,fontSize:11,color:C.navyMid,fontWeight:600}}>
                    {subsExpanded?"▴ Hide sub-tasks":`▾ Show ${activeSubs.length-3} more sub-tasks`}
                  </div>
                )}
                {completedSubs.length>0&&(
                  <>
                    <div onClick={()=>setExpandedCompletedSubtasks(p=>({...p,[item.id]:!p[item.id]}))}
                      style={{paddingLeft:38,paddingTop:6,paddingBottom:6,cursor:"pointer",fontFamily:F.body,fontSize:11,color:C.navyMid,fontWeight:600}}>
                      {completedSubsExpanded?"▴ Hide completed":`▾ Show ${completedSubs.length} completed sub-tasks`}
                    </div>
                    {completedSubsExpanded&&completedSubs.map((sub,idx)=>(
                      <div key={sub.id} style={{opacity:.55}}>
                        <ChecklistItem item={sub} isSubItem={true} subNumber={idx+1}/>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if(loading) return <Spinner/>;

  return (
    <div style={{position:"relative"}}>
      {/* 🎉 Celebration overlay */}
      {celebrating&&(
        <div style={{position:"fixed",inset:0,zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",pointerEvents:"none"}}>
          <div style={{background:"linear-gradient(135deg,#1e3a5f 0%,#2d6a4f 100%)",borderRadius:20,padding:"40px 56px",textAlign:"center",boxShadow:"0 24px 80px rgba(0,0,0,.35)",animation:"celebrationPop .4s ease-out"}}>
            <div style={{fontSize:56,marginBottom:12}}>🎉</div>
            <div style={{fontFamily:F.display,fontSize:28,color:"#fff",fontWeight:700,marginBottom:6}}>Project Complete!</div>
            <div style={{fontFamily:F.body,fontSize:15,color:"rgba(255,255,255,.75)",marginBottom:4}}>{project.name}</div>
            <div style={{fontFamily:F.body,fontSize:13,color:"rgba(255,255,255,.5)"}}>All tasks finished — great work!</div>
          </div>
          <style>{`@keyframes celebrationPop{from{transform:scale(.5);opacity:0}to{transform:scale(1);opacity:1}}`}</style>
        </div>
      )}
      <div>
      {/* Header — mobile splits buttons into a dedicated row below; desktop
          keeps the original single-row layout. */}
      {isMobile?(
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:12}}>
          <div style={{fontFamily:F.display,fontSize:18,color:C.charcoal,fontWeight:600}}>Tasks</div>
          <span style={{fontFamily:F.mono,fontSize:12,color,fontWeight:600}}>{done}/{topLevel.length} · {pct}%</span>
        </div>
      ):(
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600}}>Project Task List</div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontFamily:F.mono,fontSize:11,color,fontWeight:600}}>{done}/{topLevel.length} · {pct}%</span>
            {isAdvisor&&(
              <button onClick={()=>{setShowAISuggester(p=>!p);setShowAddForm(false);}}
                style={{background:showAISuggester?C.navyFaint:"transparent",color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"4px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                {showAISuggester?"✕ AI Suggest":"◈ AI Suggest"}
              </button>
            )}
            <button onClick={()=>{setShowAddForm(p=>!p);setShowAISuggester(false);}} style={{background:C.navy,color:"#fff",border:"none",borderRadius:6,padding:"4px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
              {showAddForm?"Cancel":"+ Add Task"}
            </button>
          </div>
        </div>
      )}
      {/* Mobile-only: progress bar directly under the heading row, then the
          button cluster. Desktop renders both at their original positions
          (progress bar below the AI Suggester panel; buttons inline in the
          header above). */}
      {isMobile&&topLevel.length>0&&(
        <div style={{background:C.bgAlt,borderRadius:3,height:5,marginTop:10,marginBottom:14,overflow:"hidden"}}>
          <div style={{width:`${pct}%`,height:"100%",background:color,borderRadius:3,transition:"width .5s"}}/>
        </div>
      )}
      {isMobile&&(
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          {isAdvisor?(
            <>
              <ActionButton
                label={showAISuggester?"✕ AI Suggest":"◈ AI Suggest"}
                onClick={()=>{setShowAISuggester(p=>!p);setShowAddForm(false);}}
                variant="secondary"
                size="lg"
                block
              />
              <ActionButton
                label={showAddForm?"Cancel":"+ Add Task"}
                onClick={()=>{setShowAddForm(p=>!p);setShowAISuggester(false);}}
                variant="primary"
                size="lg"
                block
              />
            </>
          ):(
            <ActionButton
              label={showAddForm?"Cancel":"+ Add Task"}
              onClick={()=>{setShowAddForm(p=>!p);setShowAISuggester(false);}}
              variant="primary"
              size="lg"
              block
            />
          )}
        </div>
      )}
{/* AI Task Suggester panel */}
        {showAISuggester&&isAdvisor&&(
          <AITaskSuggester
            project={project}
            client={client}
            existingItems={items}
            advisorId={advisorId||client.advisor_id}
            onAddTasks={async(suggestions)=>{
              for(const s of suggestions){
                const item=await addProjectChecklistItem(
                  project.id, client.id,
                  s.text, 'advisor',
                  s.priority||'medium',
                  null, null, user.id
                );
                await supabase.from('project_checklist_items')
                  .update({source:'ai'})
                  .eq('id',item.id);
                const updatedItem={...item,source:'ai'};
                setItems(prev=>{
                  const newItems=[...prev,updatedItem];
                  calcProgress(newItems);
                  return newItems;
                });
              }
            }}
            onClose={()=>setShowAISuggester(false)}
          />
        )}
      {/* Progress bar (desktop position) — mobile renders this above, directly
          under the heading row. */}
      {!isMobile&&topLevel.length>0&&(
        <div style={{background:C.bgAlt,borderRadius:3,height:5,marginBottom:14,overflow:"hidden"}}>
          <div style={{width:`${pct}%`,height:"100%",background:color,borderRadius:3,transition:"width .5s"}}/>
        </div>
      )}

      {/* Add task form */}
      {showAddForm&&(
        <div style={{background:C.bgAlt,borderRadius:8,padding:"12px 14px",marginBottom:14}}>
          <div style={{marginBottom:8}}>
            <input value={newText} onChange={e=>setNewText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addItem()}
              placeholder="Task description…" autoFocus
              style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 11px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <select value={newPriority} onChange={e=>setNewPriority(e.target.value)}
              style={{background:PRIORITY_COLORS[newPriority].bg,color:PRIORITY_COLORS[newPriority].color,border:`1px solid ${PRIORITY_COLORS[newPriority].bd}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,fontWeight:400}}>
              <option value="high">high priority</option>
              <option value="medium">medium priority</option>
              <option value="low">low priority</option>
            </select>
            <select value={newOwner} onChange={e=>setNewOwner(e.target.value)}
              style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,color:C.charcoal,fontWeight:400}}>
              <option value="advisor">advisor owns</option>
              <option value="client">{(client.user?.name?.split(" ")[0]||"client").toLowerCase()} owns</option>
            </select>
            {/* Unified due-date pattern (matches edit form and sub-task form):
                collapsed "+ Set due date" until the user opts in, then reveal the
                date input + Clear. Add Task is anchored to the far right via
                marginLeft:auto so the left-side controls (priority/owner/due)
                stay clustered. */}
            {newDueDate?(
              <>
                <span style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>Due:</span>
                <input type="date" value={newDueDate} onChange={e=>setNewDueDate(e.target.value)}
                  style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.mono,fontSize:11,color:C.charMid}}/>
                <button onClick={()=>setNewDueDate("")} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Clear</button>
              </>
            ):(
              <button onClick={()=>setNewDueDate(new Date().toISOString().slice(0,10))} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 8px",fontFamily:F.body,fontSize:11,color:C.charLight,cursor:"pointer"}}>+ Set due date</button>
            )}
            <div style={{marginLeft:"auto"}}>
              <Btn variant="primary" small onClick={addItem} loading={saving} disabled={!newText.trim()}>Add Task</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Filter & sort bar — mobile stacks filter + sort into two rows with
          their own labels and larger touch targets; desktop keeps a single
          flex row with Sort pushed right via marginLeft:auto. */}
      {topLevel.length>0&&(isMobile?(
        <>
          {/* Mobile Filter row */}
          <div style={{marginBottom:10}}>
            <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>Filter</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4, 1fr)",gap:6}}>
              {["all","high","medium","low"].map(p=>{
                const pc=p==="all"?null:PRIORITY_COLORS[p];
                return (
                  <button key={p} onClick={()=>setFilterPriority(p)} style={{background:filterPriority===p?(pc?pc.bg:C.navyFaint):"transparent",color:filterPriority===p?(pc?pc.color:C.navy):C.charLight,border:`1px solid ${filterPriority===p?(pc?pc.bd:C.navyBorder):C.border}`,borderRadius:6,padding:"8px 12px",fontFamily:F.body,fontSize:12,fontWeight:filterPriority===p?700:400,cursor:"pointer",minHeight:36}}>
                    {p==="all"?"All":pc.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Mobile Sort row */}
          <div style={{marginBottom:14,paddingBottom:12,borderBottom:`1px solid ${C.border}`}}>
            <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>Sort</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(2, 1fr)",gap:6}}>
              {[["date","Date"],["priority","Priority"]].map(([v,l])=>{
                const active=sortBy===v;
                return (
                  <button key={v} onClick={()=>{ if(active) setSortDir(d=>d==='desc'?'asc':'desc'); else { setSortBy(v); setSortDir('desc'); } }} style={{background:active?C.navyFaint:"transparent",color:active?C.navy:C.charLight,border:`1px solid ${active?C.navyBorder:C.border}`,borderRadius:6,padding:"8px 12px",fontFamily:F.body,fontSize:12,fontWeight:active?700:400,cursor:"pointer",minHeight:36}}>{active?`${l} ${sortDir==='desc'?'▼':'▲'}`:l}</button>
                );
              })}
            </div>
          </div>
        </>
      ):(
        <div style={{display:"flex",gap:8,paddingBottom:12,marginBottom:0,flexWrap:"wrap",alignItems:"center",borderBottom:`1px solid ${C.border}`}}>
          <span style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em"}}>Filter:</span>
          {["all","high","medium","low"].map(p=>{
            const pc=p==="all"?null:PRIORITY_COLORS[p];
            return (
              <button key={p} onClick={()=>setFilterPriority(p)} style={{background:filterPriority===p?(pc?pc.bg:C.navyFaint):"transparent",color:filterPriority===p?(pc?pc.color:C.navy):C.charLight,border:`1px solid ${filterPriority===p?(pc?pc.bd:C.navyBorder):C.border}`,borderRadius:5,padding:"3px 9px",fontFamily:F.body,fontSize:10,fontWeight:filterPriority===p?700:400,cursor:"pointer"}}>
                {p==="all"?"All":pc.label}
              </button>
            );
          })}
          <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
            <span style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em"}}>Sort:</span>
            {[["date","Date"],["priority","Priority"]].map(([v,l])=>{
              const active=sortBy===v;
              return (
                <button key={v} onClick={()=>{ if(active) setSortDir(d=>d==='desc'?'asc':'desc'); else { setSortBy(v); setSortDir('desc'); } }} style={{background:active?C.navyFaint:"transparent",color:active?C.navy:C.charLight,border:`1px solid ${active?C.navyBorder:C.border}`,borderRadius:5,padding:"3px 9px",fontFamily:F.body,fontSize:10,fontWeight:active?700:400,cursor:"pointer"}}>{active?`${l} ${sortDir==='desc'?'▼':'▲'}`:l}</button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Empty state */}
      {topLevel.length===0&&!showAddForm&&(
        <div style={{textAlign:"center",padding:"24px 0",fontFamily:F.body,fontSize:13,color:C.charLight,fontStyle:"italic"}}>
          No tasks yet. Click "+ Add Task" to get started.
        </div>
      )}

      {/* Active items */}
      {filteredActive.map((item,idx)=><ChecklistItem key={item.id} item={item} isFirst={idx===0}/>)}

      {/* Completed section */}
      {completedItems.length>0&&(
        <div style={{marginTop:12}}>
          <button onClick={()=>setShowCompleted(p=>!p)} style={{background:"none",border:"none",color:C.charMid,fontFamily:F.body,fontSize:11,cursor:"pointer",fontWeight:600,display:"flex",alignItems:"center",gap:5,padding:"6px 0"}}>
            {showCompleted?"▲":"▼"} Completed ({completedItems.length})
          </button>
          {showCompleted&&(
            <div style={{opacity:.65,marginTop:4}}>
              {completedItems.map(item=><ChecklistItem key={item.id} item={item}/>)}
            </div>
          )}
        </div>
      )}
    </div>
  </div>
  );
}

// ── PROJECT DRILL ─────────────────────────────────────────────
function ProjectDrill({client, project, isAdvisor, user, onBack, onUpdate, isMobile, advisorId}) {
  const [notes,setNotes]=useState([]);
  const [projectFiles,setProjectFiles]=useState([]);
  const [projectTimeline,setProjectTimeline]=useState([]);
  const [brianNote,setBNote]=useState("");
  const [clientNote,setCNote]=useState("");
  const [newUpdate,setNewUpdate]=useState("");
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [savingUpdate,setSavingUpdate]=useState(false);
  const [editFields,setEditFields]=useState({
    name:project.name,
    description:project.description||"",
    start_date:project.start_date||"",
    deadline:project.deadline||"TBD",
    progress:project.progress||0,
  });
  const [hasChanges,setHasChanges]=useState(false);
  const [savingFields,setSavingFields]=useState(false);
  const [projFileInput]=useState(()=>document.createElement('input'));
  const [uploadingFile,setUploadingFile]=useState(false);
  const [updatesPage,setUpdatesPage]=useState(0);
  const UPDATES_PER_PAGE=10;
  const UPDATES_INITIAL=5;
  const [showAllUpdates,setShowAllUpdates]=useState(false);
  const [activeSection,setActiveSection]=useState(user.role==="team_member"?"files":"notes"); // notes | files | timeline
  const [editingNote,setEditingNote]=useState(null);     // { id, draft } | null
  const [savingNote,setSavingNote]=useState(false);
  const [deleteConfirmNote,setDeleteConfirmNote]=useState(null);
  const [editingDescription,setEditingDescription]=useState(false);
  const {setHeader}=useContext(StickyHeaderContext);

  useEffect(()=>{
    async function load(){
      setLoading(true);
      const [n,f]=await Promise.all([
        getNotes(project.id),
        getFiles(client.id),
      ]);
      setNotes(n||[]);
      setProjectFiles((f||[]).filter(fi=>fi.category==="Projects"||fi.name?.includes(project.name)));
      setLoading(false);
    }
    load();
  },[project.id,client.id]);

  function handleFieldChange(field,value){
    setEditFields(p=>({...p,[field]:value}));
    setHasChanges(true);
  }

  async function saveFields(){
    setSavingFields(true);
    const updated=await updateProject(project.id,{
      name:editFields.name,
      description:editFields.description,
      start_date:editFields.start_date||null,
      deadline:editFields.deadline||"TBD",
      progress:Number(editFields.progress)||0,
    });
    onUpdate(updated);
    setHasChanges(false);
    setSavingFields(false);
  }

  // Description-only save — used by the inline view/edit pattern.
  function cancelEditDescription(){
    setEditFields(p=>({...p,description:project.description||""}));
    setEditingDescription(false);
  }
  async function saveDescription(){
    setSavingFields(true);
    const updated=await updateProject(project.id,{description:editFields.description});
    onUpdate(updated);
    // If only the description was being edited and no other field is pending, clear the global Unsaved indicator.
    if (editFields.name === project.name && editFields.start_date === (project.start_date||"") && editFields.deadline === (project.deadline||"TBD") && String(editFields.progress) === String(project.progress||0)) {
      setHasChanges(false);
    }
    setEditingDescription(false);
    setSavingFields(false);
  }

  function handleBack(){
    if(hasChanges){
      if(window.confirm("You have unsaved changes. Leave without saving?")) onBack();
    } else onBack();
  }

  async function saveNote(role){
    const text=role==="advisor"?brianNote:clientNote;
    if(!text.trim()) return;
    setSaving(true);
    const note=await addNote(project.id,client.id,role,user.id,text);
    setNotes(p=>[...p,note]);
    if(role==="advisor") setBNote(""); else setCNote("");
    setSaving(false);
  }

  async function postUpdate(){
    if(!newUpdate.trim()) return;
    setSavingUpdate(true);
    const role=isAdvisor?"advisor":"client";
    const note=await addNote(project.id,client.id,role,user.id,`📌 Update: ${newUpdate}`);
    setNotes(p=>[...p,note]);
    setNewUpdate("");
    setSavingUpdate(false);
  }

  // Master or original author can edit/delete a note. Clients edit their own,
  // advisors edit their own, master edits any. author_id is the source of truth.
  function canManageNote(n){
    return n.author_id === user.id || user.role === "master";
  }
  function startEditNote(n, prefix=""){
    const draft = prefix && n.text?.startsWith(prefix) ? n.text.slice(prefix.length) : n.text;
    setEditingNote({id:n.id, draft, prefix});
  }
  function cancelEditNote(){ setEditingNote(null); }
  async function saveEditNote(){
    if(!editingNote) return;
    const trimmed=(editingNote.draft||"").trim();
    if(!trimmed) return;
    const fullText = (editingNote.prefix||"") + trimmed;
    setSavingNote(true);
    try {
      await updateNote(editingNote.id, fullText);
      setNotes(p=>p.map(n=>n.id===editingNote.id?{...n,text:fullText}:n));
      setEditingNote(null);
    } catch(e) { alert("Edit failed: "+e.message); }
    setSavingNote(false);
  }
  async function confirmDeleteNote(noteId){
    try {
      await deleteNote(noteId);
      setNotes(p=>p.filter(n=>n.id!==noteId));
      setDeleteConfirmNote(null);
      if(editingNote?.id===noteId) setEditingNote(null);
    } catch(e) { alert("Delete failed: "+e.message); }
  }
  function renderNoteRow(n, isFirst, isLast, prefix=""){
    const isEditingThis = editingNote?.id === n.id;
    const isConfirmingDelete = deleteConfirmNote === n.id;
    const canManage = canManageNote(n);
    const authorName = n.author?.name || (n.author_role==="advisor"?"Advisor":(client.user?.name||"Client"));
    const displayText = prefix && n.text?.startsWith(prefix) ? n.text.slice(prefix.length) : n.text;
    return (
      <div key={n.id}
        onMouseEnter={e=>{ if(!isEditingThis && !isConfirmingDelete) e.currentTarget.style.background = C.bgAlt; }}
        onMouseLeave={e=>{ e.currentTarget.style.background = "transparent"; }}
        style={{
          padding:"14px 18px",
          borderTop: isFirst ? `1px solid ${C.border}` : "none",
          borderBottom: isLast ? "none" : `1px solid ${C.border}`,
          transition:"background .15s",
        }}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <span style={{fontFamily:F.body,fontSize:12,color:C.charcoal,fontWeight:600}}>{authorName}</span>
          <span style={{fontFamily:F.body,fontSize:10,color:C.charLight,marginLeft:"auto"}}>{fmtDate(n.created_at)}</span>
        </div>
        {isConfirmingDelete ? (
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{fontFamily:F.body,fontSize:13,color:C.red,fontWeight:600}}>Delete this note? This can't be undone.</span>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>confirmDeleteNote(n.id)} style={{background:C.red,color:"#fff",border:"none",borderRadius:5,padding:"4px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Delete</button>
              <button onClick={()=>setDeleteConfirmNote(null)} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        ) : isEditingThis ? (
          <div>
            <textarea value={editingNote.draft} onChange={e=>setEditingNote({...editingNote,draft:e.target.value})} rows={3} autoFocus
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"7px 10px",color:C.charcoal,fontFamily:F.body,fontSize:11,resize:"vertical",boxSizing:"border-box"}}/>
            <div style={{display:"flex",gap:6,marginTop:6}}>
              <button onClick={saveEditNote} disabled={!editingNote.draft.trim()||savingNote}
                style={{background:editingNote.draft.trim()?C.navy:C.bgAlt,color:editingNote.draft.trim()?"#fff":C.charLight,border:"none",borderRadius:5,padding:"4px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:editingNote.draft.trim()?"pointer":"default"}}>Save</button>
              <button onClick={cancelEditNote}
                style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14}}>
            <div style={{flex:1,fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{displayText}</div>
            {canManage&&(
              <div style={{display:"flex",gap:5,flexShrink:0}}>
                <button onClick={()=>startEditNote(n, prefix)}
                  style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Edit</button>
                <button onClick={()=>setDeleteConfirmNote(n.id)}
                  style={{background:"transparent",color:C.charLight,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Delete</button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Posted updates are notes with the "📌 Update: " prefix. Same row UI as
  // regular notes; the prefix is hidden in display + edit, then re-applied on save.
  function renderUpdateRow(n, isFirst, isLast){
    return renderNoteRow(n, isFirst, isLast, "📌 Update: ");
  }

  async function uploadProjectFile(){
    projFileInput.type="file";
    projFileInput.onchange=async(e)=>{
      const file=e.target.files[0];
      if(!file) return;
      setUploadingFile(true);
      const path=`${client.id}/projects/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const {data}=await supabase.storage.from('client-files').upload(path,file);
      const f=await addFile(client.id,{
        name:file.name,
        category:"Projects",
        uploaded_by_role:isAdvisor?"advisor":"client",
        uploaded_by_id:user.id,
        storage_path:data?.path||null,
        size_label:`${(file.size/1024).toFixed(0)} KB`,
      });
      setProjectFiles(p=>[f,...p]);
      setUploadingFile(false);
    };
    projFileInput.click();
  }

  async function openFile(f){
    if(!f.storage_path) return;
    const w=window.open('','_blank');
    const {data}=await supabase.storage.from('client-files').createSignedUrl(f.storage_path,60);
    if(data?.signedUrl){ if(w) w.location=data.signedUrl; else window.open(data.signedUrl,'_blank'); }
    else if(w) w.close();
  }

  const updates=notes.filter(n=>n.text?.startsWith("📌 Update:"));
  const advisorNotes=notes.filter(n=>n.author_role==="advisor"&&!n.text?.startsWith("📌 Update:"));
  const clientNotes=notes.filter(n=>n.author_role==="client"&&!n.text?.startsWith("📌 Update:"));
  const allNotes=[...notes].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));

  const STATUS_OPTIONS=["active","complete","paused","cancelled"];
  const PHASE_OPTIONS=["Clarity","Insight","Alignment","Execution","Momentum"];
  const RISK_OPTIONS=["low","medium","high"];

  // Publish a full-bleed sticky header into AppShell's <main> slot.
  // Deps cover every value the bar's JSX (and the handlers it captures) reads,
  // so closures always reflect current state. handleBack closes over (hasChanges, onBack);
  // saveFields closes over (project.id, editFields, onUpdate).
  useEffect(()=>{
    setHeader(
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",height:52,position:"sticky",top:0,zIndex:9,boxShadow:"0 1px 4px rgba(30,58,95,.06)"}}>
        <div style={{display:"flex",alignItems:"center",gap:0,height:"100%"}}>
          <button onClick={handleBack} style={{background:"none",border:"none",borderRight:`1px solid ${C.border}`,color:C.navyMid,fontFamily:F.body,fontSize:isMobile?12:13,cursor:"pointer",padding:isMobile?"0 14px":"0 20px",height:"100%",display:"flex",alignItems:"center",gap:6,fontWeight:500}}>
            ← Projects
          </button>
          <div style={{padding:isMobile?"0 12px":"0 18px",fontFamily:F.body,fontSize:isMobile?13:13,color:C.charcoal,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:isMobile?180:400}}>{editFields.name||project.name}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,padding:isMobile?"0 12px":"0 20px"}}>
          {hasChanges&&!isMobile&&<span style={{fontFamily:F.body,fontSize:11,color:C.amber,fontWeight:600}}>● Unsaved</span>}
          {isAdvisor&&<Btn variant="primary" small onClick={saveFields} loading={savingFields} disabled={!hasChanges}>Save</Btn>}
        </div>
      </div>
    );
    return ()=>setHeader(null);
  },[editFields, editFields.name, project.name, project.id, hasChanges, savingFields, isMobile, isAdvisor]);

  return (
    <div style={{minHeight:"100vh",background:C.bg}}>

      <div style={{maxWidth:isMobile?600:960,margin:"0 auto",padding:isMobile?"14px 14px 100px":"24px 26px 48px"}}>

        {/* Project meta — lines-not-boxes */}
        <div style={{padding:"6px 0 18px",borderBottom:`1px solid ${C.border}`,marginBottom:18}}>
          {/* Title — display, 26px */}
          {isAdvisor?(
            <input value={editFields.name} onChange={e=>handleFieldChange("name",e.target.value)} style={{width:"100%",background:"transparent",border:"none",borderBottom:`1px solid ${hasChanges?C.amber:"transparent"}`,padding:"2px 0",color:C.charcoal,fontFamily:F.display,fontSize:isMobile?42:26,fontWeight:600,boxSizing:"border-box",outline:"none",marginBottom:6}}/>
          ):(
            <div style={{fontFamily:F.display,fontSize:isMobile?42:26,color:C.charcoal,fontWeight:600,marginBottom:6}}>{project.name}</div>
          )}
          {/* Description — view/edit pattern */}
          {editingDescription&&isAdvisor?(
            <div style={{marginBottom:12}}>
              <textarea value={editFields.description} onChange={e=>handleFieldChange("description",e.target.value)} rows={2} autoFocus placeholder="Project description…"
                style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",color:C.charMid,fontFamily:F.body,fontSize:11,lineHeight:1.6,resize:"none",boxSizing:"border-box"}}/>
              <div style={{display:"flex",gap:6,marginTop:6}}>
                <button onClick={saveDescription} disabled={savingFields}
                  style={{background:C.navy,color:"#fff",border:"none",borderRadius:5,padding:"4px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:savingFields?"default":"pointer",opacity:savingFields?.6:1}}>Save</button>
                <button onClick={cancelEditDescription}
                  style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
              </div>
            </div>
          ):project.description?(
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:14,marginBottom:12}}>
              <div style={{flex:1,fontFamily:F.body,fontSize:13,color:C.charMid,lineHeight:1.6}}>{project.description}</div>
              {isAdvisor&&(
                <button onClick={()=>setEditingDescription(true)}
                  style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"3px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer",flexShrink:0}}>Edit</button>
              )}
            </div>
          ):isAdvisor?(
            <button onClick={()=>setEditingDescription(true)}
              style={{background:"none",border:"none",color:C.charLight,fontFamily:F.body,fontSize:12,cursor:"pointer",padding:0,fontStyle:"italic",display:"block",marginBottom:12}}>+ Add description</button>
          ):null}
          {/* Status / Phase / Risk pills */}
          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
            {isAdvisor?(
              <>
                <DropBtn value={project.status||"active"} onChange={v=>{onUpdate({...project,status:v});updateProject(project.id,{status:v});}}
                  options={STATUS_OPTIONS.map(o=>({value:o,label:o}))}
                  bg={C.navyFaint} color={C.navy} border={`1px solid ${C.navyBorder}`}/>
                <DropBtn value={project.phase||"Clarity"} onChange={v=>{onUpdate({...project,phase:v});updateProject(project.id,{phase:v});}}
                  options={PHASE_OPTIONS.map(o=>({value:o,label:o.toLowerCase()}))}
                  bg={C.amberBg} color={C.amber} border={`1px solid ${C.amberBd}`}/>
                <DropBtn value={project.risk||"medium"} onChange={v=>{onUpdate({...project,risk:v});updateProject(project.id,{risk:v});}}
                  options={RISK_OPTIONS.map(o=>({value:o,label:o+" risk"}))}
                  bg={project.risk==="high"?C.redBg:project.risk==="low"?C.greenBg:C.amberBg}
                  color={project.risk==="high"?C.red:project.risk==="low"?C.green:C.amber}
                  border={`1px solid ${project.risk==="high"?C.redBd:project.risk==="low"?C.greenBd:C.amberBd}`}/>
              </>
            ):(
              <div style={{display:"flex",gap:6}}>
                <Badge label={project.status||"active"} type={project.status||"active"}/>
                <Badge label={(project.phase||"clarity").toLowerCase()} type={project.phase||"Clarity"}/>
                <Badge label={(project.risk||"medium")+" risk"} type={project.risk||"medium"}/>
              </div>
            )}
          </div>
        </div>

        {/* CHECKLIST — main section */}
        <div style={{marginBottom:18}}>
          <ProjectChecklist project={project} client={client} isAdvisor={isAdvisor} user={user} onProgressUpdate={pct=>onUpdate({checklist_progress:pct})}/>
        </div>

        {/* Post an update — lines-not-boxes */}
        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:18,marginBottom:18}}>
          <div style={{fontFamily:F.mono,fontSize:11,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".08em",marginBottom:10,padding:"0 18px"}}>Post an Update</div>
          <div style={{display:"flex",gap:8,padding:"0 18px"}}>
            <input value={newUpdate} onChange={e=>setNewUpdate(e.target.value)} onKeyDown={e=>e.key==="Enter"&&postUpdate()} placeholder="Share a quick update on this project…" style={{flex:1,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:11,boxSizing:"border-box"}}/>
            <Btn variant="primary" small onClick={postUpdate} loading={savingUpdate} disabled={!newUpdate.trim()}>Post</Btn>
          </div>
          {updates.length>0&&(
            <div style={{marginTop:14}}>
              {(showAllUpdates
                ? updates.slice(updatesPage*UPDATES_PER_PAGE, (updatesPage+1)*UPDATES_PER_PAGE)
                : updates.slice(0,UPDATES_INITIAL)
              ).map((n,i,arr)=>renderUpdateRow(n, i===0, i===arr.length-1))}
              {/* Pagination controls */}
              {!showAllUpdates&&updates.length>UPDATES_INITIAL&&(
                <button onClick={()=>setShowAllUpdates(true)} style={{marginTop:10,background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 14px",fontFamily:F.body,fontSize:11,color:C.navyMid,fontWeight:600,cursor:"pointer",width:"100%"}}>
                  See More ({updates.length - UPDATES_INITIAL} more)
                </button>
              )}
              {showAllUpdates&&updates.length>UPDATES_PER_PAGE&&(
                <div style={{display:"flex",gap:8,justifyContent:"center",marginTop:10,alignItems:"center"}}>
                  <button onClick={()=>setUpdatesPage(p=>Math.max(0,p-1))} disabled={updatesPage===0} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 12px",fontFamily:F.body,fontSize:11,cursor:"pointer",opacity:updatesPage===0?.4:1}}>← Prev</button>
                  <span style={{fontFamily:F.mono,fontSize:10,color:C.charMid}}>Page {updatesPage+1} of {Math.ceil(updates.length/UPDATES_PER_PAGE)}</span>
                  <button onClick={()=>setUpdatesPage(p=>Math.min(Math.ceil(updates.length/UPDATES_PER_PAGE)-1,p+1))} disabled={(updatesPage+1)*UPDATES_PER_PAGE>=updates.length} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 12px",fontFamily:F.body,fontSize:11,cursor:"pointer",opacity:(updatesPage+1)*UPDATES_PER_PAGE>=updates.length?.4:1}}>Next →</button>
                </div>
              )}
              {showAllUpdates&&<button onClick={()=>{setShowAllUpdates(false);setUpdatesPage(0);}} style={{marginTop:8,background:"none",border:"none",fontFamily:F.body,fontSize:11,color:C.charLight,cursor:"pointer",width:"100%"}}>Show less</button>}
            </div>
          )}
        </div>

        {/* Section tabs */}
        <div style={{display:"flex",gap:2,marginBottom:14,borderBottom:`1px solid ${C.border}`}}>
          {(user.role==="team_member"?[["files","Files"]]:[["notes","Notes"],["files","Files"]]).map(([id,label])=>(
            <button key={id} onClick={()=>setActiveSection(id)} style={{background:"none",border:"none",borderBottom:activeSection===id?`2px solid ${C.navy}`:"2px solid transparent",padding:"10px 18px 8px",cursor:"pointer",fontFamily:F.body,fontSize:12,color:activeSection===id?C.navy:C.charMid,fontWeight:activeSection===id?600:400,marginBottom:-1}}>
              {label}
            </button>
          ))}
        </div>

        {/* Notes section */}
        {activeSection==="notes"&&(
          loading?<Spinner/>:(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
              {/* Client notes — LEFT (TEAL dot) */}
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,padding:"0 18px"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:C.teal,flexShrink:0}}/>
                  <h3 style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600,margin:0}}>{isAdvisor?`${client.user?.name?.split(" ")[0]||"Client"}'s Notes`:"Your Notes"}</h3>
                </div>
                {clientNotes.length===0&&<div style={{fontFamily:F.body,fontSize:12,color:C.charLight,fontStyle:"italic",padding:"14px 18px"}}>No client notes yet.</div>}
                {clientNotes.map((n,i)=>renderNoteRow(n, i===0, i===clientNotes.length-1))}
                {!isAdvisor&&(
                  <div style={{padding:"14px 18px",borderTop:clientNotes.length>0?`1px solid ${C.border}`:"none"}}>
                    <textarea value={clientNote} onChange={e=>setCNote(e.target.value)} rows={3} placeholder="Add your thoughts on this project…" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",color:C.charcoal,fontFamily:F.body,fontSize:11,resize:"vertical",boxSizing:"border-box",marginBottom:7}}/>
                    <Btn variant="outline" small onClick={()=>saveNote("client")} loading={saving} disabled={!clientNote.trim()}>Add Note</Btn>
                  </div>
                )}
              </div>
              {/* Advisor notes — RIGHT (NAVY dot) */}
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,padding:"0 18px"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:C.navy,flexShrink:0}}/>
                  <h3 style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600,margin:0}}>Advisor Notes</h3>
                </div>
                {advisorNotes.length===0&&<div style={{fontFamily:F.body,fontSize:12,color:C.charLight,fontStyle:"italic",padding:"14px 18px"}}>No advisor notes yet.</div>}
                {advisorNotes.map((n,i)=>renderNoteRow(n, i===0, i===advisorNotes.length-1))}
                {isAdvisor&&(
                  <div style={{padding:"14px 18px",borderTop:advisorNotes.length>0?`1px solid ${C.border}`:"none"}}>
                    <textarea value={brianNote} onChange={e=>setBNote(e.target.value)} rows={3} placeholder="Add advisor note…" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",color:C.charcoal,fontFamily:F.body,fontSize:11,resize:"vertical",boxSizing:"border-box",marginBottom:7}}/>
                    <Btn variant="outline" small onClick={()=>saveNote("advisor")} loading={saving} disabled={!brianNote.trim()}>Add Note</Btn>
                  </div>
                )}
              </div>
            </div>
          )
        )}

        {/* Files section */}
        {activeSection==="files"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontFamily:F.body,fontSize:13,color:C.charMid}}>Files attached to this project</div>
              <button onClick={uploadProjectFile} disabled={uploadingFile} style={{background:C.navy,color:"#fff",border:"none",borderRadius:7,padding:"6px 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer",opacity:uploadingFile?.6:1}}>
                {uploadingFile?"Uploading…":"+ Upload File"}
              </button>
            </div>
            {/* Drag and drop zone */}
            <div
              onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor=C.navy;e.currentTarget.style.background=C.navyFaint;}}
              onDragLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.bgAlt;}}
              onDrop={async e=>{
                e.preventDefault();
                e.currentTarget.style.borderColor=C.border;
                e.currentTarget.style.background=C.bgAlt;
                const file=e.dataTransfer.files[0];
                if(!file) return;
                setUploadingFile(true);
                const path=`${client.id}/projects/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
                const {data}=await supabase.storage.from('client-files').upload(path,file);
                const f=await addFile(client.id,{name:file.name,category:"Projects",uploaded_by_role:isAdvisor?"advisor":"client",uploaded_by_id:user.id,storage_path:data?.path||null,size_label:`${(file.size/1024).toFixed(0)} KB`});
                setProjectFiles(p=>[f,...p]);
                setUploadingFile(false);
              }}
              onClick={uploadProjectFile}
              style={{border:`2px dashed ${C.border}`,borderRadius:8,padding:"20px",textAlign:"center",cursor:"pointer",background:C.bgAlt,marginBottom:12,transition:"all .2s"}}>
              <div style={{fontSize:24,marginBottom:6}}>📁</div>
              <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500,marginBottom:3}}>{uploadingFile?"Uploading…":"Drag & drop a file here"}</div>
              <div style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>or click to browse your computer</div>
            </div>
            {projectFiles.length===0?(
              <Card style={{padding:32,textAlign:"center"}}>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>No files attached yet. Upload files related to this project.</div>
              </Card>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {projectFiles.map(f=>(
                  <Card key={f.id} style={{padding:"12px 16px",display:"flex",alignItems:"center",gap:12}}>
                    <span style={{fontSize:20}}>📄</span>
                    <div style={{flex:1}}>
                      <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500}}>{f.name}</div>
                      <div style={{fontFamily:F.body,fontSize:10,color:C.charLight}}>{fmtDate(f.created_at)}{f.size_label?` · ${f.size_label}`:""}</div>
                    </div>
                    {f.storage_path&&<button onClick={()=>openFile(f)} style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"4px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Open</button>}
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Category Picker (used in upload + edit forms) ─────────────
function CategoryPicker({value, onChange, customCategories, addCustomCategory}){
  const [adding,setAdding]=useState(false);
  const [newName,setNewName]=useState("");
  const all=[...FILE_CATEGORIES,...customCategories];
  const fieldStyle={background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"};

  function handleSelectChange(e){
    const v=e.target.value;
    if(v==="__add_new__"){setAdding(true);setNewName("");}
    else onChange(v);
  }
  function commitNew(){
    const trimmed=newName.trim();
    if(trimmed&&!all.includes(trimmed)) addCustomCategory(trimmed);
    if(trimmed) onChange(trimmed);
    setAdding(false);setNewName("");
  }
  function cancel(){setAdding(false);setNewName("");}

  if(adding){
    return (
      <div style={{display:"flex",gap:6,alignItems:"center"}}>
        <input autoFocus value={newName} onChange={e=>setNewName(e.target.value)}
          onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();commitNew();}}}
          placeholder="New category name" style={{...fieldStyle,flex:1}}/>
        <button type="button" onClick={commitNew} style={{background:C.navy,color:"#fff",border:"none",borderRadius:6,padding:"8px 12px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>Add</button>
        <button type="button" onClick={cancel} aria-label="Cancel" style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px",fontFamily:F.body,fontSize:14,lineHeight:1,cursor:"pointer"}}>×</button>
      </div>
    );
  }

  return (
    <select value={value} onChange={handleSelectChange} style={{...fieldStyle,width:"100%"}}>
      <option value="">Select a category…</option>
      {all.map(c=><option key={c} value={c}>{c}</option>)}
      <option value="__add_new__">+ Add new category…</option>
    </select>
  );
}

// ── FILES TAB ─────────────────────────────────────────────────
function FilesTab({client, isAdvisor, user, files, setFiles, advisorId, onUpgradeRequest, aiAnalystState, setAiAnalystState, onTimelineUpdate, onProjectsUpdate}) {
  const isMobile=useIsMobile();
  const [stage,setStage]=useState("idle");
  const [newFile,setNewFile]=useState({name:"",category:""});
  const [selectedFile,setSelectedFile]=useState(null);
  const [aiResult,setAiResult]=useState(null);
  const [selItems,setSelItems]=useState([]);
  const [filterCat,setFilterCat]=useState("All");
  const [saving,setSaving]=useState(false);
  const [dragOver,setDragOver]=useState(false);
  const [customCategories,setCustomCategories]=useState([]);
  const [expandedId,setExpandedId]=useState(null);
  const [editingId,setEditingId]=useState(null);
  const [editFields,setEditFields]=useState({name:"",category:""});
  const [deleteConfirm,setDeleteConfirm]=useState(null);
  const fileInputRef=useRef(null);

  function addCustomCategory(name){
    setCustomCategories(p=>p.includes(name)?p:[...p,name]);
  }

  const allCats=[...FILE_CATEGORIES,...customCategories];
  const categories=["All",...allCats];
  const filtered=filterCat==="All"?files:files.filter(f=>f.category===filterCat);
  const catIcon={Agreements:"📄","Session Details":"📝","Client Docs":"📁","Advisor Docs":"💼",Operations:"⚙️",Sales:"💰","Owner Updates":"📣",Other:"📎"};

  function handleFileSelect(file){
    if(!file) return;
    setSelectedFile(file);
    // Auto-fill name from file name if empty
    if(!newFile.name) setNewFile(p=>({...p,name:file.name}));
  }

  function handleDrop(e){
    e.preventDefault();setDragOver(false);
    const file=e.dataTransfer.files[0];
    if(file) handleFileSelect(file);
  }

  async function uploadToStorage(file){
    const path=`${client.id}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
    const {data,error}=await supabase.storage.from('client-files').upload(path,file);
    if(error) throw error;
    return {path:data.path, size:`${(file.size/1024).toFixed(0)} KB`};
  }

  async function saveFileOnly(){
    setSaving(true);
    try{
      let storagePath=null, sizeLabel="—";
      if(selectedFile){
        const result=await uploadToStorage(selectedFile);
        storagePath=result.path; sizeLabel=result.size;
      }
      const f=await addFile(client.id,{
        name:newFile.name||selectedFile?.name||"Untitled",
        category:newFile.category,
        uploaded_by_role:isAdvisor?"advisor":"client",
        uploaded_by_id:user.id,
        storage_path:storagePath,
        size_label:sizeLabel,
      });
      setFiles(p=>[f,...p]);
    }catch(e){console.error("Upload failed:",e);}
    setStage("idle");setNewFile({name:"",category:""});setSelectedFile(null);setSaving(false);
  }

  async function interpretFile(){
    if(advisorId){
      const u=await getUsageRecord(advisorId);
      if((u?.ai_files||0)>=USAGE_LIMITS.ai_files.free){onUpgradeRequest&&onUpgradeRequest("ai_files");return;}
      await incrementUsage(advisorId,"ai_files");
    }
    setStage("interpreting");
    const sys=`You are a BB Integrated business consultant AI. A client has uploaded a business document called "${newFile.name||selectedFile?.name}". Return ONLY valid JSON with "summary" (2-3 sentences), "keyFindings" (array of 3-5 strings), "suggestedItems" (array of up to 6 objects with "text" and "type" of "project" or "task"). No markdown.`;
    const raw=await callClaude(sys,`Document: ${newFile.name||selectedFile?.name}\nCategory: ${newFile.category}\nClient: ${client.user?.name||'client'}\nCompany: ${client.company||"their business"}`);
    try{setAiResult(JSON.parse(raw.replace(/```json|```/g,"").trim()));}
    catch{setAiResult({summary:`This document appears to be a ${newFile.category} file for ${client.company||"your business"}.`,keyFindings:["Document analyzed","Content organized","Action items identified","Ready for review"],suggestedItems:[{text:`Review and action ${newFile.name||selectedFile?.name}`,type:"project"}]});}
    setStage("review");
  }

  async function finishWithAI(){
    setSaving(true);
    try{
      let storagePath=null, sizeLabel="—";
      if(selectedFile){
        const result=await uploadToStorage(selectedFile);
        storagePath=result.path; sizeLabel=result.size;
      }
      const f=await addFile(client.id,{
        name:newFile.name||selectedFile?.name||"Untitled",
        category:newFile.category,
        uploaded_by_role:isAdvisor?"advisor":"client",
        uploaded_by_id:user.id,
        storage_path:storagePath,
        size_label:sizeLabel,
        ai_summary:aiResult?.summary||null,
      });
      setFiles(p=>[f,...p]);
    }catch(e){console.error("Upload failed:",e);}
    setStage("idle");setNewFile({name:"",category:""});setSelectedFile(null);setAiResult(null);setSelItems([]);setSaving(false);
  }

  async function downloadFile(file){
    if(!file.storage_path) return;
    const w=window.open('','_blank');
    const {data}=await supabase.storage.from('client-files').createSignedUrl(file.storage_path,60);
    if(data?.signedUrl){ if(w) w.location=data.signedUrl; else window.open(data.signedUrl,'_blank'); }
    else if(w) w.close();
  }

  return (
    <div>
      <SectionHead title="Files" sub="Shared documents, uploads, and session materials."
        action={stage==="idle"&&<Btn variant="primary" small onClick={()=>setStage("upload")}>+ Upload File</Btn>}/>

      {/* Upload form */}
      {stage==="upload"&&(
        <Card style={{padding:"22px 24px",marginBottom:16}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,marginBottom:18}}>Upload a File</div>

          {/* Drag & drop zone */}
          <div
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onDrop={handleDrop}
            onClick={()=>fileInputRef.current?.click()}
            style={{border:`2px dashed ${dragOver?C.navy:C.border}`,borderRadius:10,padding:"32px 20px",textAlign:"center",cursor:"pointer",background:dragOver?C.navyFaint:C.bgAlt,transition:"all .2s",marginBottom:16}}
          >
            <input ref={fileInputRef} type="file" style={{display:"none"}} onChange={e=>handleFileSelect(e.target.files[0])}/>
            {selectedFile?(
              <div>
                <div style={{fontSize:32,marginBottom:8}}>📄</div>
                <div style={{fontFamily:F.body,fontSize:14,color:C.navy,fontWeight:600,marginBottom:4}}>{selectedFile.name}</div>
                <div style={{fontFamily:F.mono,fontSize:11,color:C.charLight}}>{(selectedFile.size/1024).toFixed(0)} KB · Click to change</div>
              </div>
            ):(
              <div>
                <div style={{fontSize:32,marginBottom:8}}>📁</div>
                <div style={{fontFamily:F.body,fontSize:14,color:C.charcoal,fontWeight:600,marginBottom:4}}>Drag & drop your file here</div>
                <div style={{fontFamily:F.body,fontSize:12,color:C.charLight}}>or click to browse your computer</div>
              </div>
            )}
          </div>

          {/* Name & category */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div>
              <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Display Name</label>
              <input value={newFile.name} onChange={e=>setNewFile(p=>({...p,name:e.target.value}))} placeholder={selectedFile?.name||"Document name"} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Category</label>
              <CategoryPicker value={newFile.category} onChange={v=>setNewFile(p=>({...p,category:v}))} customCategories={customCategories} addCustomCategory={addCustomCategory}/>
            </div>
          </div>

          {/* Action buttons */}
          {selectedFile&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14,opacity:newFile.category?1:.5,pointerEvents:newFile.category?"auto":"none"}}>
              <div onClick={saveFileOnly} style={{border:`2px solid ${C.border}`,borderRadius:10,padding:"14px 16px",cursor:"pointer",transition:"all .2s",background:C.surface,textAlign:"center"}}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=C.navyMid;e.currentTarget.style.background=C.navyFaint;}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor=C.border;e.currentTarget.style.background=C.surface;}}>
                <div style={{fontSize:22,marginBottom:6}}>📁</div>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600,marginBottom:2}}>Upload as-is</div>
                <div style={{fontFamily:F.body,fontSize:11,color:C.charMid}}>Store for easy access and sharing</div>
              </div>
              <div onClick={interpretFile} style={{border:`2px solid ${C.navyBorder}`,borderRadius:10,padding:"14px 16px",cursor:"pointer",transition:"all .2s",background:C.navyFaint,textAlign:"center"}}>
                <div style={{fontSize:22,marginBottom:6}}>◈</div>
                <div style={{fontFamily:F.body,fontSize:13,color:C.navy,fontWeight:600,marginBottom:2}}>Let AI interpret it</div>
                <div style={{fontFamily:F.body,fontSize:11,color:C.navyMid}}>Get summary and action items</div>
              </div>
            </div>
          )}

          {!selectedFile&&(
            <div style={{display:"flex",gap:8}}>
              <Btn variant="primary" small onClick={saveFileOnly} disabled={!newFile.name.trim()||!newFile.category} loading={saving}>Save Without File</Btn>
              <Btn variant="ghost" small onClick={()=>{setStage("idle");setNewFile({name:"",category:""});setSelectedFile(null);}}>Cancel</Btn>
            </div>
          )}
          {selectedFile&&(
            <Btn variant="ghost" small onClick={()=>{setStage("idle");setNewFile({name:"",category:""});setSelectedFile(null);}}>Cancel</Btn>
          )}
        </Card>
      )}

      {stage==="interpreting"&&(
        <Card style={{padding:"36px 24px",marginBottom:16,textAlign:"center"}}>
          <div style={{fontFamily:F.mono,fontSize:28,color:C.navy,marginBottom:14,animation:"pulse 1.5s infinite"}}>◈</div>
          <div style={{fontFamily:F.display,fontSize:18,color:C.charcoal,marginBottom:6}}>AI is reading your file…</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charMid}}>Analyzing and identifying key insights.</div>
        </Card>
      )}

      {stage==="review"&&aiResult&&(
        <Card style={{padding:"22px 24px",marginBottom:16}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,marginBottom:16}}>AI Interpretation — {newFile.name||selectedFile?.name}</div>
          <div style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"13px 15px",marginBottom:14}}>
            <div style={{fontFamily:F.mono,fontSize:8,color:C.navyMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:5}}>Summary</div>
            <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.7}}>{aiResult.summary}</div>
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontFamily:F.body,fontSize:10,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Key Findings</div>
            {aiResult.keyFindings?.map((f,i)=>(
              <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"5px 0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{width:5,height:5,borderRadius:"50%",background:C.navy,marginTop:6,flexShrink:0}}/>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.5}}>{f}</div>
              </div>
            ))}
          </div>
          {aiResult.suggestedItems?.length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{fontFamily:F.body,fontSize:10,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Suggested Items — Select to Add</div>
              {aiResult.suggestedItems.map((item,i)=>{
                const on=selItems.includes(i);
                return (
                  <div key={i} onClick={()=>setSelItems(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i])} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
                    <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${on?C.navy:C.border}`,background:on?C.navyFaint:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {on&&<span style={{color:C.navy,fontSize:11}}>✓</span>}
                    </div>
                    <div style={{flex:1,fontFamily:F.body,fontSize:13,color:C.charcoal}}>{item.text}</div>
                    <Badge label={item.type} type={item.type==="project"?"active":"pending"}/>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            <Btn variant="primary" onClick={finishWithAI} loading={saving}>Save File{selItems.length>0?` + ${selItems.length} Item${selItems.length>1?"s":""}`:""} →</Btn>
            <Btn variant="ghost" small onClick={saveFileOnly}>Save File Only</Btn>
            <Btn variant="ghost" small onClick={()=>setStage("upload")}>← Back</Btn>
          </div>
        </Card>
      )}

      {stage==="review"&&aiResult&&(
        <Card style={{padding:"22px 24px",marginBottom:16}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,marginBottom:16}}>AI Interpretation — {newFile.name}</div>
          <div style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"13px 15px",marginBottom:14}}>
            <div style={{fontFamily:F.mono,fontSize:8,color:C.navyMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:5}}>Summary</div>
            <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.7}}>{aiResult.summary}</div>
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontFamily:F.body,fontSize:10,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Key Findings</div>
            {aiResult.keyFindings?.map((f,i)=>(
              <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"5px 0",borderBottom:`1px solid ${C.border}`}}>
                <div style={{width:5,height:5,borderRadius:"50%",background:C.navy,marginTop:6,flexShrink:0}}/>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.5}}>{f}</div>
              </div>
            ))}
          </div>
          {aiResult.suggestedItems?.length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{fontFamily:F.body,fontSize:10,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Suggested Items — Select to Add</div>
              {aiResult.suggestedItems.map((item,i)=>{
                const on=selItems.includes(i);
                return (
                  <div key={i} onClick={()=>setSelItems(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i])} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
                    <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${on?C.navy:C.border}`,background:on?C.navyFaint:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {on&&<span style={{color:C.navy,fontSize:11}}>✓</span>}
                    </div>
                    <div style={{flex:1,fontFamily:F.body,fontSize:13,color:C.charcoal}}>{item.text}</div>
                    <Badge label={item.type} type={item.type==="project"?"active":"pending"}/>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            <Btn variant="primary" onClick={finishWithAI} loading={saving}>Save File{selItems.length>0?` + ${selItems.length} Item${selItems.length>1?"s":""}`:""} →</Btn>
            <Btn variant="ghost" small onClick={saveFileOnly}>Save File Only</Btn>
            <Btn variant="ghost" small onClick={()=>setStage("choice")}>← Back</Btn>
          </div>
        </Card>
      )}

      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:16}}>
        {categories.map(c=>(
          <button key={c} onClick={()=>setFilterCat(c)} style={{background:filterCat===c?C.navy:C.surface,color:filterCat===c?"#fff":C.charMid,border:`1px solid ${filterCat===c?C.navy:C.border}`,borderRadius:20,padding:"5px 13px",fontFamily:F.body,fontSize:11,cursor:"pointer",fontWeight:filterCat===c?600:400}}>
            {c}
          </button>
        ))}
      </div>
      {filtered.length===0?(
        <div style={{borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,padding:"22px 18px",textAlign:"center"}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charMid,marginBottom:6}}>No files yet</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>Upload files to share with {isAdvisor?client.user?.name?.split(" ")[0]||"client":"your advisor"}.</div>
        </div>
      ):(
        <div>
          {filtered.map((f,i)=>{
            const isFirst=i===0;
            const isLast=i===filtered.length-1;
            const isExpanded=expandedId===f.id;
            const isEditing=editingId===f.id;
            const fileName=f.storage_path?f.storage_path.split('/').pop().replace(/^\d+_/,''):null;
            const uploadedByLabel=f.uploaded_by_role==="advisor"?"by Advisor":"by Client";
            const rowBaseStyle={
              padding:"22px 18px",
              borderTop:isFirst?`1px solid ${C.border}`:"none",
              borderBottom:isLast?"none":`1px solid ${C.border}`,
              transition:"background .15s",
            };
            const openFile=async()=>{
              if(!f.storage_path) return;
              const w=window.open('','_blank');
              const {data}=await supabase.storage.from('client-files').createSignedUrl(f.storage_path,300);
              if(!data?.signedUrl){ if(w) w.close(); return; }
              const isInlineRenderable=/\.(pdf|png|jpe?g|gif|webp|svg)$/i.test(fileName||'');
              const isOffice=/\.(xlsx?|docx?|pptx?)$/i.test(fileName||'');
              const encoded=encodeURIComponent(data.signedUrl);
              const previewUrl=isInlineRenderable?data.signedUrl
                :isOffice?`https://view.officeapps.live.com/op/view.aspx?src=${encoded}`
                :`https://docs.google.com/viewer?url=${encoded}&embedded=true`;
              if(w) w.location=previewUrl; else window.open(previewUrl,'_blank');
            };
            const downloadFileLocal=async()=>{
              if(!f.storage_path) return;
              const {data}=await supabase.storage.from('client-files').createSignedUrl(f.storage_path,300);
              if(!data?.signedUrl) return;
              const a=document.createElement('a');
              a.href=data.signedUrl;
              a.download=fileName||'file';
              document.body.appendChild(a);
              a.click();
              a.remove();
            };
            const startEdit=()=>{setEditingId(f.id);setEditFields({name:f.name||"",category:f.category||""});};
            const saveEdit=async()=>{
              try{
                const u=await updateFile(f.id,{name:editFields.name.trim(),category:editFields.category});
                setFiles(p=>p.map(x=>x.id===u.id?u:x));
                setEditingId(null);
                setEditFields({name:"",category:""});
              }catch(e){console.error("Update failed:",e);}
            };
            const confirmDelete=async()=>{
              try{
                await deleteFile(f.id,f.storage_path);
                setFiles(p=>p.filter(x=>x.id!==f.id));
                setDeleteConfirm(null);
                setExpandedId(null);
              }catch(e){console.error("Delete failed:",e);}
            };
            return (
              <div key={f.id} style={{...rowBaseStyle,display:"flex",flexDirection:"column"}}>
                <div onClick={()=>setExpandedId(isExpanded?null:f.id)}
                  onMouseEnter={e=>{if(!isExpanded)e.currentTarget.style.background=C.bgAlt;}}
                  onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                  style={{display:"flex",alignItems:"flex-start",gap:14,cursor:"pointer"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:14,flexShrink:0}}>{catIcon[f.category]||"📎"}</span>
                      <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",minWidth:0,flex:1}}>{f.name}</div>
                      {f.ai_summary&&<span style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:3,padding:"1px 6px",fontFamily:F.mono,fontSize:8,fontWeight:700,textTransform:"uppercase",flexShrink:0}}>AI Read</span>}
                    </div>
                    <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                      <Badge label={f.category||"Uncategorized"} type="draft"/>
                      <span style={{fontFamily:F.body,fontSize:10,color:C.charLight}}>{fmtDate(f.created_at)}</span>
                      <span style={{fontFamily:F.body,fontSize:10,color:C.charLight}}>{uploadedByLabel}</span>
                      {f.size_label&&f.size_label!=="—"&&<span style={{fontFamily:F.mono,fontSize:10,color:C.charLight}}>{f.size_label}</span>}
                    </div>
                  </div>
                  <span style={{fontFamily:F.mono,fontSize:11,color:C.charLight,flexShrink:0,marginTop:2}}>{isExpanded?"▲":"▼"}</span>
                </div>
                {f.ai_summary&&!isExpanded&&(
                  <div style={{fontFamily:F.body,fontSize:11,color:C.navyMid,marginTop:8,lineHeight:1.5,fontStyle:"italic"}}>{f.ai_summary}</div>
                )}
                {isExpanded&&(
                  <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                    {deleteConfirm===f.id?(
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                        <div>
                          <div style={{fontFamily:F.body,fontSize:13,color:C.red,fontWeight:600,marginBottom:2}}>Delete this file?</div>
                          <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>This cannot be undone.</div>
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={confirmDelete} style={{background:C.red,color:"#fff",border:"none",borderRadius:7,padding:"6px 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>Delete</button>
                          <button onClick={()=>setDeleteConfirm(null)} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:7,padding:"6px 12px",fontFamily:F.body,fontSize:12,cursor:"pointer"}}>Cancel</button>
                        </div>
                      </div>
                    ):isEditing?(
                      <div>
                        <div style={{marginBottom:12}}>
                          <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Name</label>
                          <input value={editFields.name} onChange={e=>setEditFields(p=>({...p,name:e.target.value}))} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                        </div>
                        <div style={{marginBottom:12}}>
                          <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Category</label>
                          <CategoryPicker value={editFields.category} onChange={v=>setEditFields(p=>({...p,category:v}))} customCategories={customCategories} addCustomCategory={addCustomCategory}/>
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <Btn variant="primary" small onClick={saveEdit} disabled={!editFields.name.trim()||!editFields.category}>Save</Btn>
                          <Btn variant="ghost" small onClick={()=>{setEditingId(null);setEditFields({name:"",category:""});}}>Cancel</Btn>
                        </div>
                      </div>
                    ):(
                      <>
                        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                          {f.storage_path&&<button onClick={openFile} style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Preview</button>}
                          {f.storage_path&&<button onClick={downloadFileLocal} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Download</button>}
                          <button onClick={startEdit} style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Edit</button>
                          {isAdvisor&&<button onClick={()=>setDeleteConfirm(f.id)} style={{background:C.redBg,color:C.red,border:`1px solid ${C.redBd}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Delete</button>}
                        </div>
                        {f.ai_summary&&(
                          <div style={{fontFamily:F.body,fontSize:12,color:C.charcoal,marginTop:14,lineHeight:1.7,fontStyle:"italic"}}>{f.ai_summary}</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── AI Document Analysis ── */}
      <div style={{marginTop:28}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <div style={{flex:1,height:1,background:C.border}}/>
          <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".1em",whiteSpace:"nowrap"}}>AI Document Analysis</div>
          <div style={{flex:1,height:1,background:C.border}}/>
        </div>
        <AIDocAnalysis client={client} isAdvisor={isAdvisor} advisorId={advisorId} userRole={user.role} persistedState={aiAnalystState} onStateChange={setAiAnalystState} onTimelineUpdate={onTimelineUpdate} onProjectsUpdate={onProjectsUpdate}/>
      </div>
    </div>
  );
}

// ── Microphone Button ─────────────────────────────────────────
function MicButton({onTranscript, full}) {
  const [listening,setListening]=useState(false);
  const [supported]=useState(()=>!!(window.SpeechRecognition||window.webkitSpeechRecognition));
  const recRef=useRef(null);

  function toggle(){
    if(!supported) return alert("Speech recognition not supported in this browser. Try Chrome.");
    if(listening){
      recRef.current?.stop();
      setListening(false);
      return;
    }
    const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
    const rec=new SR();
    rec.continuous=false;
    rec.interimResults=false;
    rec.lang='en-US';
    rec.onresult=e=>{
      const transcript=Array.from(e.results).map(r=>r[0].transcript).join(' ');
      onTranscript(transcript);
    };
    rec.onend=()=>setListening(false);
    rec.onerror=()=>setListening(false);
    rec.start();
    recRef.current=rec;
    setListening(true);
  }

  if(!supported) return null;

  return (
    <button onClick={toggle} title={listening?"Stop listening":"Click to speak"}
      style={{
        background:listening?"#C0392B":C.bgAlt,
        color:listening?"#fff":C.charMid,
        border:`1px solid ${listening?"#C0392B":C.border}`,
        borderRadius:8,
        padding:full?"9px 0":"10px 13px",
        width:full?"100%":undefined,
        fontFamily:F.body,fontSize:12,fontWeight:600,
        cursor:"pointer",
        display:"flex",alignItems:"center",justifyContent:"center",gap:6,
        transition:"all .2s",
        animation:listening?"pulse 1s infinite":undefined,
        flexShrink:0,
      }}>
      <span style={{fontSize:15}}>{listening?"🔴":"🎤"}</span>
      {full&&<span>{listening?"Listening… (click to stop)":"Speak"}</span>}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.6}}`}</style>
    </button>
  );
}

// ── AI Document Analysis Component ────────────────────────────
function AIDocAnalysis({client, isAdvisor, advisorId, userRole, persistedState, onStateChange, onTimelineUpdate, onProjectsUpdate}) {
  const [docFile,setDocFileLocal]=useState(persistedState?.docFile||null);
  const [docText,setDocTextLocal]=useState(persistedState?.docText||"");
  const [loading,setLoading]=useState(false);
  const [messages,setMessagesLocal]=useState(persistedState?.messages||[]);
  const [input,setInputLocal]=useState(persistedState?.input||"");
  const [sending,setSending]=useState(false);
  const [dragOver,setDragOver]=useState(false);
  const [usageCount,setUsageCountLocal]=useState(persistedState?.usageCount||0);
  const [usageLoaded,setUsageLoaded]=useState(false);
  const fileRef=useRef(null);
  const chatRef=useRef(null);

  // Wrap setters to also persist to parent
  function setDocFile(v){setDocFileLocal(v);onStateChange&&onStateChange(p=>({...p,docFile:v}));}
  function setDocText(v){setDocTextLocal(v);onStateChange&&onStateChange(p=>({...p,docText:v}));}
  function setMessages(fn){setMessagesLocal(prev=>{const next=typeof fn==="function"?fn(prev):fn;onStateChange&&onStateChange(p=>({...p,messages:next}));return next;});}
  function setInput(v){setInputLocal(v);onStateChange&&onStateChange(p=>({...p,input:v}));}
  function setUsageCount(fn){setUsageCountLocal(prev=>{const next=typeof fn==="function"?fn(prev):fn;onStateChange&&onStateChange(p=>({...p,usageCount:next}));return next;});}

  // Master has no limits
  const isMaster=userRole==="master";
  const limit=isMaster?999:isAdvisor?USAGE_LIMITS.ai_doc_analysis.free_advisor:USAGE_LIMITS.ai_doc_analysis.free_client;
  const trackingId=advisorId||client.id; // use advisorId for advisors, client.id for clients

  useEffect(()=>{
    // Load current month usage
    async function loadUsage(){
      try{
        const currentMonth=new Date().toISOString().slice(0,7); // YYYY-MM
        const {data}=await supabase.from('usage_tracking').select('ai_doc_analysis,ai_doc_analysis_month').eq('user_id',trackingId).single();
        if(data){
          // Reset if different month
          if(data.ai_doc_analysis_month!==currentMonth){
            await supabase.from('usage_tracking').update({ai_doc_analysis:0,ai_doc_analysis_month:currentMonth}).eq('user_id',trackingId);
            setUsageCount(0);
          } else {
            setUsageCount(data.ai_doc_analysis||0);
          }
        }
      }catch(e){}
      setUsageLoaded(true);
    }
    loadUsage();
  },[trackingId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function incrementDocUsage(){
    const currentMonth=new Date().toISOString().slice(0,7);
    await supabase.from('usage_tracking').upsert({user_id:trackingId,ai_doc_analysis:(usageCount+1),ai_doc_analysis_month:currentMonth},{onConflict:'user_id'});
    setUsageCount(p=>p+1);
  }

  async function createLimitReachedNote(){
    // Auto-create a thought from client to advisor
    try{
      await supabase.from('checklist_items').insert({
        client_id:client.id,
        text:JSON.stringify({
          title:"AI Analysis Access",
          text:"I've reached my monthly AI document analysis limit and would love more access. Could you help? 🙏",
          authorRole:"client",
          priority:"later",
          submitted:true,
        }),
        auto:false,
        custom:true,
        done:false,
        sort_order:Math.floor(Date.now()/1000),
      });
    }catch(e){console.error("Failed to create limit note:",e);}
  }

  const QUICK_ACTIONS=[
    {id:"summarize",label:"📋 Summarize",prompt:"Please provide a clear executive summary of this document in 3-5 bullet points."},
    {id:"projects",label:"◈ Build Projects",prompt:"Extract all action items and deliverables from this document and suggest 1 concrete project I could create in the portal. Give the project a name and description, then list detailed checklist tasks inside that project — each task with a clear name and one-sentence description of what needs to be done."},
    {id:"checklist",label:"✓ Create Checklist",prompt:"Pull out all tasks, action items, and to-dos from this document and format them as a numbered checklist."},
    {id:"timeline",label:"📅 Build Timeline",prompt:`Extract all dates, sessions, milestones, and events from this document. Format your response EXACTLY like this for each item, with no exceptions:

DATE: YYYY-MM-DD
TITLE: Short title here
DESC: One sentence description here

---

Repeat that exact format for every entry. Use YYYY-MM-DD date format. Do not add any other text, headers, or formatting outside of this structure.`},
    {id:"numbers",label:"$ Key Numbers",prompt:"Extract and summarize all important numbers, financial figures, dates, deadlines, and metrics from this document."},
    {id:"gaps",label:"◉ Identify Gaps",prompt:`Review this document for ${client.user?.name||"this client"}'s business and identify any gaps, risks, or areas needing attention as their advisor.`},
    {id:"notes",label:"📝 Advisor Notes",prompt:"Transform the key insights from this document into structured advisor notes I could add to this client's portal."},
  ];

  async function readFile(file){
    const ext=file.name.split('.').pop().toLowerCase();
    const needsServerParse=['docx','doc','xlsx','xls'].includes(ext);

    if(needsServerParse){
      // Send to server-side parser
      return new Promise((resolve,reject)=>{
        const reader=new FileReader();
        reader.onload=async e=>{
          try{
            const response=await fetch('/api/parse-doc',{
              method:'POST',
              headers:{'Content-Type':'application/json'},
              body:JSON.stringify({
                fileData:e.target.result,
                fileName:file.name,
                fileType:file.type,
              }),
            });
            const data=await response.json();
            if(data.error) throw new Error(data.error);
            resolve(data.text||'');
          }catch(err){
            reject(err);
          }
        };
        reader.readAsDataURL(file);
      });
    }

    // For text-based files read directly
    return new Promise((resolve)=>{
      const reader=new FileReader();
      reader.onload=e=>resolve(e.target.result||'');
      reader.readAsText(file);
    });
  }

  async function handleFileLoad(file){
    if(!file) return;
    // Check limit
    if(!isMaster&&usageCount>=limit){
      if(!isAdvisor) await createLimitReachedNote();
      return;
    }
    setDocFile(file);
    setLoading(true);
    setMessages([]);
    try{
      const content=await readFile(file);
      const truncated=typeof content==="string"?content.slice(0,8000):content.slice(0,2000);
      setDocText(truncated);
      await incrementDocUsage();
      const sys=`You are an expert business advisor AI assistant working with BB Integrated consultancy. You are analyzing a document for client ${client.user?.name||"the client"} at ${client.company||"their company"}.

Provide:
1. A brief 2-3 sentence overview of what the document is about
2. 2-3 key observations or highlights  
3. Ask what they'd like to explore or do with it

Be professional, specific, and conversational.`;
      const prompt=`I've uploaded "${file.name}" (${(file.size/1024).toFixed(0)} KB).\n\nContent:\n${truncated}\n\nPlease give me your initial analysis.`;
      const response=await callClaude(sys,prompt,1500);
      setMessages([{role:"assistant",text:response,ts:new Date()}]);
    }catch(e){
      setMessages([{role:"assistant",text:`I've loaded "${file.name}". What would you like to know or do with it?`,ts:new Date()}]);
    }
    setLoading(false);
  }

  const [pendingAction,setPendingAction]=useState(null); // {type, items, executing, done}

  // Parse AI response for actionable items based on last quick action
  function parseActionItems(text, actionId){
    const lines=text.split('\n').filter(l=>l.trim());
    if(actionId==='timeline'){
      const items=[];
      const lines=text.split('\n').filter(l=>l.trim());

      for(const line of lines){
        // Handle **YYYY-MM-DD** | Title | Desc format (with or without ? in date)
        const pipeMatch=line.match(/\*{0,2}(\d{4}-\d{2}-[\d?]+)\*{0,2}\s*\|\s*([^|]+)\|?\s*(.*)/);
        if(pipeMatch){
          const rawDate=pipeMatch[1].replace(/\?/g,'01'); // replace ? with 01
          const title=pipeMatch[2].trim().slice(0,80);
          const desc=pipeMatch[3].trim();
          items.push({date:rawDate,title,description:desc,selected:true});
          continue;
        }
        // Handle DATE: YYYY-MM-DD / TITLE: / DESC: structured format
        const dateLineMatch=line.match(/^DATE:\s*(\d{4}-\d{2}-[\d?]+)/i);
        if(dateLineMatch){
          // Look ahead in lines for TITLE and DESC
          const idx=lines.indexOf(line);
          const titleLine=lines[idx+1]?.match(/^TITLE:\s*(.+)/i);
          const descLine=lines[idx+2]?.match(/^DESC:\s*(.+)/i);
          items.push({
            date:dateLineMatch[1].replace(/\?/g,'01'),
            title:(titleLine?titleLine[1]:'Session').trim().slice(0,80),
            description:(descLine?descLine[1]:'').trim(),
            selected:true,
          });
          continue;
        }
        // Handle numbered list with date: "1. 2026-02-12 Title here"
        const numberedMatch=line.match(/^\d+\.\s+\*{0,2}(\d{4}-\d{2}-[\d?]+)\*{0,2}\s*[|\-–]?\s*(.+)/);
        if(numberedMatch){
          const rawDate=numberedMatch[1].replace(/\?/g,'01');
          const rest=numberedMatch[2].split('|');
          items.push({
            date:rawDate,
            title:rest[0].trim().slice(0,80),
            description:rest[1]?.trim()||'',
            selected:true,
          });
        }
      }

      // Deduplicate by title
      const seen=new Set();
      const deduped=items.filter(item=>{
        if(seen.has(item.title)) return false;
        seen.add(item.title);
        return true;
      });

      return {type:'timeline',items:deduped.slice(0,25)};
    }
    if(actionId==='projects'){
      // Parse project name/description + all tasks underneath
      const lines=text.split('\n').filter(l=>l.trim());
      let projectName='';
      let projectDesc='';
      const tasks=[];

      for(let i=0;i<lines.length;i++){
        const clean=lines[i].replace(/^#+\s*/,'').replace(/\*\*/g,'').trim();
        // Project name line
        if(clean.match(/^(Project\s*Name|Project)[:\s]+/i)){
          projectName=clean.replace(/^(Project\s*Name|Project)[:\s]+/i,'').trim();
          continue;
        }
        // Description line
        if(clean.match(/^(Description|Overview)[:\s]+/i)){
          projectDesc=clean.replace(/^(Description|Overview)[:\s]+/i,'').trim();
          continue;
        }
        // If no project name yet, first bold or header line is probably the name
        if(!projectName&&(clean.match(/^[A-Z]/)&&clean.length<80&&!clean.match(/^[\d\-\*\•]/))){
          projectName=clean;
          continue;
        }
        // Task lines — numbered or bulleted
        const taskMatch=clean.match(/^[\d\-\*\•✓☐□]+[\.\):]?\s+(.+)/);
        if(taskMatch){
          const taskText=taskMatch[1].trim();
          // Check if next line is a description (starts with lowercase or dash)
          const nextLine=lines[i+1]?.replace(/^[\s\-]+/,'').trim()||'';
          const taskDesc=nextLine&&!nextLine.match(/^[\d\-\*\•✓☐□]/)?nextLine:'';
          tasks.push({text:taskText,description:taskDesc,selected:true,priority:'medium',owner_role:'advisor'});
          if(taskDesc) i++; // skip desc line
        }
      }

      // Fallback: if no project name found, use first non-empty line
      if(!projectName) projectName=lines.find(l=>l.trim().length>3)?.replace(/^[\d\.\-\*\•#\*]+\s*/,'').trim()||'New Project';

      return {
        type:'projects',
        items:[{name:projectName,description:projectDesc,selected:true,tasks}]
      };
    }
    if(actionId==='checklist'){
      const items=lines
        .map(l=>l.replace(/^[\d\.\-\*\•☐□]+\s*/,'').trim())
        .filter(l=>l.length>3)
        .map(l=>({text:l,selected:true}));
      return {type:'checklist',items:items.slice(0,30)};
    }
    return null;
  }

  async function executeAction(action){
    setPendingAction(p=>({...p,executing:true}));
    const selectedItems=action.items.filter(i=>i.selected);
    try{
      if(action.type==='timeline'){
        for(const item of selectedItems){
          const {error}=await supabase.from('timeline').insert({
            client_id:client.id,
            title:item.title||'Session',
            date:item.date||todayLocal(),
            type:'milestone',
            body:item.description||'',
          });
          if(error) console.error('Timeline insert error:',error);
        }
      } else if(action.type==='projects'){
        for(const item of selectedItems){
          // Create the project
          const {data:proj,error:projErr}=await supabase.from('projects').insert({
            client_id:client.id,
            name:item.name,
            description:item.description||'',
            status:'active',
            phase:'Clarity',
            risk:'medium',
            progress:0,
            deadline:'TBD',
            created_at:new Date().toISOString(),
          }).select().single();
          if(projErr){console.error('Project insert error:',projErr);continue;}
          // Create checklist tasks under the project
          if(item.tasks&&item.tasks.length>0){
            for(const task of item.tasks){
              await supabase.from('project_checklist_items').insert({
                project_id:proj.id,
                client_id:client.id,
                text:task.text+(task.description?` — ${task.description}`:''),
                done:false,
                priority:task.priority||'medium',
                owner_role:task.owner_role||'advisor',
                sort_order:Math.floor(Date.now()/1000),
              });
            }
          }
        }
      } else if(action.type==='checklist'){
        // Add as thoughts/notes for now
        for(const item of selectedItems){
          await supabase.from('checklist_items').insert({
            client_id:client.id,
            text:JSON.stringify({title:'AI Checklist Item',text:item.text,authorRole:isAdvisor?'advisor':'client',priority:'later',submitted:false}),
            auto:false,custom:true,done:false,
            sort_order:Math.floor(Date.now()/1000),
          });
        }
      }
      setPendingAction(p=>({...p,executing:false,done:true}));
      // Refresh portal data
      if(action.type==='timeline') onTimelineUpdate&&onTimelineUpdate();
      if(action.type==='projects') onProjectsUpdate&&onProjectsUpdate();
    }catch(e){
      console.error('Execute error:',e);
      setPendingAction(p=>({...p,executing:false,error:e.message}));
    }
  }

  // Track which action id was last used
  const [lastActionId,setLastActionId]=useState(null);

  // Auto-detect actionable content in any AI response
  function autoDetectAction(text, hintActionId){
    // If we already know the action type, use it
    if(hintActionId&&['timeline','projects','checklist'].includes(hintActionId)){
      const parsed=parseActionItems(text,hintActionId);
      if(parsed&&parsed.items.length>0) return parsed;
    }
    // Always try to detect dates → timeline
    const dateMatches=text.match(/\d{4}-\d{2}-[\d?]+/g);
    if(dateMatches&&dateMatches.length>=2){
      const parsed=parseActionItems(text,'timeline');
      if(parsed&&parsed.items.length>=2) return parsed;
    }
    // Detect checklist patterns — numbered or bulleted task lists
    const taskLines=text.split('\n').filter(l=>l.match(/^[\d\-\*\•✓☐□]+[\.\)]\s+.{10,}/));
    if(taskLines.length>=3){
      const parsed=parseActionItems(text,'checklist');
      if(parsed&&parsed.items.length>=3) return parsed;
    }
    // Detect project patterns
    if(text.match(/project[:\s]+\w/i)||text.match(/Project Name/i)){
      const parsed=parseActionItems(text,'projects');
      if(parsed&&parsed.items.length>0) return parsed;
    }
    return null;
  }

  async function sendMessageWithAction(text, actionId=null){
    if(!text.trim()||sending) return;
    if(actionId) setLastActionId(actionId);
    setMessages(p=>[...p,{role:"user",text,ts:new Date()}]);
    setInput("");
    setSending(true);
    try{
      const sys=docText
        ?`You are an expert business advisor AI working with BB Integrated. You are discussing "${docFile?.name||"a document"}" for client ${client.user?.name||"the client"} at ${client.company||"their company"}.

Document content: ${docText.slice(0,4000)}

Continue the conversation helpfully. Be specific and actionable. When listing dates for timelines, always use YYYY-MM-DD format. When listing projects, use "Project: [name]" then "Description: [desc]" format. When listing tasks or checklist items, use a numbered or bulleted list.`
        :`You are an expert business advisor AI working with BB Integrated consultancy. You are helping the advisor with client ${client.user?.name||"the client"} at ${client.company||"their company"}.

You have access to this client portal and can help create projects, timeline entries, checklists, and notes. Be specific, actionable, and professional. When listing dates for timelines, always use YYYY-MM-DD format. When listing projects, use "Project: [name]" then "Description: [desc]" format. When listing tasks, use a numbered list.`;
      const history=messages.map(m=>`${m.role==="user"?"User":"Assistant"}: ${m.text}`).join("\n\n");
      const response=await callClaude(sys,`${history}\n\nUser: ${text}`,2000);
      const newMsg={role:"assistant",text:response,ts:new Date(),actionId};
      setMessages(p=>[...p,newMsg]);
      // Always try to detect actionable content
      const detected=autoDetectAction(response,actionId||lastActionId);
      if(detected&&detected.items.length>0) setPendingAction({...detected,executing:false,done:false});
    }catch(e){
      setMessages(p=>[...p,{role:"assistant",text:"Sorry, I had trouble with that. Please try again.",ts:new Date()}]);
    }
    setSending(false);
    setTimeout(()=>chatRef.current?.scrollTo({top:99999,behavior:"smooth"}),100);
  }

  // Simple sendMessage for free-form chat
  async function sendMessage(text){ await sendMessageWithAction(text,null); }

  const atLimit=!isMaster&&usageCount>=limit;
  const pct=isMaster?0:Math.min(100,Math.round((usageCount/limit)*100));
  const usageColor=pct>=80?C.red:pct>=60?C.amber:C.green;

  return (
    <Card style={{padding:"20px 22px"}}>
      {/* Header with usage bar */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
        <div>
          <div style={{fontFamily:F.display,fontSize:15,color:C.charcoal}}>◈ AI Document Analyst</div>
          <div style={{fontFamily:F.body,fontSize:12,color:C.charLight,marginTop:2}}>Upload any document and AI will extract insights, suggest projects, and more.</div>
        </div>
        {!isMaster&&usageLoaded&&(
          <div style={{textAlign:"right",flexShrink:0,marginLeft:16}}>
            <div style={{fontFamily:F.mono,fontSize:10,color:usageColor,fontWeight:600,marginBottom:4}}>{usageCount}/{limit} used this month</div>
            <div style={{width:120,height:4,background:C.bgAlt,borderRadius:2,overflow:"hidden"}}>
              <div style={{width:`${pct}%`,height:"100%",background:usageColor,borderRadius:2,transition:"width .3s"}}/>
            </div>
            {!isAdvisor&&<div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,marginTop:3}}>resets monthly</div>}
          </div>
        )}
      </div>

      {/* Limit reached state */}
      {atLimit?(
        <div style={{background:isAdvisor?C.amberBg:C.navyFaint,border:`1px solid ${isAdvisor?C.amberBd:C.navyBorder}`,borderRadius:10,padding:"20px 20px",marginTop:14,textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:10}}>{isAdvisor?"⚡":"🙏"}</div>
          {isAdvisor?(
            <>
              <div style={{fontFamily:F.body,fontSize:14,color:C.amber,fontWeight:600,marginBottom:6}}>Monthly limit reached</div>
              <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,marginBottom:14,lineHeight:1.6}}>You've used your {limit} monthly AI document analyses. Upgrade for unlimited access to the AI Document Analyst.</div>
              <button onClick={()=>alert("Upgrade coming soon! Contact brian@bbintegrated.com to discuss unlimited access.")} style={{background:C.amber,color:"#fff",border:"none",borderRadius:8,padding:"10px 24px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>Upgrade for $4.97/mo →</button>
            </>
          ):(
            <>
              <div style={{fontFamily:F.body,fontSize:14,color:C.navy,fontWeight:600,marginBottom:6}}>Monthly limit reached</div>
              <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,marginBottom:6,lineHeight:1.6}}>You've used your {limit} monthly AI document analyses. We've sent a note to your advisor — they can run additional analyses on your behalf.</div>
              <div style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>Your limit resets at the start of next month.</div>
            </>
          )}
        </div>
      ):(
        <>
          {/* Initial entry — full-width document drop zone */}
          {!docFile&&messages.length===0?(
            <div style={{marginTop:14}}>
              <div
                onDragOver={e=>{e.preventDefault();setDragOver(true);}}
                onDragLeave={()=>setDragOver(false)}
                onDrop={e=>{e.preventDefault();setDragOver(false);const f=e.dataTransfer.files[0];if(f)handleFileLoad(f);}}
                onClick={()=>fileRef.current?.click()}
                style={{border:`2px dashed ${dragOver?C.navy:C.border}`,borderRadius:12,padding:"36px 16px",textAlign:"center",cursor:"pointer",background:dragOver?C.navyFaint:C.bgAlt,transition:"all .2s"}}>
                <input ref={fileRef} type="file" style={{display:"none"}} accept=".txt,.csv,.pdf,.doc,.docx,.xls,.xlsx,.md" onChange={e=>handleFileLoad(e.target.files[0])}/>
                <div style={{fontSize:32,marginBottom:10}}>📄</div>
                <div style={{fontFamily:F.body,fontSize:14,color:C.charcoal,fontWeight:600,marginBottom:4}}>Drop a document</div>
                <div style={{fontFamily:F.body,fontSize:12,color:C.charLight,marginBottom:4}}>or click to browse</div>
                <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight}}>Word · Excel · PDF · CSV · Text</div>
              </div>
            </div>
          ):!docFile?(
            // Chat-only mode (no document loaded, but conversation started)
            <div style={{marginTop:14}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,background:C.bgAlt,borderRadius:8,padding:"8px 12px"}}>
                <span style={{fontSize:16}}>💬</span>
                <div style={{flex:1,fontFamily:F.body,fontSize:12,color:C.charMid}}>Chat mode — no document loaded</div>
                <button onClick={()=>{setMessages([]);setInput("");setPendingAction(null);}} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"3px 9px",fontFamily:F.body,fontSize:11,color:C.charLight,cursor:"pointer"}}>Clear ✕</button>
                <button onClick={()=>fileRef.current?.click()} style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"3px 9px",fontFamily:F.body,fontSize:11,color:C.navy,cursor:"pointer"}}>+ Add Doc</button>
                <input ref={fileRef} type="file" style={{display:"none"}} accept=".txt,.csv,.pdf,.doc,.docx,.xls,.xlsx,.md" onChange={e=>handleFileLoad(e.target.files[0])}/>
              </div>
              {/* Chat messages */}
              <div ref={chatRef} style={{maxHeight:380,overflowY:"auto",marginBottom:10,display:"flex",flexDirection:"column",gap:10,padding:"4px 0"}}>
                {messages.map((m,i)=>(
                  <div key={i} style={{display:"flex",flexDirection:"column",alignItems:m.role==="user"?"flex-end":"flex-start"}}>
                    <div style={{maxWidth:"85%",background:m.role==="user"?C.navy:C.surface,color:m.role==="user"?"#fff":C.charcoal,border:m.role==="user"?"none":`1px solid ${C.border}`,borderRadius:m.role==="user"?"12px 12px 2px 12px":"12px 12px 12px 2px",padding:"11px 15px",fontFamily:F.body,fontSize:13,lineHeight:1.7,whiteSpace:"pre-wrap"}}>
                      {m.text}
                    </div>
                    <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,marginTop:2,padding:"0 4px"}}>
                      {m.role==="user"?"You":"AI Analyst"} · {m.ts?.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}
                    </div>
                  </div>
                ))}
                {sending&&<div style={{display:"flex"}}><div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px 12px 12px 2px",padding:"11px 15px",fontFamily:F.body,fontSize:13,color:C.charMid,fontStyle:"italic"}}>◈ Thinking…</div></div>}
              </div>
              <div style={{display:"flex",gap:8}}>
                <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessageWithAction(input,lastActionId)}
                  placeholder="Type a question, paste text, or describe what you need…"
                  style={{flex:1,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",fontFamily:F.body,fontSize:13,color:C.charcoal,boxSizing:"border-box"}}/>
                <MicButton onTranscript={t=>setInput(p=>p?p+" "+t:t)}/>
                <button onClick={()=>sendMessageWithAction(input,lastActionId)} disabled={!input.trim()||sending}
                  style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:!input.trim()||sending?"default":"pointer",opacity:!input.trim()||sending?.6:1}}>Send</button>
              </div>
            </div>
          ):(
            <div style={{marginTop:14}}>
              {/* File header */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,background:C.navyFaint,borderRadius:8,padding:"10px 14px"}}>
                <span style={{fontSize:18}}>📄</span>
                <div style={{flex:1}}>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.navy,fontWeight:600}}>{docFile.name}</div>
                  <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight}}>{(docFile.size/1024).toFixed(0)} KB · Loaded for analysis</div>
                </div>
                <button onClick={()=>{setDocFile(null);setDocText("");setMessages([]);setInput("");}} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:11,color:C.charLight,cursor:"pointer"}}>Clear ✕</button>
              </div>

              {/* Quick actions */}
              {messages.length>0&&!loading&&(
                <div style={{marginBottom:12}}>
                  <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em",marginBottom:7}}>Quick Actions</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {QUICK_ACTIONS.map(a=>(
                      <button key={a.id} onClick={()=>{setInput(a.prompt);setLastActionId(a.id);}} disabled={sending}
                        style={{background:C.surface,color:C.charcoal,border:`1px solid ${C.border}`,borderRadius:7,padding:"5px 11px",fontFamily:F.body,fontSize:11,fontWeight:500,cursor:"pointer",opacity:sending?.6:1}}
                        onMouseEnter={e=>{e.currentTarget.style.background=C.navyFaint;e.currentTarget.style.borderColor=C.navyBorder;e.currentTarget.style.color=C.navy;}}
                        onMouseLeave={e=>{e.currentTarget.style.background=C.surface;e.currentTarget.style.borderColor=C.border;e.currentTarget.style.color=C.charcoal;}}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Chat */}
              {loading?(
                <div style={{textAlign:"center",padding:"28px 0"}}>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.navyMid,marginBottom:4}}>◈ Reading document…</div>
                  <div style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>Analyzing content and preparing initial review</div>
                </div>
              ):(
                <>
                  <div ref={chatRef} style={{maxHeight:380,overflowY:"auto",marginBottom:10,display:"flex",flexDirection:"column",gap:10,padding:"4px 0"}}>
                    {messages.map((m,i)=>(
                      <div key={i} style={{display:"flex",flexDirection:"column",alignItems:m.role==="user"?"flex-end":"flex-start"}}>
                        <div style={{maxWidth:"85%",background:m.role==="user"?C.navy:C.surface,color:m.role==="user"?"#fff":C.charcoal,border:m.role==="user"?"none":`1px solid ${C.border}`,borderRadius:m.role==="user"?"12px 12px 2px 12px":"12px 12px 12px 2px",padding:"11px 15px",fontFamily:F.body,fontSize:13,lineHeight:1.7,whiteSpace:"pre-wrap"}}>
                          {m.text}
                        </div>
                        <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,marginTop:2,padding:"0 4px"}}>
                          {m.role==="user"?"You":"AI Analyst"} · {m.ts?.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}
                        </div>
                      </div>
                    ))}
                    {sending&&(
                      <div style={{display:"flex",alignItems:"flex-start"}}>
                        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:"12px 12px 12px 2px",padding:"11px 15px",fontFamily:F.body,fontSize:13,color:C.charMid,fontStyle:"italic"}}>◈ Thinking…</div>
                      </div>
                    )}
                  </div>
                  <div style={{display:"flex",gap:8}}>
                    <input value={input} onChange={e=>{setInput(e.target.value);if(!QUICK_ACTIONS.find(a=>a.prompt===e.target.value))setLastActionId(null);}} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&sendMessageWithAction(input,lastActionId)}
                      placeholder="Click a quick action above or type your own question…"
                      style={{flex:1,background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",fontFamily:F.body,fontSize:13,color:C.charcoal,boxSizing:"border-box"}}/>
                    <MicButton onTranscript={t=>setInput(p=>p?p+" "+t:t)}/>
                    <button onClick={()=>sendMessageWithAction(input,lastActionId)} disabled={!input.trim()||sending}
                      style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"10px 18px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:!input.trim()||sending?"default":"pointer",opacity:!input.trim()||sending?.6:1}}>
                      Send
                    </button>
                  </div>
                  <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginTop:6}}>Click a quick action to pre-fill · Edit if needed · Press Send to run</div>

                  {/* ── Review & Execute Modal ── */}
                  {pendingAction&&!pendingAction.done&&(
                    <div style={{marginTop:16,background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:12,padding:"16px 18px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                        <div>
                          <div style={{fontFamily:F.display,fontSize:14,color:C.navy,fontWeight:600}}>
                            {pendingAction.type==="timeline"?"📅 Add to Timeline":pendingAction.type==="projects"?"◈ Add to Projects":"✓ Add to Portal"}
                          </div>
                          <div style={{fontFamily:F.body,fontSize:11,color:C.navyMid,marginTop:2}}>
                            Review each item below. Uncheck any you don't want to add, then confirm.
                          </div>
                        </div>
                        <button onClick={()=>setPendingAction(null)} style={{background:"none",border:"none",color:C.charLight,cursor:"pointer",fontSize:16}}>✕</button>
                      </div>

                      <div style={{maxHeight:280,overflowY:"auto",marginBottom:12}}>
                        {pendingAction.items.map((item,i)=>(
                          <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"8px 10px",background:item.selected?C.surface:"transparent",borderRadius:7,marginBottom:4,border:`1px solid ${item.selected?C.border:"transparent"}`}}>
                            <div onClick={()=>setPendingAction(p=>({...p,items:p.items.map((it,j)=>j===i?{...it,selected:!it.selected}:it)}))}
                              style={{width:16,height:16,borderRadius:3,border:`2px solid ${item.selected?C.navy:C.border}`,background:item.selected?C.navy:"transparent",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,marginTop:2}}>
                              {item.selected&&<span style={{color:"#fff",fontSize:9}}>✓</span>}
                            </div>
                            <div style={{flex:1}}>
                              {pendingAction.type==="timeline"&&(
                                <>
                                  <div style={{fontFamily:F.mono,fontSize:10,color:C.navyMid,marginBottom:2}}>{item.date}</div>
                                  <div style={{fontFamily:F.body,fontSize:12,color:C.charcoal,fontWeight:500}}>{item.title}</div>
                                  {item.description&&<div style={{fontFamily:F.body,fontSize:11,color:C.charMid,marginTop:2}}>{item.description}</div>}
                                </>
                              )}
                              {pendingAction.type==="projects"&&(
                                <>
                                  <div style={{fontFamily:F.body,fontSize:12,color:C.charcoal,fontWeight:600}}>{item.name}</div>
                                  {item.description&&<div style={{fontFamily:F.body,fontSize:11,color:C.charMid,marginTop:2}}>{item.description}</div>}
                                  {item.tasks&&item.tasks.length>0&&(
                                    <div style={{marginTop:8,paddingLeft:8,borderLeft:`2px solid ${C.border}`}}>
                                      <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{item.tasks.length} checklist tasks</div>
                                      {item.tasks.map((task,ti)=>(
                                        <div key={ti} style={{fontFamily:F.body,fontSize:11,color:C.charMid,marginBottom:2}}>· {task.text}</div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              )}
                              {pendingAction.type==="checklist"&&(
                                <div style={{fontFamily:F.body,fontSize:12,color:C.charcoal}}>{item.text}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {pendingAction.error&&<div style={{fontFamily:F.body,fontSize:11,color:C.red,marginBottom:8}}>Error: {pendingAction.error}</div>}

                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>executeAction(pendingAction)} disabled={pendingAction.executing||pendingAction.items.filter(i=>i.selected).length===0}
                          style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"9px 20px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer",opacity:pendingAction.executing?.7:1}}>
                          {pendingAction.executing?"Adding…":`Confirm & Add ${pendingAction.items.filter(i=>i.selected).length} Item${pendingAction.items.filter(i=>i.selected).length!==1?"s":""} →`}
                        </button>
                        <button onClick={()=>setPendingAction(null)} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 16px",fontFamily:F.body,fontSize:12,color:C.charMid,cursor:"pointer"}}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Success state */}
                  {pendingAction?.done&&(
                    <div style={{marginTop:16,background:C.greenBg,border:`1px solid ${C.greenBd}`,borderRadius:10,padding:"12px 16px",display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:18}}>✅</span>
                      <div>
                        <div style={{fontFamily:F.body,fontSize:13,color:C.green,fontWeight:600}}>Added to portal successfully!</div>
                        <div style={{fontFamily:F.body,fontSize:11,color:C.green,marginTop:2}}>Navigate to the relevant tab to see the new entries.</div>
                      </div>
                      <button onClick={()=>setPendingAction(null)} style={{marginLeft:"auto",background:"none",border:"none",color:C.green,cursor:"pointer",fontSize:14}}>✕</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}


// ── DOCS TAB ──────────────────────────────────────────────────
function DocsTab({client, isAdvisor, user, documents, setDocuments, onTimelineUpdate}) {
  const [expandedId,setExpandedId]=useState(null);
  const [showCreate,setShowCreate]=useState(false);
  const [newDoc,setNewDoc]=useState({title:"",content:""});
  const [selectedFile,setSelectedFile]=useState(null);
  const [dragOver,setDragOver]=useState(false);
  const [sigName,setSigName]=useState("");
  const [saving,setSaving]=useState(false);
  const [editingId,setEditingId]=useState(null);
  const [editFields,setEditFields]=useState({title:"",content:"",status:"draft",newFile:null});
  const [deleteConfirm,setDeleteConfirm]=useState(null);
  const docFileRef=useRef(null);
  const editFileRef=useRef(null);
  const canManage=isAdvisor||user.role==="master";

  async function handleCreate(status){
    if(!newDoc.title||(!newDoc.content&&!selectedFile)) return;
    setSaving(true);
    let storagePath=null;
    if(selectedFile){
      const path=`documents/${client.id}/${Date.now()}_${selectedFile.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const {data,error}=await supabase.storage.from('client-files').upload(path,selectedFile);
      if(error){console.error("Upload failed:",error);setSaving(false);return;}
      storagePath=data.path;
    }
    const d=await createDocument(client.id,{title:newDoc.title,content:newDoc.content,storage_path:storagePath,status,sent_date:todayLocal()});
    setDocuments(p=>[d,...p]);
    onTimelineUpdate&&onTimelineUpdate();
    setNewDoc({title:"",content:""});setSelectedFile(null);setShowCreate(false);setSaving(false);
  }

  async function handleSign(docId){
    if(!sigName.trim()) return;
    setSaving(true);
    const d=await signDocument(docId,sigName);
    setDocuments(p=>p.map(doc=>doc.id===d.id?d:doc));
    onTimelineUpdate&&onTimelineUpdate();
    setSigName("");setSaving(false);
  }

  async function handleSaveEdit(d){
    setSaving(true);
    try{
      let storagePath=d.storage_path;
      if(editFields.newFile){
        if(d.storage_path) await supabase.storage.from('client-files').remove([d.storage_path]);
        const path=`documents/${client.id}/${Date.now()}_${editFields.newFile.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
        const {data,error}=await supabase.storage.from('client-files').upload(path,editFields.newFile);
        if(error){console.error("Upload failed:",error);setSaving(false);return;}
        storagePath=data.path;
      }
      const updated=await updateDocument(d.id,{title:editFields.title,content:editFields.content,status:editFields.status,storage_path:storagePath});
      setDocuments(p=>p.map(doc=>doc.id===updated.id?updated:doc));
      setEditingId(null);
      setEditFields({title:"",content:"",status:"draft",newFile:null});
    }catch(e){console.error("Update failed:",e);}
    setSaving(false);
  }

  async function handleDelete(d){
    setSaving(true);
    try{
      await deleteDocument(d.id,d.storage_path);
      setDocuments(p=>p.filter(doc=>doc.id!==d.id));
      setExpandedId(null);
      setDeleteConfirm(null);
    }catch(e){console.error("Delete failed:",e);}
    setSaving(false);
  }

  return (
    <div>
      <SectionHead title="Documents" sub="Agreements, proposals, and documents for signature."
        action={isAdvisor&&<Btn variant="primary" small onClick={()=>setShowCreate(p=>!p)}>+ New Document</Btn>}/>
      {showCreate&&isAdvisor&&(
        <div style={{marginBottom:16}}>
          <div style={{marginBottom:12}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Document Title</label>
            <input value={newDoc.title} onChange={e=>setNewDoc(p=>({...p,title:e.target.value}))} placeholder="e.g. Engagement Agreement" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Upload File</label>
            <div
              onDragOver={e=>{e.preventDefault();setDragOver(true);}}
              onDragLeave={()=>setDragOver(false)}
              onDrop={e=>{e.preventDefault();setDragOver(false);const f=e.dataTransfer.files[0];if(f)setSelectedFile(f);}}
              onClick={()=>docFileRef.current?.click()}
              style={{border:`2px dashed ${dragOver?C.navy:C.border}`,borderRadius:10,padding:"32px 20px",textAlign:"center",cursor:"pointer",background:dragOver?C.navyFaint:C.bgAlt,transition:"all .2s"}}>
              <input ref={docFileRef} type="file" accept=".pdf,.docx,.doc,.txt" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)setSelectedFile(f);}}/>
              {selectedFile?(
                <div>
                  <div style={{fontSize:32,marginBottom:8}}>📄</div>
                  <div style={{fontFamily:F.body,fontSize:14,color:C.navy,fontWeight:600,marginBottom:4}}>{selectedFile.name}</div>
                  <div style={{fontFamily:F.mono,fontSize:11,color:C.charLight}}>{(selectedFile.size/1024).toFixed(0)} KB · Click to change</div>
                  <button onClick={e=>{e.stopPropagation();setSelectedFile(null);}} style={{marginTop:8,background:"none",border:"none",color:C.charLight,fontFamily:F.body,fontSize:11,cursor:"pointer",textDecoration:"underline"}}>Remove</button>
                </div>
              ):(
                <div>
                  <div style={{fontSize:28,marginBottom:6,color:C.charLight}}>⬆</div>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginBottom:4}}>Drag &amp; drop a file here, or <span style={{color:C.navy,fontWeight:600}}>choose file</span></div>
                  <div style={{fontFamily:F.mono,fontSize:10,color:C.charLight}}>PDF, DOCX, DOC, TXT</div>
                </div>
              )}
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>{selectedFile?"Optional Notes":"Document Content"}</label>
            <textarea value={newDoc.content} onChange={e=>setNewDoc(p=>({...p,content:e.target.value}))} rows={selectedFile?3:6} placeholder={selectedFile?"Add context or instructions for the client (optional)…":"Paste or type the document content here…"} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,resize:"vertical",boxSizing:"border-box"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
            <div style={{display:"flex",gap:8}}>
              <Btn variant="primary" small onClick={()=>handleCreate("draft")} disabled={!newDoc.title||(!newDoc.content&&!selectedFile)} loading={saving}>Save Doc</Btn>
              <Btn variant="ghost" small onClick={()=>{setShowCreate(false);setSelectedFile(null);}}>Cancel</Btn>
            </div>
            <Btn variant="primary" small onClick={()=>handleCreate("pending_signature")} disabled={!newDoc.title||(!newDoc.content&&!selectedFile)} loading={saving}>Send for Signature</Btn>
          </div>
        </div>
      )}
      {documents.length===0?(
        <div style={{borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,padding:"22px 18px",textAlign:"center"}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charMid,marginBottom:6}}>No documents yet</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>{isAdvisor?"Create a document to send for signature.":"Your advisor will send documents here for your review and signature."}</div>
        </div>
      ):(
        <div>
          {documents.map((d,i)=>{
            const isFirst=i===0;
            const isLast=i===documents.length-1;
            const isExpanded=expandedId===d.id;
            const hasContent=!!(d.content&&d.content.trim());
            const hasFile=!!d.storage_path;
            const fileName=hasFile?d.storage_path.split('/').pop().replace(/^\d+_/,''):null;
            const previewSnippet=hasContent
              ?(d.content.slice(0,100)+(d.content.length>100?'…':''))
              :(hasFile?`📎 ${fileName}`:'');
            const rowBaseStyle={
              padding:"22px 18px",
              borderTop:isFirst?`1px solid ${C.border}`:"none",
              borderBottom:isLast?"none":`1px solid ${C.border}`,
              transition:"background .15s",
            };
            const openFile=async()=>{
              if(!d.storage_path) return;
              const w=window.open('','_blank');
              const {data}=await supabase.storage.from('client-files').createSignedUrl(d.storage_path,300);
              if(!data?.signedUrl){ if(w) w.close(); return; }
              const isInlineRenderable=/\.(pdf|png|jpe?g|gif|webp|svg)$/i.test(fileName||'');
              const isOffice=/\.(xlsx?|docx?|pptx?)$/i.test(fileName||'');
              const encoded=encodeURIComponent(data.signedUrl);
              const previewUrl=isInlineRenderable?data.signedUrl
                :isOffice?`https://view.officeapps.live.com/op/view.aspx?src=${encoded}`
                :`https://docs.google.com/viewer?url=${encoded}&embedded=true`;
              if(w) w.location=previewUrl; else window.open(previewUrl,'_blank');
            };
            const downloadFile=async()=>{
              if(!d.storage_path) return;
              const {data}=await supabase.storage.from('client-files').createSignedUrl(d.storage_path,300);
              if(!data?.signedUrl) return;
              const a=document.createElement('a');
              a.href=data.signedUrl;
              a.download=fileName||'document';
              document.body.appendChild(a);
              a.click();
              a.remove();
            };
            return (
              <div key={d.id} style={{...rowBaseStyle,display:"flex",flexDirection:"column"}}>
                <div onClick={()=>setExpandedId(isExpanded?null:d.id)}
                  onMouseEnter={e=>{if(!isExpanded)e.currentTarget.style.background=C.bgAlt;}}
                  onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                  style={{display:"flex",alignItems:"flex-start",gap:14,cursor:"pointer"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      {d.status==="signed"?<CheckCircle2 size={14} color={C.charLight}/>:<FileText size={14} color={C.charLight}/>}
                      <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500}}>{d.title}</div>
                    </div>
                    <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",marginBottom:6}}>
                      <Badge label={d.status==="signed"?"Signed":d.status==="pending_signature"?"Awaiting Signature":"Draft"} type={d.status==="signed"?"signed":d.status==="pending_signature"?"pending_signature":"draft"}/>
                      <span style={{fontFamily:F.body,fontSize:10,color:C.charLight}}>Sent {fmtDate(d.sent_date)}</span>
                      {d.signed_by&&<span style={{fontFamily:F.mono,fontSize:10,color:C.green}}>Signed by {d.signed_by}</span>}
                    </div>
                    {previewSnippet&&<div style={{fontFamily:F.body,fontSize:12,color:C.charMid,lineHeight:1.5,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{previewSnippet}</div>}
                  </div>
                  <span style={{fontFamily:F.mono,fontSize:11,color:C.charLight,flexShrink:0,marginTop:2}}>{isExpanded?"▲":"▼"}</span>
                </div>
                {isExpanded&&(
                  <div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                    {deleteConfirm===d.id?(
                      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                        <div>
                          <div style={{fontFamily:F.body,fontSize:13,color:C.red,fontWeight:600,marginBottom:2}}>Delete this document?</div>
                          <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>This cannot be undone.</div>
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={()=>handleDelete(d)} disabled={saving} style={{background:C.red,color:"#fff",border:"none",borderRadius:7,padding:"6px 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:saving?"not-allowed":"pointer",opacity:saving?.55:1}}>{saving?"Deleting…":"Delete"}</button>
                          <button onClick={()=>setDeleteConfirm(null)} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:7,padding:"6px 12px",fontFamily:F.body,fontSize:12,cursor:"pointer"}}>Cancel</button>
                        </div>
                      </div>
                    ):editingId===d.id?(
                      <div>
                        <div style={{marginBottom:12}}>
                          <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Title</label>
                          <input value={editFields.title} onChange={e=>setEditFields(p=>({...p,title:e.target.value}))} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                          <div>
                            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Status</label>
                            <select value={editFields.status} onChange={e=>setEditFields(p=>({...p,status:e.target.value}))} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}>
                              <option value="draft">Draft</option>
                              <option value="pending_signature">Awaiting Signature</option>
                              <option value="signed">Signed</option>
                            </select>
                          </div>
                          <div>
                            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>{d.storage_path?"Replace File":"Upload File"}</label>
                            <div onClick={()=>editFileRef.current?.click()} style={{cursor:"pointer",padding:"9px 12px",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,fontFamily:F.body,fontSize:12,color:editFields.newFile?C.navy:C.charMid,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                              {editFields.newFile?`📄 ${editFields.newFile.name} (new)`:(d.storage_path?`📄 ${fileName} — click to replace`:"Click to upload")}
                            </div>
                            <input ref={editFileRef} type="file" accept=".pdf,.docx,.doc,.txt" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)setEditFields(p=>({...p,newFile:f}));}}/>
                          </div>
                        </div>
                        <div style={{marginBottom:12}}>
                          <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Content</label>
                          <textarea value={editFields.content} onChange={e=>setEditFields(p=>({...p,content:e.target.value}))} rows={6} placeholder="Document content (optional if a file is attached)" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,resize:"vertical",boxSizing:"border-box"}}/>
                        </div>
                        <div style={{display:"flex",gap:8}}>
                          <Btn variant="primary" small onClick={()=>handleSaveEdit(d)} loading={saving} disabled={!editFields.title.trim()}>Save</Btn>
                          <Btn variant="ghost" small onClick={()=>{setEditingId(null);setEditFields({title:"",content:"",status:"draft",newFile:null});}}>Cancel</Btn>
                        </div>
                      </div>
                    ):(
                      <>
                        {(hasFile||canManage)&&(
                          <div style={{display:"flex",gap:8,marginBottom:hasContent?14:0,flexWrap:"wrap"}}>
                            {hasFile&&<button onClick={openFile} style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Preview</button>}
                            {hasFile&&<button onClick={downloadFile} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Download</button>}
                            {canManage&&<button onClick={()=>{setEditingId(d.id);setEditFields({title:d.title,content:d.content||"",status:d.status,newFile:null});}} style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Edit</button>}
                            {canManage&&<button onClick={()=>setDeleteConfirm(d.id)} style={{background:C.redBg,color:C.red,border:`1px solid ${C.redBd}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Delete</button>}
                          </div>
                        )}
                        {hasContent&&(
                          <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.75,whiteSpace:"pre-wrap"}}>{d.content}</div>
                        )}
                        {d.status!=="signed"&&!isAdvisor&&(
                          <div style={{marginTop:18,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
                            <div style={{fontFamily:F.display,fontSize:15,color:C.navy,marginBottom:4}}>Sign This Document</div>
                            <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:14}}>By typing your full name below, you confirm you have read and agree to this document.</div>
                            <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
                              <div style={{flex:1}}>
                                <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Full Legal Name</label>
                                <input value={sigName} onChange={e=>setSigName(e.target.value)} placeholder="Type your full name to sign" style={{width:"100%",background:C.surface,border:`1px solid ${C.borderHi||C.border}`,borderRadius:8,padding:"11px 13px",color:C.charcoal,fontFamily:F.display,fontSize:15,fontStyle:"italic",boxSizing:"border-box"}}/>
                              </div>
                              <Btn variant="success" onClick={()=>handleSign(d.id)} disabled={!sigName.trim()} loading={saving}>Sign Document</Btn>
                            </div>
                          </div>
                        )}
                        {d.status==="signed"&&d.signed_date&&(
                          <div style={{marginTop:14,fontFamily:F.body,fontSize:12,color:C.green,fontWeight:600}}>✓ Signed by {d.signed_by} on {fmtDate(d.signed_date)}</div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── TEAM MEMBERS TAB ──────────────────────────────────────────
// Phase 3: page-level toggles + per-item access pickers. The Select Access
// modal opens when a per-item page is toggled on (or via the inline Edit
// button on an already-on toggle). Assessment is on/off only — no picker.
// Persistence: page toggles → team_member_profiles.page_access;
// per-item selections → team_member_access rows (synced on save).
const TEAM_PAGE_OPTIONS = [
  {key:"projects",   label:"Projects"},
  {key:"files",      label:"Files"},
  {key:"docs",       label:"Docs"},
  {key:"resources",  label:"Resources"},
  {key:"billing",    label:"Billing"},
];
const TEAM_PICKER_KEYS = TEAM_PAGE_OPTIONS.filter(o => o.key !== 'assessment').map(o => o.key);
function emptyTeamItemAccess() {
  return TEAM_PICKER_KEYS.reduce((m, k) => { m[k] = []; return m; }, {});
}

function TeamMembersTab({client, isAdvisor, user}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({firstName:"", lastName:"", email:"", jobTitle:"", pageAccess:[], itemAccess: emptyTeamItemAccess()});
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // {url, pin, name, message}
  const [copied, setCopied] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Per-client item counts. Used to gate page toggles ("No items yet") and
  // render the "X of Y" pill on member rows.
  const [clientCounts, setClientCounts] = useState({projects:0, files:0, docs:0, resources:0, billing:0, assessment:0});

  // Select Access modal state.
  const [pickerPage, setPickerPage] = useState(null);          // page key currently being picked, or null
  const [pickerSelected, setPickerSelected] = useState(new Set());
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerCache, setPickerCache] = useState(null);        // {projects:[...], files:[...], ...}

  const isMobile = useIsMobile();

  useEffect(()=>{
    let alive = true;
    (async()=>{
      try {
        const data = await getTeamMembersForClient(client.id);
        if (!alive) return;
        setMembers(data||[]);
        if (data && data[0]?.clientItemCounts) {
          setClientCounts(data[0].clientItemCounts);
        } else {
          // No members yet — fetch counts directly so the form still gates correctly.
          const counts = await getClientItemCounts(client.id);
          if (alive) setClientCounts(counts);
        }
      } catch(e) {
        console.error("Failed to load team members:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return ()=>{ alive=false; };
  },[client.id]);

  function resetForm(){
    setDraft({firstName:"", lastName:"", email:"", jobTitle:"", pageAccess:[], itemAccess: emptyTeamItemAccess()});
    setEditingId(null);
  }
  function openAdd(){ resetForm(); setShowForm(true); setInviteResult(null); }
  function openEdit(m){
    // Split stored name on first space (mirrors temp_name.split pattern at App.js:164).
    const fullName = (m.name||"").trim();
    const splitIdx = fullName.indexOf(" ");
    const fn = splitIdx >= 0 ? fullName.slice(0, splitIdx) : fullName;
    const ln = splitIdx >= 0 ? fullName.slice(splitIdx + 1) : "";
    const ia = emptyTeamItemAccess();
    if (m.itemAccess) {
      TEAM_PICKER_KEYS.forEach(k => { ia[k] = Array.isArray(m.itemAccess[k]) ? [...m.itemAccess[k]] : []; });
    }
    setDraft({
      firstName: fn,
      lastName: ln,
      email: m.email || "",
      jobTitle: m.job_title || "",
      pageAccess: m.page_access || [],
      itemAccess: ia,
    });
    setEditingId(m.id);
    setShowForm(true);
    setInviteResult(null);
  }
  function cancelForm(){ resetForm(); setShowForm(false); }

  async function loadPickerCache(){
    if (pickerCache) return pickerCache;
    setPickerLoading(true);
    try {
      const data = await getClientResourcesForPicker(client.id);
      setPickerCache(data);
      return data;
    } finally {
      setPickerLoading(false);
    }
  }

  async function togglePageAccess(key){
    if (key === 'assessment' || key === 'resources') {
      // Binary toggle — no picker.
      setDraft(p => ({...p, pageAccess: p.pageAccess.includes(key) ? p.pageAccess.filter(k=>k!==key) : [...p.pageAccess, key]}));
      return;
    }
    const currentlyOn = draft.pageAccess.includes(key);
    if (currentlyOn) {
      // Off: remove from page_access AND clear itemAccess for this key (per spec).
      setDraft(p => ({
        ...p,
        pageAccess: p.pageAccess.filter(k => k !== key),
        itemAccess: {...p.itemAccess, [key]: []},
      }));
      return;
    }
    // Disabled state — no items to grant.
    if (!clientCounts[key] || clientCounts[key] === 0) return;
    await loadPickerCache();
    setPickerSelected(new Set(draft.itemAccess[key] || []));
    setPickerPage(key);
  }

  // Inline "Edit" affordance: re-open picker for an already-on toggle without
  // clearing the existing selection.
  async function openPicker(key){
    if (key === 'assessment' || key === 'resources' || !TEAM_PICKER_KEYS.includes(key)) return;
    if (!clientCounts[key] || clientCounts[key] === 0) return;
    await loadPickerCache();
    setPickerSelected(new Set(draft.itemAccess[key] || []));
    setPickerPage(key);
  }

  function pickerToggleAll(){
    if (!pickerPage || !pickerCache) return;
    const items = pickerCache[pickerPage] || [];
    if (pickerSelected.size === items.length) {
      setPickerSelected(new Set());
    } else {
      setPickerSelected(new Set(items.map(i => i.id)));
    }
  }
  function pickerToggleOne(id){
    setPickerSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function pickerCancel(){
    // Per spec: closes modal. If the modal opened via off→on toggle, the
    // toggle was never added to pageAccess, so leaving it as-is naturally
    // reverts to OFF. If opened via Edit on an already-on toggle, the
    // existing selection is untouched.
    setPickerPage(null);
    setPickerSelected(new Set());
  }
  function pickerSave(){
    if (!pickerPage || pickerSelected.size === 0) return;
    const ids = Array.from(pickerSelected);
    setDraft(p => ({
      ...p,
      pageAccess: p.pageAccess.includes(pickerPage) ? p.pageAccess : [...p.pageAccess, pickerPage],
      itemAccess: {...p.itemAccess, [pickerPage]: ids},
    }));
    setPickerPage(null);
    setPickerSelected(new Set());
  }

  function gen6DigitPin(){ return String(Math.floor(100000 + Math.random()*900000)); }

  function buildInviteMessage({name, url, pin, inviterName, clientCompany}){
    return `Hi ${titleCaseName(name)},

You've been invited to join ${clientCompany}'s portal in Integro — a shared workspace for the team's projects, files, and documents.

GAIN ACCESS:
Click the link below to enter your PIN, then verify your name and email and set your own password.

Link: ${url}
PIN: ${pin}

Once you're in, you'll have access to the parts of the portal that have been shared with you. Please reach out if you have any questions.

— ${inviterName}`;
  }

  async function handleSave(){
    const fn = draft.firstName.trim();
    const ln = draft.lastName.trim();
    const name = `${fn} ${ln}`.trim();
    const email = draft.email.trim().toLowerCase();
    if(!fn || !email) return;
    setSaving(true);
    try {
      if (editingId) {
        await updateTeamMember(editingId, {
          name,
          jobTitle: draft.jobTitle.trim(),
          pageAccess: draft.pageAccess,
          itemAccess: draft.itemAccess,
          grantedBy: user?.id || null,
        });
        const refreshed = await getTeamMembersForClient(client.id);
        setMembers(refreshed||[]);
        if (refreshed && refreshed[0]?.clientItemCounts) setClientCounts(refreshed[0].clientItemCounts);
        cancelForm();
      } else {
        const pin = gen6DigitPin();
        const inviterName = user?.name || "Your team";
        const clientCompany = client.company || "your team";
        // Create the profile first (we need its token to build the URL).
        const created = await createTeamMember(client.id, user?.id || null, {
          name,
          email,
          jobTitle: draft.jobTitle.trim(),
          pin,
          pageAccess: draft.pageAccess,
          itemAccess: draft.itemAccess,
          inviteMessageTemplate: null,
          invitedByName: user?.name || null,
        });
        const url = `${window.location.origin}/join/team/${created.invite_token}`;
        const message = buildInviteMessage({name, url, pin, inviterName, clientCompany});
        // Persist the rendered template so it can be re-shown later.
        await updateTeamMember(created.id, { inviteMessageTemplate: message });
        setInviteResult({url, pin, name, message});
        const refreshed = await getTeamMembersForClient(client.id);
        setMembers(refreshed||[]);
        if (refreshed && refreshed[0]?.clientItemCounts) setClientCounts(refreshed[0].clientItemCounts);
        cancelForm();
      }
    } catch(e) {
      console.error("Failed to save team member:", e);
      alert("Failed to save team member: " + (e.message||e));
    }
    setSaving(false);
  }

  function copyMessage(){
    if (!inviteResult) return;
    navigator.clipboard.writeText(inviteResult.message);
    setCopied(true);
    setTimeout(()=>setCopied(false), 2000);
  }

  function showStoredInvite(m){
    const url = `${window.location.origin}/join/team/${m.invite_token}`;
    const inviterName = user?.name || "Your team";
    const clientCompany = client.company || "your team";
    const message = m.invite_message_template || buildInviteMessage({
      name: m.name || "there",
      url,
      pin: m.invite_pin || "(no PIN on file)",
      inviterName,
      clientCompany,
    });
    setInviteResult({url, pin: m.invite_pin || "", name: m.name || "", message});
  }

  function armDelete(id){ setDeleteConfirmId(id); }
  function cancelDelete(){ setDeleteConfirmId(null); }
  async function confirmDelete(id){
    try {
      await deleteTeamMember(id);
      setMembers(p=>p.filter(m=>m.id!==id));
      setDeleteConfirmId(null);
    } catch(e) {
      alert("Failed to delete team member: " + (e.message||e));
    }
  }

  const pillStyle = {background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"3px 9px",fontFamily:F.body,fontSize:11,fontWeight:600,whiteSpace:"nowrap"};

  // Row pill: "X of Y projects · 2 of 5 files · All docs · Assessment"
  function accessSummary(m){
    const pa = m.page_access || [];
    if (pa.length === 0) return "No access yet";
    const counts = m.clientItemCounts || clientCounts;
    const parts = [];
    TEAM_PAGE_OPTIONS.forEach(opt => {
      if (!pa.includes(opt.key)) return;
      if (opt.key === 'assessment') { parts.push("Assessment"); return; }
      const total = counts[opt.key] || 0;
      const selected = (m.itemAccess?.[opt.key] || []).length;
      if (total === 0) return;
      if (selected >= total) parts.push(`All ${opt.label.toLowerCase()}`);
      else parts.push(`${selected} of ${total} ${opt.label.toLowerCase()}`);
    });
    return parts.length === 0 ? "No access yet" : parts.join(" · ");
  }

  // Form renderer — used in two slots: above the list in Add mode
  // (editingId === null) and inline beneath the row being edited
  // (editingId === m.id). Identical JSX in both places; the existing
  // editingId-driven branches already swap title, subtitle, email lock,
  // and button label, so no per-slot copy needed here.
  function renderTeamForm(){
    return (
      <Card style={{padding:"22px 24px",marginBottom:20}}>
        <div style={{fontFamily:F.display,fontSize:18,color:C.charcoal,marginBottom:4}}>
          {editingId ? "Edit Team Member" : "Add Team Member"}
        </div>
        <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,marginBottom:16}}>
          {editingId ? "Update the team member's details and access." : "Create a team member profile and generate their copy-paste invite."}
        </div>
        <div style={{display:"grid",gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr",gap:12,marginBottom:16}}>
          <div>
            <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>First Name</label>
            <input value={draft.firstName} onChange={e=>setDraft(p=>({...p,firstName:e.target.value}))} placeholder="Jane"
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>Last Name</label>
            <input value={draft.lastName} onChange={e=>setDraft(p=>({...p,lastName:e.target.value}))} placeholder="Smith"
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>
              Email {editingId && <span style={{textTransform:"none",fontWeight:500,color:C.charLight}}>(locked)</span>}
            </label>
            <input value={draft.email} onChange={e=>setDraft(p=>({...p,email:e.target.value}))} placeholder="jane@acme.co" type="email" disabled={!!editingId}
              style={{width:"100%",background:editingId?C.surface:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:editingId?C.charLight:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>Job Title</label>
            <input value={draft.jobTitle} onChange={e=>setDraft(p=>({...p,jobTitle:e.target.value}))} placeholder="Operations Manager"
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
        </div>

        {/* Page access toggles + per-item picker triggers */}
        <div style={{marginBottom:18}}>
          <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:9,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>Page Access</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:10,alignItems:"center"}}>
            {TEAM_PAGE_OPTIONS.map(opt => {
              const on = draft.pageAccess.includes(opt.key);
              const isAssessment = opt.key === 'assessment';
              const isPageLevel = opt.key === 'assessment' || opt.key === 'resources';
              const total = clientCounts[opt.key] || 0;
              const disabled = !isPageLevel && total === 0;
              const selected = (draft.itemAccess[opt.key] || []).length;
              let label = opt.label;
              if (on && !isPageLevel) {
                label = (selected >= total && total > 0)
                  ? `${opt.label} · All ${total}`
                  : `${opt.label} · ${selected}/${total}`;
              }
              return (
                <div key={opt.key} style={{display:"flex",alignItems:"center",gap:6}}>
                  <button
                    onClick={()=>!disabled && togglePageAccess(opt.key)}
                    type="button"
                    disabled={disabled}
                    title={disabled ? `No items yet — add ${opt.label.toLowerCase()} first` : ""}
                    style={{
                      background: disabled ? C.bgAlt : (on ? C.navy : C.bgAlt),
                      color: disabled ? C.charLight : (on ? "#fff" : C.charMid),
                      border: `1px solid ${disabled ? C.border : (on ? C.navy : C.border)}`,
                      borderRadius: on && !isPageLevel ? "7px 0 0 7px" : 7,
                      padding: "7px 14px",
                      fontFamily: F.body,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? .55 : 1,
                    }}>
                    {on ? "✓ " : ""}{label}
                  </button>
                  {on && !isPageLevel && (
                    <button
                      onClick={()=>openPicker(opt.key)}
                      type="button"
                      title="Edit selection"
                      style={{
                        background: C.navy,
                        color: "#fff",
                        border: `1px solid ${C.navy}`,
                        borderLeft: `1px solid rgba(255,255,255,.25)`,
                        borderRadius: "0 7px 7px 0",
                        padding: "7px 11px",
                        fontFamily: F.body,
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: "pointer",
                        marginLeft: -6,
                      }}>
                      Edit
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginTop:8}}>
            * Toggling a section on opens a picker so you can choose which items this member can see. Resources is shared with everyone — on/off only.
          </div>
          <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginTop:4}}>
            ** Greyed-out sections have no items to share yet.
          </div>
        </div>

        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%"}}>
          <Btn variant="primary" onClick={handleSave} loading={saving} disabled={!draft.firstName.trim() || !draft.email.trim()}>
            {editingId ? "Save Changes" : "Create & Generate Invite"}
          </Btn>
          <button type="button" onClick={cancelForm}
            style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 18px",color:C.charMid,fontFamily:F.body,fontSize:13,cursor:"pointer"}}>
            Cancel
          </button>
        </div>
      </Card>
    );
  }

  const action = (
    <button onClick={openAdd}
      style={{background:C.navy,color:"#fff",border:"none",borderRadius:8,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
      + Add Team Member
    </button>
  );

  return (
    <div>
      {isMobile ? (
        <div style={{marginBottom:20}}>
          <h2 style={{fontFamily:F.display,fontSize:22,color:C.charcoal,fontWeight:600,margin:0}}>Team Members</h2>
          <p style={{fontFamily:F.body,fontSize:13,color:C.charMid,margin:"5px 0 14px"}}>
            Invite your team to collaborate. Toggle a section on, then pick which specific items this member can see.
          </p>
          <button onClick={openAdd}
            style={{width:"100%",height:46,background:C.navy,color:"#fff",border:"none",borderRadius:8,fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
            + Add Team Member
          </button>
        </div>
      ) : (
        <SectionHead
          title="Team Members"
          sub="Invite your team to collaborate. Toggle a section on, then pick which specific items this member can see."
          action={action}/>
      )}

      {/* Add form — top-of-list slot, ONLY in Add mode. When editing,
          the form renders inline beneath the row being edited (see
          members.map below), so the top slot is suppressed to avoid
          a second copy. */}
      {showForm && !editingId && renderTeamForm()}

      {/* Select Access modal */}
      {pickerPage && (
        <SelectAccessModal
          pageLabel={TEAM_PAGE_OPTIONS.find(o => o.key === pickerPage)?.label || pickerPage}
          items={(pickerCache && pickerCache[pickerPage]) || []}
          loading={pickerLoading}
          selected={pickerSelected}
          onToggle={pickerToggleOne}
          onToggleAll={pickerToggleAll}
          onCancel={pickerCancel}
          onSave={pickerSave}
        />
      )}

      {/* Invite-ready confirmation modal (inline) */}
      {inviteResult && (
        <Card style={{padding:"22px 24px",marginBottom:20,background:C.greenBg,border:`1px solid ${C.greenBd}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:12}}>
            <div>
              <div style={{fontFamily:F.display,fontSize:16,color:C.charcoal,fontWeight:700,marginBottom:2}}>Invite ready for {inviteResult.name}</div>
              <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>Copy this message and send it however you'd like — no email is sent automatically.</div>
            </div>
            <button onClick={()=>setInviteResult(null)}
              style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Close</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>Invite Link</label>
              <div style={{fontFamily:F.mono,fontSize:11,color:C.charcoal,background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 12px",wordBreak:"break-all"}}>{inviteResult.url}</div>
            </div>
            <div>
              <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>PIN</label>
              <div style={{fontFamily:F.mono,fontSize:18,color:C.charcoal,background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 12px",letterSpacing:".2em"}}>{inviteResult.pin || "—"}</div>
            </div>
          </div>
          <label style={{fontFamily:F.body,fontSize:9,color:C.charMid,display:"block",marginBottom:5,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em"}}>Copy-Paste Message</label>
          <textarea readOnly value={inviteResult.message} rows={4}
            style={{width:"100%",background:C.surface,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box",resize:"vertical",marginBottom:12}}/>
          <button onClick={copyMessage}
            style={{background:copied?C.green:C.navy,color:"#fff",border:"none",borderRadius:7,padding:"9px 18px",fontFamily:F.body,fontSize:13,fontWeight:600,cursor:"pointer"}}>
            {copied ? "Copied!" : "Copy Message"}
          </button>
        </Card>
      )}

      {/* Team list — lines-not-boxes, matching the Advisors row pattern */}
      <div style={{marginBottom:28}}>
        {loading ? (
          <div style={{padding:"22px 18px",fontFamily:F.body,fontSize:13,color:C.charLight,fontStyle:"italic"}}>Loading team members…</div>
        ) : members.length === 0 ? (
          <div style={{padding:"22px 18px",borderTop:`1px solid ${C.border}`,fontFamily:F.body,fontSize:13,color:C.charLight,fontStyle:"italic"}}>
            No team members yet. Click "+ Add Team Member" to invite your first.
          </div>
        ) : (
          members.map((m,i)=>{
            const isFirst=i===0;
            const isLast=i===members.length-1;
            const initials=(m.name||"?").split(" ").map(w=>w[0]||"").join("").slice(0,2).toUpperCase();
            const isConfirmingDelete = deleteConfirmId === m.id;
            const statusActive = !!m.invite_claimed;
            return isMobile ? (
              <Fragment key={m.id}>
                <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:14,marginBottom:12}}>
                  {/* Header — avatar + name/email */}
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:36,height:36,borderRadius:"50%",background:C.navyFaint,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F.mono,fontSize:12,color:C.navy,fontWeight:700,flexShrink:0}}>{initials}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontFamily:F.body,fontSize:15,color:C.charcoal,fontWeight:600}}>{m.name}</div>
                      <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{m.email}</div>
                    </div>
                  </div>
                  {/* Badges — allowed to wrap below the header */}
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center",marginTop:12}}>
                    {m.job_title && <span style={pillStyle}>{m.job_title}</span>}
                    <span style={pillStyle}>{accessSummary(m)}</span>
                    <Badge label={statusActive ? "Active" : "Pending Invite"} type={statusActive ? "active" : "pending"}/>
                  </div>
                  {/* Actions — divider then primary/secondary buttons + kebab */}
                  <div style={{marginTop:13,borderTop:`1px solid ${C.border}`,paddingTop:12}}>
                    {isConfirmingDelete ? (
                      <div style={{display:"flex",gap:8}}>
                        <button onClick={()=>confirmDelete(m.id)}
                          style={{flex:1,height:44,background:C.red,color:"#fff",border:"none",borderRadius:6,padding:"0 14px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                          Click again to permanently delete
                        </button>
                        <button onClick={cancelDelete}
                          style={{height:44,background:C.surface,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"0 14px",fontFamily:F.body,fontSize:12,cursor:"pointer"}}>Cancel</button>
                      </div>
                    ) : (
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        {!statusActive && (
                          <ActionButton label="Show Invite" onClick={()=>showStoredInvite(m)} variant="primary" block size="lg"/>
                        )}
                        <ActionButton label="Edit" onClick={()=>openEdit(m)} variant="secondary" block size="lg"/>
                        <OverflowMenu items={[{ label: 'Delete', onClick: ()=>armDelete(m.id), danger: true }]}/>
                      </div>
                    )}
                  </div>
                </div>
                {showForm && editingId === m.id && renderTeamForm()}
              </Fragment>
            ) : (
              <Fragment key={m.id}>
                <div
                  onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
                  onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                  style={{
                    padding:"18px 18px",
                    borderTop:isFirst?`1px solid ${C.border}`:"none",
                    borderBottom:isLast?"none":`1px solid ${C.border}`,
                    display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",
                    transition:"background .15s",
                  }}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:C.navyFaint,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:F.mono,fontSize:12,color:C.navy,fontWeight:700,flexShrink:0}}>{initials}</div>
                  <div style={{flex:1,minWidth:160}}>
                    <div style={{fontFamily:F.body,fontSize:14,color:C.charcoal,fontWeight:600}}>{m.name}</div>
                    <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>{m.email}</div>
                  </div>
                  {m.job_title && <span style={pillStyle}>{m.job_title}</span>}
                  <span style={pillStyle}>{accessSummary(m)}</span>
                  <Badge label={statusActive ? "Active" : "Pending Invite"} type={statusActive ? "active" : "pending"}/>
                  {isConfirmingDelete ? (
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <button onClick={()=>confirmDelete(m.id)}
                        style={{background:C.red,color:"#fff",border:"none",borderRadius:5,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                        Click again to permanently delete
                      </button>
                      <button onClick={cancelDelete}
                        style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      {!statusActive && (
                        <button onClick={()=>showStoredInvite(m)}
                          style={{background:"transparent",color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:5,padding:"5px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Show Invite</button>
                      )}
                      <button onClick={()=>openEdit(m)}
                        style={{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Edit</button>
                      <button onClick={()=>armDelete(m.id)}
                        style={{background:"transparent",color:C.charLight,border:`1px solid ${C.border}`,borderRadius:5,padding:"5px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Delete</button>
                    </div>
                  )}
                </div>
                {showForm && editingId === m.id && renderTeamForm()}
              </Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}

// Select Access modal — used by TeamMembersTab when toggling on a per-item
// page or clicking the inline Edit affordance on an already-on toggle.
function SelectAccessModal({pageLabel, items, loading, selected, onToggle, onToggleAll, onCancel, onSave}) {
  const total = items.length;
  const allSelected = total > 0 && selected.size === total;
  const canSave = selected.size > 0;
  return (
    <div onClick={onCancel}
      style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(20,30,50,.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
      <div onClick={e=>e.stopPropagation()}
        style={{background:C.surface,borderRadius:12,maxWidth:520,width:"100%",maxHeight:"80vh",display:"flex",flexDirection:"column",boxShadow:"0 12px 48px rgba(0,0,0,.25)"}}>
        <div style={{padding:"18px 22px 12px",borderBottom:`1px solid ${C.border}`}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600,marginBottom:3}}>Select Access — {pageLabel}</div>
          <div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>Choose which {pageLabel.toLowerCase()} this team member can see.</div>
        </div>
        <div style={{padding:"12px 22px",overflowY:"auto",flex:1}}>
          {loading ? (
            <div style={{padding:"18px 0",fontFamily:F.body,fontSize:13,color:C.charLight,fontStyle:"italic"}}>Loading…</div>
          ) : items.length === 0 ? (
            <div style={{padding:"18px 0",fontFamily:F.body,fontSize:13,color:C.charLight,fontStyle:"italic"}}>No items to select.</div>
          ) : (
            <>
              <label style={{display:"flex",alignItems:"center",gap:10,padding:"8px 6px",cursor:"pointer",borderBottom:`1px solid ${C.border}`,marginBottom:6}}>
                <input type="checkbox" checked={allSelected} onChange={onToggleAll}
                  style={{cursor:"pointer",accentColor:C.navy,width:16,height:16}}/>
                <span style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600}}>Select all {pageLabel.toLowerCase()} ({total})</span>
              </label>
              {items.map(it => (
                <label key={it.id}
                  onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
                  onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"8px 6px",cursor:"pointer",borderRadius:6}}>
                  <input type="checkbox" checked={selected.has(it.id)} onChange={()=>onToggle(it.id)}
                    style={{cursor:"pointer",accentColor:C.navy,width:16,height:16}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{it.name}</div>
                    {it.subtitle && <div style={{fontFamily:F.body,fontSize:11,color:C.charLight}}>{it.subtitle}</div>}
                  </div>
                </label>
              ))}
            </>
          )}
        </div>
        <div style={{padding:"12px 22px 18px",borderTop:`1px solid ${C.border}`,display:"flex",justifyContent:"flex-end",gap:8}}>
          <button onClick={onCancel}
            style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 16px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button onClick={onSave} disabled={!canSave}
            style={{background:canSave?C.navy:C.bgAlt,color:canSave?"#fff":C.charLight,border:`1px solid ${canSave?C.navy:C.border}`,borderRadius:7,padding:"8px 16px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:canSave?"pointer":"not-allowed",opacity:canSave?1:.7}}>
            Save Selection
          </button>
        </div>
      </div>
    </div>
  );
}

// ── BILLING TAB ───────────────────────────────────────────────
function BillingTab({client, isAdvisor, user, invoices, setInvoices, isMobile}) {
  const [showCreate,setShowCreate]=useState(false);
  const [newInv,setNewInv]=useState({title:"",amount:"",dueDate:""});
  const [invFile,setInvFile]=useState(null);
  const [marking,setMarking]=useState(null);
  const [saving,setSaving]=useState(false);
  const [expandedInv,setExpandedInv]=useState(null);
  const [editingInvId,setEditingInvId]=useState(null);
  const [editInv,setEditInv]=useState({title:"",amount:"",dueDate:""});
  const [savingEdit,setSavingEdit]=useState(false);
  const [deleteInvConfirmId,setDeleteInvConfirmId]=useState(null);
  const [editingDateField,setEditingDateField]=useState(null); // {invId, field:'issue_date'|'paid_date'}
  const [editingDateValue,setEditingDateValue]=useState("");
  const invFileRef=useRef(null);

  async function saveInvoiceDate(invId, field, value){
    const prev = invoices.find(i=>i.id===invId);
    const newVal = value || null;
    setInvoices(p=>p.map(i=>i.id===invId?{...i,[field]:newVal}:i));
    try {
      const {error} = await supabase.from('invoices').update({[field]:newVal}).eq('id',invId);
      if (error) throw error;
    } catch (e) {
      console.error("saveInvoiceDate failed:", e);
      if (prev) setInvoices(p=>p.map(i=>i.id===invId?prev:i));
    }
    setEditingDateField(null);
    setEditingDateValue("");
  }

  const paidCount=invoices.filter(i=>i.status==="paid").length;
  const unpaidCount=invoices.filter(i=>i.status!=="paid").length;

  async function handleCreate(){
    if(!newInv.title||!newInv.amount) return;
    setSaving(true);
    let storagePath=null;
    if(invFile){
      const path=`invoices/${client.id}/${Date.now()}_${invFile.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const {data}=await supabase.storage.from('client-files').upload(path,invFile);
      if(data) storagePath=data.path;
    }
    const inv=await createInvoice(client.id,{
      title:newInv.title,
      amount:Number(newInv.amount.replace(/[^0-9.]/g,"")),
      status:"unpaid",
      issue_date:todayLocal(),
      due_date:newInv.dueDate||todayLocal(7),
      storage_path:storagePath,
    });
    setInvoices(p=>[{...inv,storage_path:storagePath},...p]);
    setNewInv({title:"",amount:"",dueDate:""});setInvFile(null);setShowCreate(false);setSaving(false);
  }

  async function handleMarkPaid(id,e){
    e.stopPropagation();
    setMarking(id);
    const inv=await markInvoicePaid(id);
    setInvoices(p=>p.map(i=>i.id===id?inv:i));
    setMarking(null);
  }

  async function handleMarkUnpaid(id,e){
    e.stopPropagation();
    setMarking(id);
    const inv=await markInvoiceUnpaid(id);
    setInvoices(p=>p.map(i=>i.id===id?inv:i));
    setMarking(null);
  }

  async function openInvoiceFile(inv,e){
    e.stopPropagation();
    if(!inv.storage_path) return;
    const w=window.open('','_blank');
    const {data}=await supabase.storage.from('client-files').createSignedUrl(inv.storage_path,60);
    if(data?.signedUrl){ if(w) w.location=data.signedUrl; else window.open(data.signedUrl,'_blank'); }
    else if(w) w.close();
  }

  function openEdit(inv){
    // Close any open create form so we don't have two forms competing.
    setShowCreate(false);
    setEditInv({
      title: inv.title || "",
      amount: String(inv.amount ?? ""),
      dueDate: inv.due_date || "",
    });
    setEditingInvId(inv.id);
  }

  function handleCancelEdit(){
    setEditingInvId(null);
    setEditInv({title:"",amount:"",dueDate:""});
  }

  async function handleSaveEdit(){
    if(!editInv.title||!editInv.amount) return;
    setSavingEdit(true);
    try {
      const inv = await updateInvoice(editingInvId, {
        title: editInv.title,
        amount: Number(editInv.amount.replace(/[^0-9.]/g,"")),
        due_date: editInv.dueDate || null,
      });
      // storage_path isn't round-tripped through the DB today (it's a
      // client-side-only field set after upload in handleCreate). Preserve
      // the local copy so the Open button doesn't disappear after edit.
      setInvoices(p=>p.map(i=>i.id===editingInvId?{...inv,storage_path:i.storage_path}:i));
      setEditingInvId(null);
      setEditInv({title:"",amount:"",dueDate:""});
    } catch (e) {
      console.error("updateInvoice failed:", e);
    }
    setSavingEdit(false);
  }

  function armDelete(id){ setDeleteInvConfirmId(id); }
  function cancelDelete(){ setDeleteInvConfirmId(null); }

  async function confirmDelete(inv){
    try {
      await deleteInvoice(inv.id, inv.storage_path);
      setInvoices(p=>p.filter(i=>i.id!==inv.id));
    } catch (e) {
      console.error("deleteInvoice failed:", e);
    }
    setDeleteInvConfirmId(null);
  }

  // Form helper — used by both the top create slot and the inline per-row
  // edit slot. Branches on editingInvId for title, button label, draft
  // source, and submit handler. The attachment dropzone is create-only;
  // edit only touches title / amount / due date.
  function renderInvoiceForm(){
    const editing = !!editingInvId;
    const draft = editing ? editInv : newInv;
    const setDraft = editing ? setEditInv : setNewInv;
    return (
      <Card style={{padding:"18px 22px",marginBottom:16}}>
        <div style={{fontFamily:F.display,fontSize:15,color:C.charcoal,marginBottom:12}}>{editing ? "Edit Invoice" : "New Invoice"}</div>
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"2fr 1fr 1fr",gap:12,marginBottom:12}}>
          <div>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Description</label>
            <input type="text" value={draft.title} onChange={e=>setDraft(p=>({...p,title:e.target.value}))} placeholder="e.g. May 2026 — Advisory Services" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Amount ($)</label>
            <input type="text" value={draft.amount} onChange={e=>setDraft(p=>({...p,amount:e.target.value}))} placeholder="5,000" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
          <div>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Due Date</label>
            <input type="date" value={draft.dueDate} onChange={e=>setDraft(p=>({...p,dueDate:e.target.value}))} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
        </div>
        {/* Attachment dropzone — create only. Edit doesn't touch the file. */}
        {!editing && (
          <div style={{marginBottom:12}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Attach Invoice Template (optional)</label>
            <div onClick={()=>invFileRef.current?.click()} style={{border:`2px dashed ${C.border}`,borderRadius:8,padding:"14px 16px",cursor:"pointer",background:C.bgAlt,display:"flex",alignItems:"center",gap:10}}>
              <input ref={invFileRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg" style={{display:"none"}} onChange={e=>setInvFile(e.target.files[0])}/>
              <span style={{fontSize:20}}>📄</span>
              <span style={{fontFamily:F.body,fontSize:12,color:invFile?C.navy:C.charLight}}>{invFile?invFile.name:"Click to attach your invoice template (PDF, Word, image)"}</span>
              {invFile&&<button onClick={e=>{e.stopPropagation();setInvFile(null);}} style={{marginLeft:"auto",background:"none",border:"none",color:C.charLight,cursor:"pointer",fontSize:12}}>✕</button>}
            </div>
          </div>
        )}
        <div style={{display:"flex",gap:8}}>
          <Btn variant="primary" small onClick={editing ? handleSaveEdit : handleCreate} disabled={!draft.title||!draft.amount} loading={editing ? savingEdit : saving}>
            {editing ? "Save Changes" : "Create Invoice"}
          </Btn>
          <Btn variant="ghost" small onClick={editing ? handleCancelEdit : ()=>{setShowCreate(false);setInvFile(null);}}>Cancel</Btn>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {isMobile?(
        <div style={{marginBottom:20}}>
          <div style={{fontFamily:F.display,fontSize:34,color:C.charcoal,fontWeight:600,marginBottom:6}}>Billing</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginTop:0,marginBottom:14}}>Invoices and payment history for your engagement.</div>
          {isAdvisor&&<ActionButton variant="primary" size="lg" block label="+ New Invoice" onClick={()=>setShowCreate(p=>!p)}/>}
        </div>
      ):(
        <SectionHead title="Billing" sub="Invoices and payment history for your engagement."
          action={isAdvisor&&<Btn variant="primary" small onClick={()=>setShowCreate(p=>!p)}>+ New Invoice</Btn>}/>
      )}
      <div style={{display:"flex",gap:10,marginBottom:20,...(isMobile&&{justifyContent:"center"})}}>
        {[
          {l:"Invoices Paid",v:paidCount,c:C.green},
          {l:"Awaiting Payment",v:unpaidCount,c:unpaidCount>0?C.amber:C.charLight},
        ].map(s=>(
          <div key={s.l} style={{padding:"14px 16px",...(isMobile?{flex:"0 0 auto",minWidth:140}:{flex:1})}}>
            <div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em",marginBottom:5}}>{s.l}</div>
            <div style={{fontFamily:F.display,fontSize:28,color:s.c,fontWeight:700,lineHeight:1}}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Create form — top-of-list slot, ONLY in Create mode. When editing
          an existing invoice, renderInvoiceForm is rendered inline below
          the row being edited (see the row map), so the top slot is
          suppressed to avoid two forms competing. */}
      {showCreate&&isAdvisor&&!editingInvId&&renderInvoiceForm()}

      {invoices.length===0?(
        <Card style={{padding:40,textAlign:"center"}}><div style={{fontFamily:F.display,fontSize:17,color:C.charMid}}>No invoices yet.</div></Card>
      ):(
        <div>
          {invoices.map((inv,i)=>{
            const isFirst=i===0;
            const isLast=i===invoices.length-1;
            const isPaid=inv.status==="paid";
            const rowBaseStyle={
              padding:"22px 18px",
              borderTop:isFirst?`1px solid ${C.border}`:"none",
              borderBottom:isLast?"none":`1px solid ${C.border}`,
              transition:"background .15s",
            };
            const kebabItems = isPaid ? [
              ...(inv.storage_path ? [{label:"📄 Open file", onClick:()=>openInvoiceFile(inv,{stopPropagation:()=>{}})}] : []),
              {label:"Mark unpaid", onClick:()=>handleMarkUnpaid(inv.id,{stopPropagation:()=>{}})},
              {label:"Edit", onClick:()=>openEdit(inv)},
              {label:"Delete", onClick:()=>armDelete(inv.id), danger:true},
            ] : [
              ...(inv.storage_path ? [{label:"📄 Open file", onClick:()=>openInvoiceFile(inv,{stopPropagation:()=>{}})}] : []),
              {label:"Mark paid", onClick:()=>handleMarkPaid(inv.id,{stopPropagation:()=>{}})},
              {label:"Edit", onClick:()=>openEdit(inv)},
              {label:"Delete", onClick:()=>armDelete(inv.id), danger:true},
            ];
            return (
              <Fragment key={inv.id}>
              {isMobile?(
                <div
                  onClick={()=>setExpandedInv(expandedInv===inv.id?null:inv.id)}
                  style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:16,marginBottom:12,position:"relative",cursor:"pointer",WebkitTapHighlightColor:"transparent"}}>
                  {deleteInvConfirmId===inv.id&&(
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",background:C.redBg,border:`1px solid ${C.redBd}`,borderRadius:7,marginBottom:10}}>
                      <span style={{fontFamily:F.body,fontSize:12,color:C.red}}>Delete "{inv.title}"?</span>
                      <div style={{display:"flex",gap:6}}>
                        <button onClick={e=>{e.stopPropagation();confirmDelete(inv);}} style={{background:C.red,color:"#fff",border:"none",borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Delete</button>
                        <button onClick={e=>{e.stopPropagation();cancelDelete();}} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
                      </div>
                    </div>
                  )}
                  <div style={{marginBottom:10}}>
                    <div style={{display:"flex",alignItems:"flex-start",gap:8,paddingRight:isAdvisor?44:0}}>
                      {isPaid?<CheckCircle2 size={18} color={C.charLight} style={{flexShrink:0,marginTop:2}}/>:<Clock size={18} color={C.charLight} style={{flexShrink:0,marginTop:2}}/>}
                      <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,fontWeight:600,lineHeight:1.25,overflowWrap:"anywhere",minWidth:0,flex:1}}>{inv.title}</div>
                    </div>
                    <div style={{marginTop:6}}>
                      <Badge label={isPaid?"Paid":"Payment Due"} type={isPaid?"paid":"unpaid"}/>
                    </div>
                    <div style={{textAlign:"right",fontFamily:F.mono,fontSize:10,color:C.charLight,marginTop:6}}>
                      {expandedInv===inv.id?"▲ Hide":"▼ Details"}
                    </div>
                  </div>
                  {expandedInv===inv.id&&(
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:0}}>
                      <div>
                        <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",marginBottom:2}}>Amount</div>
                        <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal}}>{fmtMoney(inv.amount)}</div>
                      </div>
                      <div>
                        <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",marginBottom:2}}>Issue Date</div>
                        {editingDateField?.invId===inv.id&&editingDateField?.field==='issue_date'?(
                          <>
                            <input type="date" value={editingDateValue} onClick={e=>e.stopPropagation()} onChange={e=>setEditingDateValue(e.target.value)} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 6px",fontFamily:F.mono,fontSize:11,color:C.charMid,width:"100%",boxSizing:"border-box"}}/>
                            <div style={{display:"flex",gap:4,marginTop:4}}>
                              <button onClick={e=>{e.stopPropagation();saveInvoiceDate(inv.id,'issue_date',editingDateValue);}} style={{background:C.navy,color:"#fff",border:"none",borderRadius:4,padding:"3px 8px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Save</button>
                              <button onClick={e=>{e.stopPropagation();setEditingDateField(null);}} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:4,padding:"3px 8px",fontFamily:F.body,fontSize:10,cursor:"pointer"}}>Cancel</button>
                            </div>
                          </>
                        ):(
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal}}>{fmtDate(inv.issue_date)}</div>
                            <button onClick={e=>{e.stopPropagation();setEditingDateField({invId:inv.id,field:'issue_date'});setEditingDateValue(inv.issue_date||"");}} style={{background:"none",border:"none",padding:2,cursor:"pointer",color:C.charLight,fontSize:11,lineHeight:1}} aria-label="Edit issue date">✎</button>
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",marginBottom:2}}>{isPaid?"Paid On":"Due Date"}</div>
                        {isPaid&&editingDateField?.invId===inv.id&&editingDateField?.field==='paid_date'?(
                          <>
                            <input type="date" value={editingDateValue} onClick={e=>e.stopPropagation()} onChange={e=>setEditingDateValue(e.target.value)} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 6px",fontFamily:F.mono,fontSize:11,color:C.charMid,width:"100%",boxSizing:"border-box"}}/>
                            <div style={{display:"flex",gap:4,marginTop:4}}>
                              <button onClick={e=>{e.stopPropagation();saveInvoiceDate(inv.id,'paid_date',editingDateValue);}} style={{background:C.navy,color:"#fff",border:"none",borderRadius:4,padding:"3px 8px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Save</button>
                              <button onClick={e=>{e.stopPropagation();setEditingDateField(null);}} style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:4,padding:"3px 8px",fontFamily:F.body,fontSize:10,cursor:"pointer"}}>Cancel</button>
                            </div>
                          </>
                        ):isPaid?(
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal}}>{fmtDate(inv.paid_date)}</div>
                            <button onClick={e=>{e.stopPropagation();setEditingDateField({invId:inv.id,field:'paid_date'});setEditingDateValue(inv.paid_date||"");}} style={{background:"none",border:"none",padding:2,cursor:"pointer",color:C.charLight,fontSize:11,lineHeight:1}} aria-label="Edit paid date">✎</button>
                          </div>
                        ):(
                          <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal}}>{fmtDate(inv.due_date)}</div>
                        )}
                      </div>
                    </div>
                  )}
                  {isAdvisor&&(
                    <div style={{position:"absolute",top:10,right:10}} onClick={e=>e.stopPropagation()}>
                      <OverflowMenu title={inv.title} subtitle="Invoice actions" items={kebabItems}/>
                    </div>
                  )}
                </div>
              ):(
                <div
                  onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
                  onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                  style={{...rowBaseStyle,display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:14}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                        {isPaid?<CheckCircle2 size={14} color={C.charLight}/>:<Clock size={14} color={C.charLight}/>}
                        <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500}}>{inv.title}</div>
                      </div>
                      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                        <Badge label={isPaid?"Paid":"Payment Due"} type={isPaid?"paid":"unpaid"}/>
                        <span style={{fontFamily:F.body,fontSize:10,color:C.charLight}}>Issued {fmtDate(inv.issue_date)}</span>
                        {isPaid&&inv.paid_date&&<span style={{fontFamily:F.body,fontSize:10,color:C.green}}>Paid {fmtDate(inv.paid_date)}</span>}
                        {!isPaid&&inv.due_date&&<span style={{fontFamily:F.body,fontSize:10,color:C.amber}}>Due {fmtDate(inv.due_date)}</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                      {deleteInvConfirmId===inv.id ? (
                        <>
                          <button onClick={()=>confirmDelete(inv)} style={{background:C.red,color:"#fff",border:"none",borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>
                            Click again to delete
                          </button>
                          <button onClick={cancelDelete} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={()=>setExpandedInv(expandedInv===inv.id?null:inv.id)} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>
                            {expandedInv===inv.id?"▲ Hide":"▼ Details"}
                          </button>
                          {inv.storage_path&&<button onClick={e=>openInvoiceFile(inv,e)} style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>📄 Open</button>}
                          {!isPaid&&isAdvisor&&<button onClick={e=>handleMarkPaid(inv.id,e)} disabled={marking===inv.id} style={{background:C.greenBg,color:C.green,border:`1px solid ${C.greenBd}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>{marking===inv.id?"…":"Mark Paid"}</button>}
                          {isPaid&&isAdvisor&&<button onClick={e=>handleMarkUnpaid(inv.id,e)} disabled={marking===inv.id} style={{background:C.amberBg,color:C.amber,border:`1px solid ${C.amberBd}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>{marking===inv.id?"…":"Mark Unpaid"}</button>}
                          {isAdvisor&&<button onClick={()=>openEdit(inv)} style={{background:C.surface,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Edit</button>}
                          {isAdvisor&&<button onClick={()=>armDelete(inv.id)} style={{background:C.surface,color:C.red,border:`1px solid ${C.redBd}`,borderRadius:6,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Delete</button>}
                        </>
                      )}
                    </div>
                  </div>
                  {expandedInv===inv.id&&(
                    <div style={{paddingTop:14,borderTop:`1px solid ${C.border}`,display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16}}>
                      <div><div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",marginBottom:3}}>Amount</div><div style={{fontFamily:F.display,fontSize:24,color:isPaid?C.green:C.charcoal,fontWeight:700}}>{fmtMoney(inv.amount)}</div></div>
                      <div><div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",marginBottom:3}}>Issue Date</div><div style={{fontFamily:F.body,fontSize:14,color:C.charcoal}}>{fmtDate(inv.issue_date)}</div></div>
                      <div><div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".06em",marginBottom:3}}>{inv.paid_date?"Paid On":"Due Date"}</div><div style={{fontFamily:F.body,fontSize:14,color:C.charcoal}}>{fmtDate(inv.paid_date||inv.due_date)}</div></div>
                    </div>
                  )}
                </div>
              )}
              {editingInvId === inv.id && renderInvoiceForm()}
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── RESOURCES TAB ─────────────────────────────────────────────
function ResourcesTab({client, isAdvisor, user, resources, setResources}) {
  const [showAdd,setShowAdd]=useState(false);
  const [newR,setNewR]=useState({title:"",description:"",type:"link",url:"",scope:"client",category:"Other"});
  const [resFile,setResFile]=useState(null);
  const [saving,setSaving]=useState(false);
  const [deleteConfirm,setDeleteConfirm]=useState(null);
  const [dragOver,setDragOver]=useState(false);
  const resFileRef=useRef(null);
  const [previewResource,setPreviewResource]=useState(null);
  const [previewUrl,setPreviewUrl]=useState(null);
  const [previewLoading,setPreviewLoading]=useState(false);
  const typeIcon={link:"🔗",doc:"📄",file:"📁",video:"🎥"};

  async function handleAdd(){
    if(!newR.title) return;
    setSaving(true);
    let url=newR.url||"#";
    if(resFile&&(newR.type==="file"||newR.type==="doc")){
      const path=`resources/${client.id}/${Date.now()}_${resFile.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const {data,error}=await supabase.storage.from('client-files').upload(path,resFile);
      if(!error){
        const {data:signed}=await supabase.storage.from('client-files').createSignedUrl(data.path,60*60*24*365);
        url=signed?.signedUrl||"#";
      }
    }
    const r=await addResource({...newR,url,client_id:newR.scope==="client"?client.id:null,created_by:user.id});
    setResources(p=>[...p,r]);
    setNewR({title:"",description:"",type:"link",url:"",scope:"client",category:"Other"});
    setResFile(null);setShowAdd(false);setSaving(false);
  }

  async function handleDelete(r){
    await deleteResource(r.id);
    setResources(p=>p.filter(res=>res.id!==r.id));
    setDeleteConfirm(null);
  }

  async function handlePreview(r){
    if(previewResource?.id===r.id){setPreviewResource(null);setPreviewUrl(null);return;}
    setPreviewResource(r);setPreviewUrl(null);
    if(r.url&&r.url!=="#"&&r.url!==""){
      // For YouTube, extract embed URL
      if(r.type==="video"||r.url.includes("youtube")||r.url.includes("youtu.be")){
        const match=r.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
        if(match) setPreviewUrl(`https://www.youtube.com/embed/${match[1]}`);
        else setPreviewUrl(r.url);
      } else if(r.url.includes("supabase")||r.type==="doc"||r.type==="file"){
        setPreviewLoading(true);
        // Try to get a fresh signed URL if it's a storage path
        setPreviewUrl(r.url);
        setPreviewLoading(false);
      } else {
        setPreviewUrl(r.url);
      }
    }
  }

  async function handleAdd(){
    if(!newR.title) return;
    setSaving(true);
    let url=newR.url||"#";
    // Upload file/doc if selected
    if(resFile&&(newR.type==="file"||newR.type==="doc")){
      const path=`resources/${client.id}/${Date.now()}_${resFile.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const {data,error}=await supabase.storage.from('client-files').upload(path,resFile);
      if(!error){
        const {data:signed}=await supabase.storage.from('client-files').createSignedUrl(data.path,60*60*24*365);
        url=signed?.signedUrl||"#";
      }
    }
    const r=await addResource({...newR,url,client_id:newR.scope==="client"?client.id:null,created_by:user.id});
    setResources(p=>[...p,r]);
    setNewR({title:"",description:"",type:"link",url:"",scope:"client",category:"Other"});
    setResFile(null);setShowAdd(false);setSaving(false);
  }

  async function handleDelete(r){
    await deleteResource(r.id);
    setResources(p=>p.filter(res=>res.id!==r.id));
    setDeleteConfirm(null);
  }

  const global=resources.filter(r=>r.scope==="global");
  const clientSpecific=resources.filter(r=>r.scope==="client");

  const ResourceCard=({r})=>{
    const isPreviewing=previewResource?.id===r.id;
    const isImage=r.url&&/\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/i.test(r.url);
    const isPdf=r.url&&/\.pdf(\?|$)/i.test(r.url);
    const isYoutube=r.type==="video"||(r.url&&(r.url.includes("youtube")||r.url.includes("youtu.be")));
    const canPreview=isImage||isPdf||isYoutube||(r.url&&r.url!=="#");
    return (
      <div>
        <div style={{display:"flex",alignItems:"flex-start",gap:10,padding:"12px 0",borderBottom:isPreviewing?`1px solid ${C.navyBorder}`:`1px solid ${C.border}`}}>
          <FileText size={14} color={C.charLight} style={{flexShrink:0,marginTop:3}}/>
          <div style={{flex:1}}>
            <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500,marginBottom:3}}>{r.title}</div>
            {r.description&&<div style={{fontFamily:F.body,fontSize:11,color:C.charMid,lineHeight:1.5,marginBottom:5}}>{r.description}</div>}
            <div style={{display:"flex",gap:7}}><Badge label={r.category} type="draft"/>{r.scope==="global"&&<Badge label="All Clients" type="global"/>}</div>
          </div>
          <div style={{display:"flex",gap:6,flexShrink:0,alignItems:"center"}}>
            {canPreview&&(
              <button onClick={()=>handlePreview(r)} style={{background:isPreviewing?C.navy:C.navyFaint,color:isPreviewing?"#fff":C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                {isPreviewing?"▲ Close":"▼ Preview"}
              </button>
            )}
            {r.url&&r.url!=="#"&&<a href={r.url} target="_blank" rel="noopener noreferrer" style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:6,padding:"5px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer",textDecoration:"none"}}>↗</a>}
            {isAdvisor&&(
              deleteConfirm===r.id?(
                <div style={{display:"flex",gap:5}}>
                  <button onClick={()=>handleDelete(r)} style={{background:C.red,color:"#fff",border:"none",borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>Delete</button>
                  <button onClick={()=>setDeleteConfirm(null)} style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 8px",fontFamily:F.body,fontSize:10,cursor:"pointer"}}>Cancel</button>
                </div>
              ):(
                <button onClick={()=>setDeleteConfirm(r.id)} style={{background:C.bgAlt,color:C.charLight,border:`1px solid ${C.border}`,borderRadius:5,width:26,height:26,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:12}} title="Delete">🗑</button>
              )
            )}
          </div>
        </div>
        {/* Inline preview */}
        {isPreviewing&&(
          <div style={{background:C.bgAlt,borderRadius:"0 0 8px 8px",overflow:"hidden",marginBottom:8}}>
            {previewLoading&&<div style={{padding:20,textAlign:"center",fontFamily:F.body,fontSize:12,color:C.charLight}}>Loading preview…</div>}
            {previewUrl&&!previewLoading&&(
              isYoutube?(
                <iframe src={previewUrl} width="100%" height="300" frameBorder="0" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" allowFullScreen style={{display:"block"}}/>
              ):isImage?(
                <img src={previewUrl} alt={r.title} style={{width:"100%",maxHeight:400,objectFit:"contain",display:"block"}}/>
              ):isPdf?(
                <iframe src={previewUrl} width="100%" height="500" style={{display:"block",border:"none"}}/>
              ):(
                <div style={{padding:"16px",textAlign:"center"}}>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginBottom:10}}>Preview not available for this file type.</div>
                  <a href={previewUrl} target="_blank" rel="noopener noreferrer" style={{background:C.navy,color:"#fff",borderRadius:7,padding:"8px 16px",fontFamily:F.body,fontSize:12,fontWeight:600,textDecoration:"none"}}>Open File →</a>
                </div>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <SectionHead title="Resources" sub="BB Integrated frameworks, guides, and client-specific materials."
        action={isAdvisor&&<Btn variant="primary" small onClick={()=>setShowAdd(p=>!p)}>+ Add Resource</Btn>}/>
      {showAdd&&isAdvisor&&(
        <Card style={{padding:"18px 22px",marginBottom:16}}>
          <div style={{fontFamily:F.display,fontSize:16,color:C.charcoal,marginBottom:14}}>Add Resource</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div><label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Title</label><input value={newR.title} onChange={e=>setNewR(p=>({...p,title:e.target.value}))} placeholder="Resource title" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/></div>
            <div><label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Category</label><input value={newR.category} onChange={e=>setNewR(p=>({...p,category:e.target.value}))} placeholder="e.g. Framework, Guide" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/></div>
          </div>
          <div style={{marginBottom:12}}><label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Description</label><input value={newR.description} onChange={e=>setNewR(p=>({...p,description:e.target.value}))} placeholder="Brief description" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/></div>
          <div style={{display:"flex",gap:10,marginBottom:12}}>
            <div style={{flex:1}}><label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Type</label>
              <select value={newR.type} onChange={e=>setNewR(p=>({...p,type:e.target.value,url:""}))} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13}}>
                {["link","video","doc","file"].map(t=><option key={t} value={t}>{t==="link"?"🔗 Link":t==="video"?"🎥 YouTube Video":t==="doc"?"📄 Document":"📁 File"}</option>)}
              </select>
            </div>
            <div style={{flex:1}}><label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Visibility</label>
              <select value={newR.scope} onChange={e=>setNewR(p=>({...p,scope:e.target.value}))} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13}}>
                <option value="client">This client only</option><option value="global">All clients</option>
              </select>
            </div>
          </div>
          {/* Type-specific input */}
          {(newR.type==="link")&&(
            <div style={{marginBottom:12}}><label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>URL</label><input value={newR.url} onChange={e=>setNewR(p=>({...p,url:e.target.value}))} placeholder="https://..." style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/></div>
          )}
          {newR.type==="video"&&(
            <div style={{marginBottom:12}}><label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>YouTube URL</label><input value={newR.url} onChange={e=>setNewR(p=>({...p,url:e.target.value}))} placeholder="https://youtube.com/watch?v=..." style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"9px 12px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/></div>
          )}
          {(newR.type==="doc"||newR.type==="file")&&(
            <div style={{marginBottom:12}}>
              <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Upload File</label>
              <div onDragOver={e=>{e.preventDefault();setDragOver(true);}} onDragLeave={()=>setDragOver(false)} onDrop={e=>{e.preventDefault();setDragOver(false);setResFile(e.dataTransfer.files[0]);}} onClick={()=>resFileRef.current?.click()} style={{border:`2px dashed ${dragOver?C.navy:C.border}`,borderRadius:8,padding:"20px",textAlign:"center",cursor:"pointer",background:dragOver?C.navyFaint:C.bgAlt}}>
                <input ref={resFileRef} type="file" style={{display:"none"}} onChange={e=>setResFile(e.target.files[0])}/>
                {resFile?<div style={{fontFamily:F.body,fontSize:13,color:C.navy,fontWeight:600}}>📄 {resFile.name}</div>:<div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>Drag & drop or click to browse</div>}
              </div>
            </div>
          )}
          <div style={{display:"flex",gap:8}}><Btn variant="primary" small onClick={handleAdd} disabled={!newR.title} loading={saving}>Add Resource</Btn><Btn variant="ghost" small onClick={()=>{setShowAdd(false);setResFile(null);}}>Cancel</Btn></div>
        </Card>
      )}
      {global.length>0&&<div><div style={{fontFamily:F.display,fontSize:16,color:C.charcoal,marginBottom:2}}>BB Integrated Resources</div><div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginBottom:12}}>Available to all clients</div>{global.map(r=><ResourceCard key={r.id} r={r}/>)}</div>}
      {clientSpecific.length>0&&<div style={{marginTop:14}}><div style={{fontFamily:F.display,fontSize:16,color:C.charcoal,marginBottom:2}}>Your Resources</div><div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginBottom:12}}>Materials specific to your engagement</div>{clientSpecific.map(r=><ResourceCard key={r.id} r={r}/>)}</div>}
      {resources.length===0&&<Card style={{padding:40,textAlign:"center"}}><div style={{fontFamily:F.display,fontSize:17,color:C.charMid,marginBottom:6}}>No resources yet</div><div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>{isAdvisor?"Add links, videos, documents, or files for this client.":"Your advisor will share resources and materials here."}</div></Card>}
    </div>
  );
}

// ── TIMELINE TAB ──────────────────────────────────────────────
function TimelineTab({timeline, isAdvisor, onDelete, client}) {
  const tc={session:{color:C.navy,bg:C.navyFaint,bd:C.navyBorder},milestone:{color:C.teal,bg:C.tealBg,bd:C.tealBd},insight:{color:C.purple,bg:C.purpleBg,bd:C.purpleBd},note:{color:C.slate||C.charMid,bg:C.bgAlt,bd:C.border}};
  const [showForm,setShowForm]=useState(false);
  const [newEntry,setNewEntry]=useState({title:"",date:todayLocal(),type:"session",body:""});
  const [saving,setSaving]=useState(false);
  const [entries,setEntries]=useState(timeline);
  const [editingId,setEditingId]=useState(null);
  const [editFields,setEditFields]=useState({});

  useEffect(()=>setEntries(timeline),[timeline]);

  async function saveEntry(){
    if(!newEntry.title.trim()) return;
    setSaving(true);
    try{
      const {data,error}=await supabase.from('timeline').insert({
        client_id:client.id,
        title:newEntry.title,
        date:newEntry.date,
        type:newEntry.type,
        body:newEntry.body,
      }).select().single();
      if(error) throw error;
      setEntries(p=>[data,...p].sort((a,b)=>new Date(b.date)-new Date(a.date)));
      setNewEntry({title:"",date:todayLocal(),type:"session",body:""});
      setShowForm(false);
    }catch(e){alert("Failed to save: "+e.message);}
    setSaving(false);
  }

  async function saveEdit(id){
    setSaving(true);
    try{
      const {data,error}=await supabase.from('timeline').update({
        title:editFields.title,
        date:editFields.date,
        type:editFields.type,
        body:editFields.body,
      }).eq('id',id).select().single();
      if(error) throw error;
      setEntries(p=>p.map(e=>e.id===id?data:e).sort((a,b)=>new Date(b.date)-new Date(a.date)));
      setEditingId(null);
    }catch(e){alert("Failed to save: "+e.message);}
    setSaving(false);
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:F.display,fontSize:22,color:C.charcoal,fontWeight:600,margin:0}}>Engagement Timeline</h2>
          <p style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginTop:5}}>Progressive record of every session, milestone, and insight.</p>
        </div>
        {isAdvisor&&<button onClick={()=>setShowForm(p=>!p)} style={{background:C.navy,color:"#fff",border:"none",borderRadius:7,padding:"7px 16px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>+ Add Entry</button>}
      </div>

      {/* Add Entry Form */}
      {showForm&&isAdvisor&&(
        <div style={{marginBottom:16}}>
          <div style={{fontFamily:F.display,fontSize:14,color:C.charcoal,marginBottom:12}}>New Timeline Entry</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Title</label>
              <input value={newEntry.title} onChange={e=>setNewEntry(p=>({...p,title:e.target.value}))} placeholder="Session title or milestone…"
                style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",fontFamily:F.body,fontSize:13,color:C.charcoal,boxSizing:"border-box"}}/>
            </div>
            <div>
              <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Date</label>
              <input type="date" value={newEntry.date} onChange={e=>setNewEntry(p=>({...p,date:e.target.value}))}
                style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",fontFamily:F.body,fontSize:13,color:C.charcoal,boxSizing:"border-box"}}/>
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Type</label>
            <div style={{display:"flex",gap:6}}>
              {["session","milestone","insight","note"].map(t=>(
                <button key={t} onClick={()=>setNewEntry(p=>({...p,type:t}))}
                  style={{background:newEntry.type===t?C.navy:C.bgAlt,color:newEntry.type===t?"#fff":C.charMid,border:`1px solid ${newEntry.type===t?C.navy:C.border}`,borderRadius:6,padding:"5px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Notes (optional)</label>
            <textarea value={newEntry.body} onChange={e=>setNewEntry(p=>({...p,body:e.target.value}))} rows={2} placeholder="Brief description…"
              style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:7,padding:"8px 11px",fontFamily:F.body,fontSize:13,color:C.charcoal,resize:"vertical",boxSizing:"border-box"}}/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn variant="primary" small onClick={saveEntry} loading={saving} disabled={!newEntry.title.trim()}>Save Entry</Btn>
            <Btn variant="ghost" small onClick={()=>setShowForm(false)}>Cancel</Btn>
          </div>
        </div>
      )}

      {entries.length===0?<Card style={{padding:40,textAlign:"center"}}><div style={{fontFamily:F.display,fontSize:17,color:C.charMid}}>Timeline builds as your engagement progresses.</div></Card>:(
        <div style={{position:"relative"}}>
          <div style={{position:"absolute",left:18,top:28,bottom:28,width:2,background:C.border,borderRadius:1}}/>
          {entries.map((t,i)=>{
            const cfg=tc[t.type]||tc.session;
            const isEditing=editingId===t.id;
            return (
              <div key={t.id||i} style={{display:"flex",gap:18,position:"relative"}}>
                <div style={{width:38,flexShrink:0,display:"flex",justifyContent:"center",paddingTop:22}}>
                  <div style={{width:13,height:13,borderRadius:"50%",background:cfg.color,border:`3px solid ${C.bg||"#F6F7FA"}`,zIndex:1,boxShadow:`0 0 0 2px ${cfg.color}40`}}/>
                </div>
                <div style={{flex:1,padding:"22px 0",borderBottom:i===entries.length-1?"none":`1px solid ${C.border}`}}>
                  {isEditing?(
                    // Edit mode
                    <div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                        <input value={editFields.title} onChange={e=>setEditFields(p=>({...p,title:e.target.value}))}
                          style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",fontFamily:F.body,fontSize:13,color:C.charcoal,boxSizing:"border-box",width:"100%"}}/>
                        <input type="date" value={editFields.date} onChange={e=>setEditFields(p=>({...p,date:e.target.value}))}
                          style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",fontFamily:F.body,fontSize:13,color:C.charcoal,boxSizing:"border-box",width:"100%"}}/>
                      </div>
                      <div style={{display:"flex",gap:5,marginBottom:8}}>
                        {["session","milestone","insight","note"].map(tp=>(
                          <button key={tp} onClick={()=>setEditFields(p=>({...p,type:tp}))}
                            style={{background:editFields.type===tp?C.navy:C.bgAlt,color:editFields.type===tp?"#fff":C.charMid,border:`1px solid ${editFields.type===tp?C.navy:C.border}`,borderRadius:5,padding:"3px 9px",fontFamily:F.body,fontSize:10,fontWeight:600,cursor:"pointer"}}>
                            {tp}
                          </button>
                        ))}
                      </div>
                      <textarea value={editFields.body} onChange={e=>setEditFields(p=>({...p,body:e.target.value}))} rows={2}
                        style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 10px",fontFamily:F.body,fontSize:12,color:C.charcoal,resize:"vertical",boxSizing:"border-box",marginBottom:8}}/>
                      <div style={{display:"flex",gap:6}}>
                        <Btn variant="primary" small onClick={()=>saveEdit(t.id)} loading={saving}>Save</Btn>
                        <Btn variant="ghost" small onClick={()=>setEditingId(null)}>Cancel</Btn>
                      </div>
                    </div>
                  ):(
                    // View mode
                    <>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                        <div style={{display:"flex",gap:7,alignItems:"center"}}>
                          <span style={{background:cfg.bg,color:cfg.color,border:`1px solid ${cfg.bd}`,borderRadius:4,padding:"2px 7px",fontSize:9,fontWeight:700,fontFamily:F.body,textTransform:"uppercase"}}>{t.type}</span>
                          <span style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600}}>{t.title}</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,marginLeft:8}}>
                          <span style={{fontFamily:F.body,fontSize:9,color:C.charLight}}>{fmtDate(t.date)}</span>
                          {isAdvisor&&t.id&&(<>
                            <button onClick={()=>{setEditingId(t.id);setEditFields({title:t.title,date:t.date,type:t.type||"session",body:t.body||""});}}
                              style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:5,width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:10}}>✏️</button>
                            <button onClick={async()=>{if(window.confirm("Delete this timeline entry?")){await supabase.from('timeline').delete().eq('id',t.id);setEntries(p=>p.filter(e=>e.id!==t.id));onDelete&&onDelete(t.id);}}}
                              style={{background:C.redBg,color:C.red,border:`1px solid ${C.redBd}`,borderRadius:5,width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:11}}>🗑</button>
                          </>)}
                        </div>
                      </div>
                      {t.body&&<div style={{fontFamily:F.body,fontSize:12,color:C.charMid,lineHeight:1.6}}>{t.body}</div>}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
// ── MESSAGES TAB ──────────────────────────────────────────────
function MessagesTab({client, isAdvisor, user, advisorId, onUpgradeRequest}) {
  const [msgs,setMsgs]=useState([]);
  const [input,setInput]=useState("");
  const [loading,setLoading]=useState(true);
  const [aiResult,setAiResult]=useState(null);
  const [analyzing,setAnalyzing]=useState(false);
  const [selItems,setSelItems]=useState([]);
  const bottomRef=useRef(null);

  useEffect(()=>{
    getMessages(client.id).then(data=>{setMsgs(data||[]);setLoading(false);});
    const sub=subscribeToMessages(client.id,msg=>setMsgs(p=>[...p,msg]));
    return ()=>sub.unsubscribe?.();
  },[client.id]);

  useEffect(()=>{bottomRef.current?.scrollIntoView({behavior:"smooth"});},[msgs]);

  async function send(){
    if(!input.trim()) return;
    const role=isAdvisor?"advisor":"client";
    const msg=await sendMessage(client.id,role,user.id,input);
    setMsgs(p=>[...p,msg]);
    setInput("");setAiResult(null);
  }

  async function analyze(){
    if(advisorId){
      const u=await getUsageRecord(advisorId);
      if((u?.ai_messages||0)>=USAGE_LIMITS.ai_messages.free){onUpgradeRequest&&onUpgradeRequest("ai_messages");return;}
      await incrementUsage(advisorId,"ai_messages");
    }
    if(msgs.length<2) return;
    setAnalyzing(true);setAiResult(null);
    const convo=msgs.slice(-30).map(m=>`${m.from_role==="advisor"?"Advisor":client.user?.name||"Client"}: ${m.text}`).join("\n");
    const sys=`You are a BB Integrated business consultant AI. Carefully read this conversation between an advisor and a client. Write a specific, accurate summary of what was actually discussed. Then identify concrete action items mentioned or implied. Return only valid JSON with:
"summary": (2-3 sentences accurately describing what was actually discussed in this specific conversation, not generic filler),
"actionItems": (array of up to 5 objects each with "text" describing a specific action item from the conversation, and "type" of "project" or "note").
No markdown. Base everything strictly on the actual conversation content provided.`;
    const raw=await callClaude(sys,`Client: ${client.user?.name||'client'} at ${client.company||'their business'}\n\nConversation:\n${convo}`);
    try{
      const parsed=JSON.parse(raw.replace(/```json|```/g,"").trim());
      setAiResult(parsed);
    }catch{
      // Even fallback uses actual message content
      const lastMessages=msgs.slice(-5).map(m=>m.text).join(" | ");
      setAiResult({
        summary:`The conversation covered: ${lastMessages.slice(0,200)}...`,
        actionItems:[{text:"Review key points from this conversation with advisor",type:"note"}]
      });
    }
    setAnalyzing(false);
  }

  const initials=(client.user?.name||"CL").split(" ").map(w=>w[0]).join("").slice(0,2);

  return (
    <div>
      <SectionHead title="Messages" sub={isAdvisor?`Direct conversation with ${client.user?.name||"client"}`:"Direct conversation with your BB Integrated advisor"}/>
      {loading?<Spinner/>:(
        <>
          <Card style={{display:"flex",flexDirection:"column",height:440,padding:0,overflow:"hidden",marginBottom:aiResult?14:0}}>
            <div style={{flex:1,overflowY:"auto",padding:"14px 18px",display:"flex",flexDirection:"column",gap:9}}>
              {msgs.length===0&&<div style={{textAlign:"center",padding:40,fontFamily:F.body,fontSize:13,color:C.charLight}}>No messages yet. Start the conversation.</div>}
              {msgs.map((m,i)=>{
                const isMe=isAdvisor?m.from_role==="advisor":m.from_role==="client";
                const fromAdvisor=m.from_role==="advisor";
                return (
                  <div key={m.id||i} style={{display:"flex",flexDirection:isMe?"row-reverse":"row",gap:9,alignItems:"flex-end"}}>
                    <div style={{width:30,height:30,borderRadius:"50%",background:fromAdvisor?C.navy:C.bgAlt,border:`1px solid ${fromAdvisor?C.navy:C.border}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <span style={{fontFamily:F.mono,fontSize:8,color:fromAdvisor?"#fff":C.charMid,fontWeight:600}}>{fromAdvisor?"BBI":initials}</span>
                    </div>
                    <div style={{maxWidth:"68%"}}>
                      <div style={{background:isMe?C.navy:C.bgAlt,color:isMe?"#fff":C.charcoal,border:`1px solid ${isMe?C.navy:C.border}`,borderRadius:isMe?"11px 11px 2px 11px":"11px 11px 11px 2px",padding:"9px 13px",fontFamily:F.body,fontSize:13,lineHeight:1.55}}>{m.text}</div>
                      <div style={{fontFamily:F.mono,fontSize:8,color:C.charLight,marginTop:2,textAlign:isMe?"right":"left"}}>{fmtTime(m.created_at)}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef}/>
            </div>
            <div style={{borderTop:`1px solid ${C.border}`,padding:"10px 14px",display:"flex",gap:8,alignItems:"flex-end",background:C.bgAlt}}>
              <textarea value={input} onChange={e=>setInput(e.target.value)} placeholder={isAdvisor?`Message ${client.user?.name?.split(" ")[0]||"client"}…`:"Message your advisor…"} rows={2}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
                style={{flex:1,background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 11px",color:C.charcoal,fontFamily:F.body,fontSize:13,resize:"none",boxSizing:"border-box"}}/>
              <div style={{display:"flex",flexDirection:"column",gap:5}}>
                <button onClick={send} style={{background:C.navy,color:"#fff",border:"none",borderRadius:6,padding:"7px 13px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer"}}>Send</button>
                <button onClick={analyze} disabled={analyzing||msgs.length<2} style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"4px 9px",fontFamily:F.body,fontSize:9,fontWeight:600,cursor:"pointer",opacity:msgs.length<2?.4:1}}>
                  {analyzing?"…":"AI Review"}
                </button>
              </div>
            </div>
          </Card>
          {aiResult&&(
            <Card style={{padding:"16px 20px"}}>
              <div style={{fontFamily:F.display,fontSize:15,color:C.charcoal,marginBottom:12}}>AI Conversation Review</div>
              <div style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"11px 13px",marginBottom:12}}>
                <div style={{fontFamily:F.mono,fontSize:8,color:C.navy,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>Summary</div>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.7}}>{aiResult.summary}</div>
              </div>
              {aiResult.actionItems?.length>0&&(
                <>
                  <div style={{fontFamily:F.body,fontSize:10,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Spotted Action Items</div>
                  {aiResult.actionItems.map((item,i)=>{
                    const on=selItems.includes(i);
                    return (
                      <div key={i} onClick={()=>setSelItems(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i])} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:i<aiResult.actionItems.length-1?`1px solid ${C.border}`:"none",cursor:"pointer"}}>
                        <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${on?C.navy:C.border}`,background:on?C.navyFaint:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                          {on&&<span style={{color:C.navy,fontSize:10}}>✓</span>}
                        </div>
                        <div style={{flex:1,fontFamily:F.body,fontSize:13,color:C.charcoal}}>{item.text}</div>
                        <Badge label={item.type} type={item.type==="project"?"active":"pending"}/>
                      </div>
                    );
                  })}
                  {selItems.length>0&&(
                    <div style={{marginTop:12,display:"flex",justifyContent:"flex-end"}}>
                      <Btn variant="primary" small onClick={async()=>{
                        for(const i of selItems){
                          const item=aiResult.actionItems[i];
                          await addTimelineEntry(client.id,{
                            type:"milestone",
                            title:`Action: ${item.text}`,
                            body:`Extracted from message conversation AI review.`,
                            date:todayLocal(),
                          });
                        }
                        setSelItems([]);setAiResult(null);
                        alert(`${selItems.length} item${selItems.length>1?"s":""} added to the Timeline.`);
                      }}>Add {selItems.length} item{selItems.length>1?"s":""} to Timeline</Btn>
                    </div>
                  )}
                </>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ── SESSIONS TAB ──────────────────────────────────────────────
function SessionsTab({client, user, sessions, setSessions, advisorId, onUpgradeRequest}) {
  const isMobile=useIsMobile();
  const [stage,setStage]=useState("idle");
  const [transcript,setTranscript]=useState("");
  const [sessionTitle,setSessionTitle]=useState("");
  const [aiResult,setAiResult]=useState(null);
  const [selActions,setSelActions]=useState([]);
  const [selProjects,setSelProjects]=useState([]);
  const [saving,setSaving]=useState(false);

  async function processTranscript(){
    if(!transcript.trim()) return;
    if(advisorId){
      const u=await getUsageRecord(advisorId);
      if((u?.ai_sessions||0)>=USAGE_LIMITS.ai_sessions.free){onUpgradeRequest&&onUpgradeRequest("ai_sessions");return;}
      await incrementUsage(advisorId,"ai_sessions");
    }
    setStage("processing");
    const sys=`BB Integrated consultant AI. Analyze a session transcript. Return only valid JSON with "summary" (3-4 sentences), "decisions" (array of up to 4 strings), "actionItems" (array of up to 8 objects with "text", "owner" of "advisor" or "client", "priority" of "high","medium","low"), "projectSuggestions" (array of up to 4 objects with "name" and "description"). No markdown.`;
    const raw=await callClaude(sys,`Session: ${sessionTitle||"Untitled"}\nClient: ${client.user?.name||"client"}\nCompany: ${client.company||""}\n\nTranscript:\n${transcript.slice(0,3000)}`);
    try{setAiResult(JSON.parse(raw.replace(/```json|```/g,"").trim()));}
    catch{setAiResult({summary:`Session with ${client.user?.name||"client"} covered operational priorities and next steps.`,decisions:["Proceed with current project timeline","Schedule follow-up session"],actionItems:[{text:"Send updated summary to client",owner:"advisor",priority:"high"}],projectSuggestions:[]});}
    setStage("review");
  }

  async function saveSession(){
    setSaving(true);
    const title=sessionTitle||`Session — ${new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}`;
    const session=await createSession({client_id:client.id,advisor_id:user.id,title,date:todayLocal(),summary:aiResult.summary,decisions:aiResult.decisions||[],transcript_excerpt:transcript.slice(0,500)+"…"});
    const selectedItems=[
      ...aiResult.actionItems?.filter((_,i)=>selActions.includes(i))||[],
    ].map(a=>({session_id:session.id,client_id:client.id,text:a.text,type:"task",owner:a.owner,priority:a.priority}));
    const selectedProjects=aiResult.projectSuggestions?.filter((_,i)=>selProjects.includes(i))||[];
    selectedProjects.forEach(p=>selectedItems.push({session_id:session.id,client_id:client.id,text:p.name,type:"project",priority:"medium",owner:"advisor"}));
    if(selectedItems.length>0) await addSessionItems(selectedItems);
    await addTimelineEntry(client.id,{type:"session",title,body:aiResult.summary,date:todayLocal()});
    setSessions(p=>[{...session,items:selectedItems},...p]);
    setSaving(false);setStage("done");
    setTimeout(()=>{setStage("idle");setTranscript("");setSessionTitle("");setAiResult(null);setSelActions([]);setSelProjects([]);},2000);
  }

  const priorityColor={high:C.red,medium:C.amber,low:C.green};

  return (
    <div>
      <SectionHead title="Session Recording" sub="Paste a session transcript — AI extracts tasks, decisions, and projects automatically."/>
      {stage==="idle"&&(
        <div style={{marginBottom:20}}>
          <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>Previous Sessions</div>
          {sessions.length===0?(
            <div style={{borderTop:`1px solid ${C.border}`,borderBottom:`1px solid ${C.border}`,padding:"22px 18px",textAlign:"center"}}>
              <div style={{fontFamily:F.display,fontSize:17,color:C.charMid,marginBottom:6}}>No sessions yet</div>
              <div style={{fontFamily:F.body,fontSize:13,color:C.charLight}}>Past sessions will appear here once you process a transcript below.</div>
            </div>
          ):sessions.map((s,i)=>{
            const isFirst=i===0;
            const isLast=i===sessions.length-1;
            const decisionsCount=(s.decisions||[]).length;
            const rowBaseStyle={
              padding:"22px 18px",
              borderTop:isFirst?`1px solid ${C.border}`:"none",
              borderBottom:isLast?"none":`1px solid ${C.border}`,
              transition:"background .15s",
            };

            // Mobile — stacked
            if(isMobile){
              return (
                <div key={s.id||i}
                  onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
                  onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                  style={{...rowBaseStyle,display:"flex",flexDirection:"column"}}>
                  {/* 1. Title */}
                  <div style={{fontFamily:F.body,fontSize:14,color:C.charcoal,fontWeight:500,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{s.title}</div>
                  {/* 2. Meta row */}
                  <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",marginTop:10}}>
                    {decisionsCount>0&&<span style={{fontFamily:F.body,fontSize:10,color:C.teal,background:C.tealBg,border:`1px solid ${C.tealBd}`,borderRadius:4,padding:"2px 8px",fontWeight:600}}>{decisionsCount} decisions</span>}
                    <span style={{fontFamily:F.body,fontSize:10,color:C.charLight}}>{fmtDate(s.date)}</span>
                  </div>
                  {/* 3. Summary */}
                  {s.summary&&(
                    <div style={{fontFamily:F.body,fontSize:11,color:C.navyMid,marginTop:10,lineHeight:1.5,fontStyle:"italic"}}>{s.summary}</div>
                  )}
                </div>
              );
            }

            // Desktop — main row + summary sub-line
            return (
              <div key={s.id||i}
                onMouseEnter={e=>{e.currentTarget.style.background=C.bgAlt;}}
                onMouseLeave={e=>{e.currentTarget.style.background="transparent";}}
                style={{...rowBaseStyle,display:"flex",flexDirection:"column",gap:6}}>
                {/* Main row */}
                <div style={{display:"flex",alignItems:"center",gap:14}}>
                  <div style={{flex:1,minWidth:0,fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.title}</div>
                  {decisionsCount>0&&(
                    <span style={{fontFamily:F.body,fontSize:10,color:C.teal,background:C.tealBg,border:`1px solid ${C.tealBd}`,borderRadius:4,padding:"2px 8px",fontWeight:600,flexShrink:0}}>{decisionsCount} decisions</span>
                  )}
                  <span style={{fontFamily:F.body,fontSize:11,color:C.charLight,minWidth:100,textAlign:"right",flexShrink:0}}>{fmtDate(s.date)}</span>
                </div>
                {/* Optional sub-line: summary */}
                {s.summary&&(
                  <div style={{fontFamily:F.body,fontSize:11,color:C.navyMid,lineHeight:1.5,fontStyle:"italic"}}>{s.summary}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {stage==="idle"&&(
        <Card style={{padding:"28px 32px",textAlign:"center",border:`2px dashed ${C.border}`}}>
          <div style={{fontSize:40,marginBottom:14}}>🎙️</div>
          <div style={{fontFamily:F.display,fontSize:20,color:C.charcoal,marginBottom:6}}>Process a Session Recording</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charMid,lineHeight:1.7,marginBottom:24,maxWidth:480,margin:"0 auto 24px"}}>Record your session using any tool, then paste the transcript here. AI will extract action items, decisions, and project ideas automatically.</div>
          <Btn variant="primary" onClick={()=>setStage("paste")}>+ New Session Transcript</Btn>
        </Card>
      )}

      {stage==="paste"&&(
        <Card style={{padding:"22px 24px"}}>
          <div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,marginBottom:16}}>New Session Transcript</div>
          <div style={{marginBottom:14}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Session Title</label>
            <input value={sessionTitle} onChange={e=>setSessionTitle(e.target.value)} placeholder={`Session with ${client.user?.name?.split(" ")[0]||"client"} — ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}`} style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 13px",color:C.charcoal,fontFamily:F.body,fontSize:13,boxSizing:"border-box"}}/>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{fontFamily:F.body,fontSize:10,color:C.charMid,display:"block",marginBottom:5,fontWeight:600,textTransform:"uppercase",letterSpacing:".06em"}}>Paste Transcript</label>
            <textarea value={transcript} onChange={e=>setTranscript(e.target.value)} rows={10} placeholder="Paste your session transcript here…" style={{width:"100%",background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:8,padding:"11px 13px",color:C.charcoal,fontFamily:F.body,fontSize:13,lineHeight:1.7,resize:"vertical",boxSizing:"border-box"}}/>
            <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginTop:6}}>{transcript.length} characters</div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn variant="primary" onClick={processTranscript} disabled={transcript.length<50}>Analyze with AI →</Btn>
            <Btn variant="ghost" onClick={()=>{setStage("idle");setTranscript("");setSessionTitle("");}}>Cancel</Btn>
          </div>
        </Card>
      )}

      {stage==="processing"&&(
        <Card style={{padding:"48px 32px",textAlign:"center"}}>
          <div style={{fontFamily:F.mono,fontSize:32,color:C.navy,marginBottom:16,animation:"pulse 1.5s infinite"}}>◈</div>
          <div style={{fontFamily:F.display,fontSize:20,color:C.charcoal,marginBottom:6}}>AI is analyzing your session…</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charMid}}>Extracting decisions, action items, and project ideas.</div>
        </Card>
      )}

      {stage==="review"&&aiResult&&(
        <Card style={{padding:"22px 24px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
            <div><div style={{fontFamily:F.display,fontSize:17,color:C.charcoal,marginBottom:3}}>{sessionTitle||"Session Analysis"}</div><div style={{fontFamily:F.body,fontSize:12,color:C.charMid}}>Review what AI found — select items to save.</div></div>
            <span style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:6,padding:"3px 10px",fontFamily:F.mono,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>AI Result</span>
          </div>
          <div style={{background:C.navyFaint,border:`1px solid ${C.navyBorder}`,borderRadius:8,padding:"14px 16px",marginBottom:16}}>
            <div style={{fontFamily:F.mono,fontSize:8,color:C.navyMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:5}}>Session Summary</div>
            <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.75}}>{aiResult.summary}</div>
          </div>
          {aiResult.decisions?.length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{fontFamily:F.body,fontSize:10,color:C.teal,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Decisions Made</div>
              {aiResult.decisions.map((d,i)=>(
                <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"7px 0",borderBottom:`1px solid ${C.border}`}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:C.teal,marginTop:5,flexShrink:0}}/>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,lineHeight:1.5}}>{d}</div>
                </div>
              ))}
            </div>
          )}
          {aiResult.actionItems?.length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{fontFamily:F.body,fontSize:10,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Action Items — Select to Add</div>
              {aiResult.actionItems.map((item,i)=>{
                const on=selActions.includes(i);
                return (
                  <div key={i} onClick={()=>setSelActions(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i])} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
                    <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${on?C.navy:C.border}`,background:on?C.navyFaint:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
                      {on&&<span style={{color:C.navy,fontSize:11}}>✓</span>}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,marginBottom:2}}>{item.text}</div>
                      <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight}}>Owner: {item.owner==="advisor"?"Advisor":client.user?.name?.split(" ")[0]||"Client"}</div>
                    </div>
                    <span style={{background:priorityColor[item.priority]+"15",color:priorityColor[item.priority],border:`1px solid ${priorityColor[item.priority]}33`,borderRadius:4,padding:"2px 8px",fontSize:9,fontWeight:700,fontFamily:F.body,textTransform:"uppercase",flexShrink:0}}>{item.priority}</span>
                  </div>
                );
              })}
            </div>
          )}
          {aiResult.projectSuggestions?.length>0&&(
            <div style={{marginBottom:18}}>
              <div style={{fontFamily:F.body,fontSize:10,color:C.charMid,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Project Ideas — Select to Create</div>
              {aiResult.projectSuggestions.map((proj,i)=>{
                const on=selProjects.includes(i);
                return (
                  <div key={i} onClick={()=>setSelProjects(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i])} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"11px 0",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
                    <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${on?C.green:C.border}`,background:on?C.greenBg:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2}}>
                      {on&&<span style={{color:C.green,fontSize:11}}>✓</span>}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500,marginBottom:3}}>{proj.name}</div>
                      <div style={{fontFamily:F.body,fontSize:12,color:C.charMid,lineHeight:1.5}}>{proj.description}</div>
                    </div>
                    <Badge label="New Project" type="active"/>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            <Btn variant="primary" onClick={saveSession} loading={saving}>Save Session{(selActions.length+selProjects.length)>0?` + ${selActions.length+selProjects.length} Item${(selActions.length+selProjects.length)>1?"s":""}`:""} to Portal</Btn>
            <Btn variant="ghost" small onClick={()=>setStage("paste")}>← Edit Transcript</Btn>
          </div>
        </Card>
      )}

      {stage==="done"&&(
        <Card style={{padding:"48px 32px",textAlign:"center"}}>
          <div style={{fontSize:40,marginBottom:14}}>✅</div>
          <div style={{fontFamily:F.display,fontSize:20,color:C.charcoal,marginBottom:6}}>Session saved successfully</div>
          <div style={{fontFamily:F.body,fontSize:13,color:C.charMid}}>Action items and projects have been added to the portal.</div>
        </Card>
      )}
    </div>
  );
}

// ── MAIN PORTAL COMPONENT ─────────────────────────────────────
export default function Portal({client, isAdvisor, user, isImpersonating=false, onLogout, onBack, advisorId, operatorRole=null, onUpgradeRequest, banner=null, pageAccess=null, initialChromeSection=null}) {
  const [tab,setTab]=useState("Overview");
  const [subView,setSubView]=useState(null);
  const [chromeSection,setChromeSection]=useState(initialChromeSection); // null | 'inbox' | 'settings' | 'admin'
  const [loading,setLoading]=useState(true);
  const [showCategoriesModal,setShowCategoriesModal]=useState(false);
  const [selectedCats,setSelectedCats]=useState(client.selected_categories||[]);
  const [savingCats,setSavingCats]=useState(false);
  const [notifications,setNotifications]=useState([]);
  const [directProjectId,setDirectProjectId]=useState(null);
  const [hiddenTabs,setHiddenTabs]=useState(client.hidden_tabs||[]);
  const [aiAnalystState,setAiAnalystState]=useState({docFile:null,docText:"",messages:[],input:"",usageCount:0});
  const [compassState,setCompassState]=useState({});
  const [extraBreadcrumb,setExtraBreadcrumb]=useState([]);
  const [stickyHeader,setStickyHeader]=useState(null);
  // Next Session state — lifted from OverviewTab so the breadcrumb header
  // (rendered by Portal, not OverviewTab) can read and edit it.
  const [nextSession,setNextSession]=useState(client.next_session_date||"");
  const [savingSession,setSavingSession]=useState(false);
  const [isEditingNextSessionHeader,setIsEditingNextSessionHeader]=useState(false);
  const [nextSessionDraftHeader,setNextSessionDraftHeader]=useState("");
  async function saveNextSession(date){
    setSavingSession(true);
    await updateClient(client.id,{next_session_date:date||null});
    client.next_session_date=date;
    setSavingSession(false);
  }
  const isMobile=useIsMobile();

  // Build notifications from timeline on load. Spread the row first so the
  // Inbox classifier can read type/title for badge + routing decisions.
  function buildNotifications(tl){
    const recent=(tl||[]).slice(0,5).map(t=>({
      ...t,
      text:t.title,
      created_at:t.date,
      read:false,
    }));
    setNotifications(recent);
  }

  function clearNotifications(){
    setNotifications(p=>p.map(n=>({...n,read:true})));
  }

  // Live data state
  const [projects,setProjects]=useState([]);
  const [files,setFiles]=useState([]);
  const [documents,setDocuments]=useState([]);
  const [invoices,setInvoices]=useState([]);
  const [resources,setResources]=useState([]);
  const [sessions,setSessions]=useState([]);
  const [checklist,setChecklist]=useState([]);
  const [timeline,setTimeline]=useState([]);
  const [assessmentData,setAssessmentData]=useState([]);

  // Keep Inbox notifications in sync with timeline state — re-runs whenever
  // timeline changes (e.g., after doc create/sign fires a timeline event).
  useEffect(()=>{buildNotifications(timeline);},[timeline]); // eslint-disable-line react-hooks/exhaustive-deps

  const ALL_TABS=["Overview","Projects","Files","Docs","Team Members","Billing","Resources","Timeline","Messages","Sessions"];
  // PREVIEW_TABS use inverted semantics: HIDDEN by default, visible only when
  // the master has added them to hidden_tabs (the same column reused — no new
  // schema needed). For a regular tab, presence in hidden_tabs = hidden;
  // for a preview tab, presence = enabled.
  const TABS=(user.role==="master"||isAdvisor)?ALL_TABS
    :user.role==="team_member"?ALL_TABS.filter(t=>{
      if(t==="Overview")return true;
      if(["Team Members","Timeline","Messages","Sessions"].includes(t))return false;
      const key=t==="Billing"?"billing":t.toLowerCase();
      return (pageAccess||[]).includes(key);
    })
    :ALL_TABS.filter(t=>{
      const inList=hiddenTabs.includes(t);
      return PREVIEW_TABS.includes(t)?inList:!inList;
    });

  // Load all client data from Supabase on mount
  useEffect(()=>{
    async function loadAll(){
      setLoading(true);
      try{
        const [proj,fil,doc,inv,res,ses,chk,tl,asmnt]=await Promise.all([
          getProjects(client.id),
          getFiles(client.id),
          getDocuments(client.id),
          getInvoices(client.id),
          getResources(client.id),
          getSessions(client.id),
          getChecklist(client.id),
          getTimeline(client.id),
          getAssessment(client.id),
        ]);
        // Fetch checklist progress for all projects and merge in
        const progressMap=await getClientCheckslistProgress(client.id).catch(()=>({}));
        const projWithProgress=(proj||[]).map(p=>({...p,checklist_progress:progressMap[p.id]??p.checklist_progress??p.progress??0}));
        setProjects(projWithProgress);
        setFiles(fil||[]);
        setDocuments(doc||[]);
        setInvoices(inv||[]);
        setResources(res||[]);
        setSessions(ses||[]);
        setChecklist(chk||[]);
        setTimeline(tl||[]);
        setAssessmentData(asmnt||[]);
        buildNotifications(tl||[]);
      }catch(e){console.error("Failed to load portal data:",e);}
      setLoading(false);
    }
    loadAll();
  },[client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleAddProject(proj){
    const p=await createProjectFromThought(client.id, proj.name, proj.description, {
      phase: proj.phase, risk: proj.risk, deadline: proj.deadline,
    });
    setProjects(prev=>[p,...prev]);
  }

  function handleNavigate(destination, projectId=null){
    if(destination==="_categories"){ setShowCategoriesModal(true); return; }
    if(typeof destination==="function"){ destination(); return; }
    if(destination==="inbox"||destination==="settings"||destination==="admin"){
      setChromeSection(destination);
      setSubView(null);
      return;
    }
    setChromeSection(null);
    setSubView(null);
    setTab(destination);
    if(projectId) setDirectProjectId(projectId);
  }

  async function saveCategorySelections(){
    setSavingCats(true);
    await updateClient(client.id,{selected_categories:selectedCats});
    client.selected_categories=selectedCats; // update local ref
    setShowCategoriesModal(false);
    setSavingCats(false);
  }

  // Categories modal
  const CategoriesModal = ()=>(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:C.surface,borderRadius:14,padding:"26px 28px",width:"100%",maxWidth:560,boxShadow:"0 20px 60px rgba(0,0,0,.25)"}}>
        <div style={{fontFamily:F.display,fontSize:20,color:C.charcoal,fontWeight:600,marginBottom:4}}>Update Your Support Needs</div>
        <div style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginBottom:20}}>Select all areas where you'd like support.</div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
          {CATEGORIES.map(cat=>{
            const on=selectedCats.includes(cat.id);
            return (
              <div key={cat.id} onClick={()=>setSelectedCats(p=>p.includes(cat.id)?p.filter(x=>x!==cat.id):[...p,cat.id])}
                style={{display:"flex",alignItems:"center",gap:12,padding:"11px 14px",borderRadius:9,border:`2px solid ${on?cat.color+"60":C.border}`,background:on?cat.color+"08":C.surface,cursor:"pointer",transition:"all .15s"}}>
                <div style={{width:20,height:20,borderRadius:5,border:`2px solid ${on?cat.color:C.border}`,background:on?cat.color:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {on&&<span style={{color:"#fff",fontSize:11}}>✓</span>}
                </div>
                <span style={{fontSize:16,color:cat.color}}>{cat.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:500}}>{cat.label}</div>
                  <div style={{fontFamily:F.body,fontSize:11,color:C.charMid}}>{cat.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn variant="primary" onClick={saveCategorySelections} loading={savingCats} disabled={selectedCats.length===0}>Save Selections</Btn>
          <Btn variant="ghost" onClick={()=>setShowCategoriesModal(false)}>Cancel</Btn>
        </div>
      </div>
    </div>
  );

  // Sidebar icon map — keyed by tab name
  const SIDEBAR_ICON_BY_TAB = {
    Overview: LayoutDashboard,
    Projects: FolderKanban,
    Files: Folder,
    Docs: FileText,
    "Team Members": UserPlus,
    Billing: Receipt,
    Resources: Library,
    Timeline: Clock,
    Messages: MessageSquare,
    Sessions: Mic,
  };
  const sidebarItems = [
    ...TABS.map(t => ({ id: t, label: t, icon: SIDEBAR_ICON_BY_TAB[t] })),
    ...(COMPASS_ENABLED && isAdvisor && isPilot0Context(advisorId, client?.id) ? [{ id:"Compass", label:"Compass", icon:Compass }] : []),
  ];

  const currentSection = chromeSection || tab;
  const unreadCount = notifications.filter(n => !n.read).length;
  const roleLabel = user.role==="master" ? "Master" : isAdvisor ? "Advisor" : user.role==="team_member" ? "Team Member" : "Client";
  const isMaster = user.role === "master";
  const clientName = client.company || client.user?.name || "Client";

  // Breadcrumb segments — Piece 5 will append extraSegments via context for project drill.
  const subViewLabel = {assessment:"Assessment",review:"Assessment Review",gap:"Gap Analysis"}[subView];
  const breadcrumb = chromeSection==="inbox"
    ? [{label:clientName,onClick:()=>handleNavigate("Overview")},{label:"Inbox"}]
  : chromeSection==="settings"
    ? [{label:clientName,onClick:()=>handleNavigate("Overview")},{label:"Settings"}]
  : chromeSection==="admin"
    ? [{label:clientName,onClick:()=>handleNavigate("Overview")},{label:"Admin"}]
  : subView
    ? [{label:clientName,onClick:()=>handleNavigate("Overview")},{label:tab,onClick:()=>setSubView(null)},{label:subViewLabel}]
    : [{label:clientName,onClick:()=>handleNavigate("Overview")},{label:tab},...extraBreadcrumb];

  return (
    <StickyHeaderContext.Provider value={{setHeader:setStickyHeader}}>
    <BreadcrumbContext.Provider value={{setSegments:setExtraBreadcrumb}}>
    <AppShell
      user={user}
      roleLabel={roleLabel}
      onLogout={onLogout}
      onBack={onBack}
      banner={banner}
      clientContext={{
        logoUrl: client.logo_url,
        companyName: client.company || client.user?.name,
        onClick: () => handleNavigate("Overview"),
      }}
      sidebarItems={sidebarItems}
      currentSection={currentSection}
      onNavigate={handleNavigate}
      unreadCount={unreadCount}
      showAdmin={user.role === "master" || user.role === "sales" || user.role === "client"}
      hasBottomNav={isMobile}
      stickyHeader={stickyHeader}
      pageHeader={{breadcrumb, rightContent: (() => {
        if (tab !== "Overview") return null;
        const today = new Date().toISOString().split('T')[0];
        const hasUpcoming = nextSession && nextSession >= today;
        if (isEditingNextSessionHeader && isAdvisor) {
          return (
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <input type="date" value={nextSessionDraftHeader} onChange={e=>setNextSessionDraftHeader(e.target.value)} autoFocus
                style={{background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:13,color:C.charcoal}}/>
              <button onClick={async()=>{setNextSession(nextSessionDraftHeader);await saveNextSession(nextSessionDraftHeader);setIsEditingNextSessionHeader(false);}}
                style={{background:C.navy,color:"#fff",border:"none",borderRadius:5,padding:"4px 12px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Save</button>
              <button onClick={()=>{setNextSessionDraftHeader(nextSession);setIsEditingNextSessionHeader(false);}}
                style={{background:C.bgAlt,color:C.charMid,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Cancel</button>
              {nextSession&&(
                <button onClick={async()=>{setNextSession("");setNextSessionDraftHeader("");await saveNextSession("");setIsEditingNextSessionHeader(false);}}
                  style={{background:C.bgAlt,color:C.charLight,border:`1px solid ${C.border}`,borderRadius:5,padding:"4px 10px",fontFamily:F.body,fontSize:11,cursor:"pointer"}}>Clear</button>
              )}
            </div>
          );
        }
        if (!hasUpcoming && isAdvisor) {
          return (
            <button onClick={()=>{setNextSessionDraftHeader("");setIsEditingNextSessionHeader(true);}}
              style={{background:"none",border:"none",color:C.charLight,fontFamily:F.body,fontSize:12,cursor:"pointer",padding:0,fontStyle:"italic"}}>
              + Schedule next session
            </button>
          );
        }
        if (!hasUpcoming) return null;
        return (
          <div onClick={isAdvisor?()=>{setNextSessionDraftHeader(nextSession);setIsEditingNextSessionHeader(true);}:undefined}
            style={{textAlign:"right",cursor:isAdvisor?"pointer":"default"}}>
            <div style={{fontFamily:F.mono,fontSize:9,color:C.charLight,textTransform:"uppercase",letterSpacing:".08em"}}>Next Session</div>
            <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600}}>{fmtDate(nextSession)}</div>
          </div>
        );
      })()}}>

      {showCategoriesModal&&<CategoriesModal/>}

      {loading ? <Spinner/> : (
        <div className="fi">
          {/* Sub-view overlay — takes precedence over tab content */}
          {subView==="assessment" && (
            <AssessmentView client={client} isAdvisor={false} assessmentData={assessmentData}
              onDone={()=>{getAssessment(client.id).then(setAssessmentData);setSubView(null);}}
              onBack={()=>setSubView(null)}/>
          )}
          {subView==="review" && (
            <AssessmentView client={client} isAdvisor={true} assessmentData={assessmentData}
              onDone={()=>{getAssessment(client.id).then(setAssessmentData);setSubView("gap");}}
              onBack={()=>setSubView(null)}/>
          )}
          {subView==="gap" && (
            <GapAnalysisView client={client} assessmentData={assessmentData}
              onAddProject={handleAddProject} onBack={()=>setSubView(null)}
              advisorId={advisorId} onUpgradeRequest={onUpgradeRequest}/>
          )}

          {/* Inbox section */}
          {!subView && chromeSection==="inbox" && (
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontFamily:F.body,fontSize:13,color:C.charMid}}>{(notifications||[]).length===0 ? "No notifications yet" : `${unreadCount} unread of ${notifications.length}`}</div>
                {unreadCount>0 && (
                  <button onClick={clearNotifications} style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:7,padding:"6px 14px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer"}}>Mark all read</button>
                )}
              </div>
              <Card style={{padding:0,overflow:"hidden"}}>
                {(notifications||[]).length===0 ? (
                  <div style={{padding:"36px 20px",textAlign:"center",fontFamily:F.body,fontSize:13,color:C.charLight}}>No activity yet for this client.</div>
                ) : (
                  (notifications||[]).map((n,i)=>{
                    const cls = classifyTimelineEvent(n);
                    const clickable = !!cls.route;
                    const baseBg = n.read ? "transparent" : C.navyFaint;
                    return (
                      <div key={i}
                        onClick={clickable ? ()=>{
                          const r = cls.route;
                          handleNavigate(r.tab, r.projectId || null);
                          if (r.subView) setSubView(r.subView);
                        } : undefined}
                        onMouseEnter={clickable ? e=>{e.currentTarget.style.background=C.bgAlt;} : undefined}
                        onMouseLeave={clickable ? e=>{e.currentTarget.style.background=baseBg;} : undefined}
                        style={{
                          padding:"12px 16px",
                          borderBottom:i<notifications.length-1?`1px solid ${C.border}`:"none",
                          background:baseBg,
                          cursor:clickable?"pointer":"default",
                          transition:"background .15s",
                        }}>
                        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:3}}>
                          <Badge label={cls.label} type={cls.color}/>
                          <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,flex:1,minWidth:0}}>{n.text}</div>
                        </div>
                        <div style={{fontFamily:F.body,fontSize:9,color:C.charLight}}>{fmtDate(n.created_at)}</div>
                      </div>
                    );
                  })
                )}
              </Card>
            </div>
          )}

          {/* Settings placeholder */}
          {!subView && chromeSection==="settings" && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <ChangeEmailCard isImpersonating={isImpersonating} />
              <ChangePasswordCard isImpersonating={isImpersonating} />
            </div>
          )}

          {/* Admin section — master, advisor, and real client (never team_member).
              Mounted inside a client portal → always client-scoped (forceClientScope)
              regardless of viewer role, so a master/sales operator sees only this
              client's team members instead of their full roster. operatorRole carries
              the REAL operator role through the Preview shim so Delete gating still
              works (master operator → Delete shows, anyone else → hidden). */}
          {!subView && chromeSection==="admin" && user.role !== "team_member" && (isMaster||isAdvisor||user.role==="client") && (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {(isMaster||isAdvisor)&&(
                <Card style={{padding:"16px 18px"}}>
                  <div style={{fontFamily:F.body,fontSize:13,color:C.charcoal,fontWeight:600,marginBottom:4}}>Client Tab Visibility</div>
                  <div style={{fontFamily:F.body,fontSize:11,color:C.charLight,marginBottom:10}}>Toggle which tabs this client can see in their portal.</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {["Projects","Files","Docs","Team Members","Billing","Resources","Timeline","Messages","Sessions"].map(tabName=>{
                      const isPreview=PREVIEW_TABS.includes(tabName);
                      const inList=hiddenTabs.includes(tabName);
                      const visible=isPreview?inList:!inList;
                      return (
                        <button key={tabName} onClick={async()=>{
                          const updated=inList?hiddenTabs.filter(t=>t!==tabName):[...hiddenTabs,tabName];
                          try{
                            await supabase.from('clients').update({hidden_tabs:updated}).eq('id',client.id);
                            setHiddenTabs(updated);
                          }catch(err){
                            alert("Failed to save tab visibility. Please try again.");
                          }
                        }} style={{
                          background:visible?C.greenBg:C.redBg,
                          color:visible?C.green:C.red,
                          border:`1px solid ${visible?C.greenBd:C.redBd}`,
                          borderRadius:6,padding:"6px 13px",
                          fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer",
                          display:"inline-flex",alignItems:"center",gap:6,
                        }}>
                          {visible?"●":"○"} {tabName}
                          {isPreview&&(
                            <span style={{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,borderRadius:3,padding:"0px 5px",fontSize:8,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase"}}>Preview</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{fontFamily:F.body,fontSize:10,color:C.charLight,marginTop:10}}>● = visible to client &nbsp;·&nbsp; ○ = hidden from client</div>
                </Card>
              )}
              <AdminDirectory user={user} clientId={client.id} forceClientScope={true} operatorRole={operatorRole}/>
            </div>
          )}

          {/* Tab content (only when no sub-view and no chrome section) */}
          {!subView && !chromeSection && (
            <>
              {tab==="Overview"  && <OverviewTab client={client} isAdvisor={isAdvisor} user={user} checklist={checklist} setChecklist={setChecklist} timeline={timeline} projects={projects} setProjects={setProjects} invoices={invoices} documents={documents} assessmentData={assessmentData} onStartAssessment={()=>setSubView("assessment")} onReviewAssessment={()=>setSubView("review")} onViewGap={()=>setSubView("gap")} onReopenReview={async()=>{await reopenAdvisorReview(client.id);setSubView("review");}} onUpdateClient={()=>{}} onNavigate={handleNavigate} isMobile={isMobile}/>}
              {tab==="Projects"  && <ProjectsTab client={client} isAdvisor={isAdvisor} user={user} projects={projects} setProjects={setProjects} directProjectId={directProjectId} onDirectProjectHandled={()=>setDirectProjectId(null)} isMobile={isMobile} advisorId={advisorId}/>}
              {tab==="Files"     && <FilesTab client={client} isAdvisor={isAdvisor} user={user} files={files} setFiles={setFiles} advisorId={advisorId} onUpgradeRequest={onUpgradeRequest} aiAnalystState={aiAnalystState} setAiAnalystState={setAiAnalystState} onTimelineUpdate={()=>getTimeline(client.id).then(setTimeline)} onProjectsUpdate={()=>getProjects(client.id).then(setProjects)}/>}
              {tab==="Docs"      && <DocsTab client={client} isAdvisor={isAdvisor} user={user} documents={documents} setDocuments={setDocuments} onTimelineUpdate={()=>getTimeline(client.id).then(setTimeline)}/>}
              {tab==="Team Members" && <TeamMembersTab client={client} isAdvisor={isAdvisor} user={user}/>}
              {tab==="Billing"   && <BillingTab client={client} isAdvisor={isAdvisor} user={user} invoices={invoices} setInvoices={setInvoices} isMobile={isMobile}/>}
              {tab==="Resources" && <ResourcesTab client={client} isAdvisor={isAdvisor} user={user} resources={resources} setResources={setResources}/>}
              {tab==="Timeline"  && <TimelineTab timeline={timeline} isAdvisor={isAdvisor} client={client} onDelete={async(id)=>{await supabase.from('timeline').delete().eq('id',id);setTimeline(p=>p.filter(t=>t.id!==id));}}/>}
              {tab==="Messages"  && <MessagesTab client={client} isAdvisor={isAdvisor} user={user} advisorId={advisorId} onUpgradeRequest={onUpgradeRequest}/>}
              {tab==="Sessions"  && isAdvisor && <SessionsTab client={client} user={user} sessions={sessions} setSessions={setSessions} advisorId={advisorId} onUpgradeRequest={onUpgradeRequest}/>}
              {tab==="Compass"   && COMPASS_ENABLED && isAdvisor && isPilot0Context(advisorId, client?.id) && <CompassModule client={client} user={user} isAdvisor={isAdvisor} businessUnderstanding={compassState} setBusinessUnderstanding={setCompassState}/>}
            </>
          )}
        </div>
      )}

      {/* Mobile bottom nav (kept alongside the sidebar drawer per design) */}
      {isMobile && <BottomNav tabs={TABS} tab={tab} setTab={(t)=>handleNavigate(t)} iconByTab={SIDEBAR_ICON_BY_TAB}/>}

      <div style={{textAlign:"center",padding:"24px 0 12px",fontFamily:F.body,fontSize:10,color:C.charLight,letterSpacing:".06em"}}>a BB Integrated product</div>
    </AppShell>
    </BreadcrumbContext.Provider>
    </StickyHeaderContext.Provider>
  );
}

